import type { FlowV2, FlowValidation, FlowStats, FlowPlatform, FlowSession, FlowSessionBrief, FlowCloseReason, FlowDialogSnapshot } from './supportFlow';
import type { SceneConfig } from '../scene';
import { trackNow } from './analytics';
import { AUTH_EXPIRED_EVENT } from './authTokens';

/** Строка списка «Мои обращения» (GET /api/tickets/my) */
export interface TMyTicketRow {
  id:         number;
  subject:    string;
  status:     'open' | 'in_progress' | 'resolved' | 'closed';
  priority:   'low' | 'normal' | 'high' | 'urgent';
  category:   string | null;
  created_at: string;
  updated_at: string;
  has_unread:       number | boolean;
  has_pending_diag: number | boolean;
  has_close_offer:  number | boolean;
  /** Чья очередь отвечать */
  turn: 'user' | 'support';
  last_message_preview: string;
  last_message_sender:  'user' | 'admin' | null;
}

// ── Вкладка «Сервер» (RCON) ───────────────────────────────────────────
export type TRconAccount = 'own' | 'nolaunch' | 'game' | 'none';
export type TRconVpn = 'clean' | 'likely_vpn' | 'vpn';
export type TRconLogType = 'join' | 'leave' | 'kick' | 'ban' | 'guard' | 'admin' | 'system' | 'chat';
export type TRconChannel = 'global' | 'side' | 'command' | 'group' | 'vehicle' | 'direct' | 'unknown';

export interface TRconPlayer {
  key: string;
  slot: number;
  /** Ник из игровой БД, без персонажа — имя профиля Steam */
  name: string;
  profile_name: string;
  /** Прежние имена профиля Steam, последние сначала */
  profiles: string[];
  steam_id: string | null;
  /** null без права users.view */
  guid: string | null;
  ip: string | null;
  ping: number;
  verified: boolean;
  lobby: boolean;
  joined_at: string;
  country: string | null;
  country_name: string | null;
  isp: string | null;
  vpn: TRconVpn | null;
  /** Почему стоит метка VPN; пусто, если IP чистый */
  vpn_reasons: string[];
  shared_ip: number;
  game: { name: string; adminlevel: number; cash: number; bank: number; minutes: number; fraction: string | null; insert_time: string | null } | null;
  lk: {
    user_id: number; login: string; display_name: string | null; status: string;
    created_at: string | null; last_site_login_at: string | null; active_restrictions: number;
    donate_total: number; has_totp: boolean; roles: string | null;
  } | null;
  launcher: { active: boolean; version: string | null; last_seen_at: string | null } | null;
  forum: { id: number; username: string } | null;
  account: TRconAccount;
  flag: 'banned' | 'risk' | null;
  reasons: string[];
  admin: boolean;
  newbie: boolean;
  watch: { id: number; reason: string; created_by: string } | null;
  ban: { reason: string; expires_at: string | null; source: 'site' | 'battleye' } | null;
  linked_bans: { user_id: number; login: string | null; link_id: number; status: string }[];
  ip_matches: { source: 'site' | 'forum'; user_id: number | null; login: string | null; forum_username: string | null; banned: boolean }[];
  today: { sessions: number; seconds: number; first_at: string | null };
}

export interface TRconLogRow {
  id: number;
  at: string;
  type: TRconLogType;
  channel: TRconChannel | null;
  steam_id: string | null;
  name: string | null;
  text: string;
  ip: string | null;
  actor: string | null;
}

export interface TRconHeadless {
  key: string; slot: number; name: string;
  /** null без права users.view */
  ip: string | null;
  ping: number; joined_at: string;
}

export interface TRconLive {
  connection: { connected: boolean; since: string | null; error: string | null; polled_at: string | null };
  server: { players: number; max: number; peak_today: number; history: { t: number; n: number }[] };
  lock: { locked: boolean; at: string | null; by: string | null; reset_by_restart_at: string | null };
  guard: {
    ban_kick: boolean; use_be_list: boolean; site_banned: number; be_list: number | null;
    admission: { connected: boolean; enabled: boolean; degraded: boolean; policy: string | null };
  };
  settings: { ping_warn: number; ping_bad: number; poll_sec: number };
  players: TRconPlayer[];
  /** Headless-клиенты — не игроки, отдельно от списка */
  headless: TRconHeadless[];
  feed: TRconLogRow[];
  can_see_chat: boolean;
}

/** Сессии за период: уникальные игроки по дням МСК; items обрезаны, total — после поиска */
export interface TRconSessionsRange {
  from: string; to: string; source: 'rcon' | 'game';
  days: { date: string; players: number }[];
  items: TRconSession[];
  total: number;
  truncated: boolean;
}

/** История игрока из игрового журнала входов: при нескольких совпадениях по нику — только matches */
export interface TRconPlayerHistory {
  matches: { steam_id: string; name: string | null }[];
  player: { steam_id: string; name: string | null } | null;
  items: TRconSession[];
  months: { month: string; sessions: number; seconds: number }[];
  total: number;
}

export interface TRconSession {
  id: number; guid: string | null; steam_id: string | null; profile_name: string; game_name: string | null;
  ip: string | null; country: string | null; started_at: string; ended_at: string | null;
  end_reason: 'leave' | 'kick' | 'guard' | 'restart' | null; max_ping: number | null;
}

export interface TRconWatch {
  id: number; steam_id: string; name: string | null; reason: string; notify: 'tg' | 'panel';
  expires_at: string | null; created_by: string; created_at: string; last_seen_at: string | null; online: boolean;
}

export interface TRconGuard {
  site_bans: number; be_list: number | null; be_list_at: string | null;
  kicked: { at: string; name: string | null; steam_id: string | null; source: string; reason: string | null; online: boolean }[];
  be_only: { guid: string | null; reason: string; minutes: number | null; steam_id: string | null }[];
  sync: TBanSyncStatus;
  ban_kick: boolean;
}

export interface TRconSettings {
  poll_sec: number; ping_warn: number; ping_bad: number; retention_days: number;
  ban_kick: boolean; use_be_list: boolean; ban_kick_template: string;
  notify: { watch: boolean; alt: boolean; guard: boolean; lost: boolean; locked: boolean };
  /** Ники headless-клиентов через запятую, «*» в конце — любое продолжение */
  hc_names: string;
  /** IP headless-клиентов, кроме IP сервера и локальных */
  hc_ips: string;
}

export interface TRconAction {
  id: number; actor_id: number | null; actor_login: string; action: string; entity: string; entity_id: string;
  details: unknown; created_at: string;
}

// ─── Base URL ─────────────────────────────────────────────────────────
const BASE      = process.env.REACT_APP_ADMIN_API_URL ?? 'http://localhost:3001';
const AUTH_BASE = process.env.REACT_APP_AUTH_URL      ?? 'http://localhost:3000';

// ─── Types ────────────────────────────────────────────────────────────

// Лог действий на сайте
export interface TAuditLogRow {
  id:          number;
  actor_id:    number | null;
  actor_login: string;
  actor_avatar?: string | null;
  action:      string;
  entity:      string;
  entity_id:   string;
  details:     string | null;
  ip:          string;
  created_at:  string;
}

export interface TAuditLogsResponse {
  data:       TAuditLogRow[];
  nextCursor: number | null;
  hasMore:    boolean;
}

// Файловое хранилище
export interface TStorageFile {
  id:              number;
  slug:            string;
  title:           string;
  description:     string | null;
  original_name:   string;
  mime:            string;
  size:            number;
  access:          'public' | 'auth';
  has_password:    boolean;
  inline_view:     boolean;
  is_active:       boolean;
  uploaded_by:     number;
  downloads_count: number;
  created_at:      string;
}

export interface TStorageFileStats {
  total:        number;
  unique_users: number;
  unique_ips:   number;
  last7days:    number;
  recent: {
    id: number; user_id: number | null; user_login: string | null;
    ip: string; user_agent: string | null; created_at: string;
  }[];
}

export interface TFileDownloadRow {
  id:         number;
  user_id:    number | null;
  user_login: string | null;
  ip:         string;
  user_agent: string | null;
  created_at: string;
}

export interface TFileDownloadsPage {
  data:  TFileDownloadRow[];
  total: number;
  page:  number;
  pages: number;
}

// Денежные операции
export interface TMoneyRow {
  id_money_operation: number;
  player_name:        string | null;
  type:               string | null;
  modifier:           string | null;
  amount:             number | null;
  message:            string | null;
  uid:                string | null;
  cash:               number | null;
  bank:               number | null;
  illegal:            number | null;
  /** Баланс фишек казино после операции (type='chips') */
  chips:              number;
  date_time:          string;
}

export interface TMoneySearchResponse {
  data:       TMoneyRow[];
  nextCursor: number | null;
  hasMore:    boolean;
}

// ── Связь игроков (передачи между двумя игроками, поиск RMT) ──────────
export type TPairChannel = 'cash' | 'illegal' | 'bank' | 'chips' | 'rob' | 'mail' | 'auction';

export interface TPairPlayer {
  uid: string; name: string | null; userId: number | null;
  /** Первый вход в игру, МСК */
  createdAt: string | null; ageDays: number | null;
}

export interface TPairTransfer {
  key: string;
  /** Настенное МСК «YYYY-MM-DD HH:mm:ss» */
  at: string;
  dir: 'ab' | 'ba';
  channel: TPairChannel;
  amount: number;
  /** both — есть записи обеих сторон; one — одной; name — получатель указан только ником */
  verified: 'both' | 'one' | 'name';
  aBalance: number | null;
  bBalance: number | null;
  text: string;
  extra: string | null;
  refs: { aOp?: number; bOp?: number; mail?: number; auction?: number };
}

export interface TPairDirTotals { sum: number; count: number; avg: number; max: number }

export interface TPairSummary {
  ab: TPairDirTotals; ba: TPairDirTotals;
  net: number; skew: number | null; level: 'warning' | 'notice' | null;
  byChannel: { channel: TPairChannel; ab: { sum: number; count: number }; ba: { sum: number; count: number } }[];
  byDay: { day: string; ab: number; ba: number }[];
}

export interface TPairSignal {
  kind: string; level: 'hi' | 'mid' | 'ok'; title: string; text: string;
}

export interface TMoneyPairResponse {
  a: TPairPlayer; b: TPairPlayer; from: string; to: string;
  channels: TPairChannel[];
  summary: TPairSummary; signals: TPairSignal[]; transfers: TPairTransfer[];
  truncated: boolean; excluded: boolean;
  settings: { skewNotice: number; skewWarning: number; minTotal: number; minCount: number };
}

export interface TCounterparty {
  uid: string; name: string | null; given: number; received: number; count: number;
  skew: number | null; level: 'warning' | 'notice' | null; ageDays: number | null; lastAt: string; byName: boolean;
}

export interface TCounterpartiesResponse {
  uid: string; name: string | null; items: TCounterparty[]; truncated: boolean;
}

export interface TRmtExclusion { a: string; b: string; note: string; by: string; at: string }

export interface TRmtSettings {
  enabled: boolean;
  skewNotice: number; skewWarning: number; minTotal: number; minCount: number;
  days: number; newAccountDays: number; donationHours: number; matchSeconds: number;
  channels: TPairChannel[];
  exclusions: TRmtExclusion[];
}

export interface TRmtDetectorStatus { at: string; pairs: number; opened: number; updated: number; error: string | null }

export interface TMoneyStatRow {
  modifier: string;
  type:     string;
  total:    number;
  count:    number;
}

export interface TPlayerMoneyProfile {
  uid:            string;
  lastBalance:    { player_name: string; cash: number; bank: number; illegal: number; chips: number; date_time: string } | null;
  stats:          TMoneyStatRow[];
  balanceHistory: { cash: number; bank: number; illegal: number; chips: number; date_time: string }[];
  historyFrom?:   string;
  historyTo?:     string;
  operations:     TMoneyRow[];
  nextCursor:     number | null;
  hasMore:        boolean;
}

// Логи транспорта
export interface TVehicleRow {
  id:          number;
  classname:   string;
  plate:       string;
  type:        string;
  price:       number;
  pid:         string;
  name:        string;
  insert_time: string;
}

export interface TVehicleStatRow {
  type:        string;
  count:       number;
  total_price: number;
}

export interface TVehicleSearchResponse {
  data:       TVehicleRow[];
  nextCursor: number | null;
  hasMore:    boolean;
}

export interface TPlayerVehicleProfile {
  pid:        string;
  stats:      TVehicleStatRow[];
  operations: TVehicleRow[];
  nextCursor: number | null;
  hasMore:    boolean;
}

// Логи ресурсов (logs_resource — добыча/переработка/рыбалка/бартер)
export interface TResourceRow {
  id:          number;
  pid:         string | null;
  player_name: string | null;
  type:        string | null;
  item_name:   string | null;
  amount:      number | null;
  date_time:   string;
  comment:     string | null;
}

export interface TResourceStatRow {
  type:      string;
  item_name: string;
  total:     number;
  count:     number;
}

export interface TResourceTimelineRow {
  day:   string;
  type:  string;
  total: number;
}

export interface TResourceSearchResponse {
  data:       TResourceRow[];
  nextCursor: number | null;
  hasMore:    boolean;
}

export interface TPlayerResourceProfile {
  pid:        string;
  stats:      TResourceStatRow[];
  timeline:   TResourceTimelineRow[];
  operations: TResourceRow[];
  nextCursor: number | null;
  hasMore:    boolean;
}

// Логи находок металлоискателя (logs_resources)
export interface TResourceFindRow {
  id:          number;
  pid:         string;
  player_name: string;
  type:        string;
  items:       string;
  date_time:   string;
}

export interface TResourceFindsResponse {
  data:       TResourceFindRow[];
  nextCursor: number | null;
  hasMore:    boolean;
}

export interface TBplaRow {
  id:          number;
  pid:         string;
  /** SQF-массив позиции строкой — только у logs_bpla_create */
  position?:   string | null;
  classname:   string;
  insert_time: string;
  player_name: string | null;
}

export interface TBplaSearchResponse {
  data:       TBplaRow[];
  nextCursor: number | null;
  hasMore:    boolean;
}

export interface TMommaHeartRow {
  id:          number;
  pid:         string;
  /** SQF-массив позиции строкой "[x,y,z]" */
  position:    string;
  insert_date: string;
  player_name: string | null;
}

export interface TMommaHeartSearchResponse {
  data:       TMommaHeartRow[];
  nextCursor: number | null;
  hasMore:    boolean;
}

// ─── События игры (logs_any) и игровые инциденты ──────────────────────

export type TGameIncidentSeverity = 'critical' | 'warning' | 'notice' | 'info';
export type TGameIncidentStatus   = 'open' | 'closed';
export type TGameIncidentVerdict  = 'violation' | 'false_positive' | 'noted';

// ── Журнал убийств (/api/logs/kills/*) ──────────────────────────────

export type TKillWeaponKind = 'firearm' | 'melee' | 'launcher' | 'vehicle' | 'none';

export interface TKillRow {
  id:            number;
  agony_id:      number;
  /** Настенное МСК игровой БД «YYYY-MM-DD HH:mm:ss» */
  date_time:     string;
  killer_uid:    string | null;
  killer_name:   string | null;
  victim_uid:    string | null;
  victim_name:   string | null;
  weapon_key:    string | null;
  weapon_label:  string | null;
  weapon_name:   string | null;
  weapon_kind:   TKillWeaponKind;
  victim_weapon: string | null;
  grid:          string | null;
  distance:      number | null;
  killer_group:  string | null;
  victim_group:  string | null;
  teammate:      boolean;
  teammate_by:   'snapshot' | 'tag' | 'roster' | null;
  /** Порог «подозрение от» ствола, м; null — не проверяется */
  threshold:     number | null;
  suspicious:    boolean;
  killer_team:   string | null;
  victim_team:   string | null;
  /** finished — добит после агонии, none — нет; null — ещё не проверено */
  outcome:       'finished' | 'none' | 'died' | null;
  finisher_uid:  string | null;
  finisher_name: string | null;
  finished_after: number | null;
  zone_kind:     'warzone' | 'war' | null;
  zone_name:     string | null;
}

export interface TKillFilters {
  player?:    string;
  killer?:    string;
  victim?:    string;
  weapon?:    string;
  minDist?:   string;
  maxDist?:   string;
  from?:      string;
  to?:        string;
  teammates?: boolean;
  outcome?:   string;
  zone?:      string;
  grid?:      string;
  team?:      string;
  killerTeam?: string;
  victimTeam?: string;
  /** Конкретные агонии через запятую (ссылки из аналитики) */
  ids?:       string;
  cursor?:    number | null;
  limit?:     number;
}

export interface TKillSettings {
  enabled:            boolean;
  streakNotice:       number;
  streakWarning:      number;
  kdDays:             number;
  kdMinKills:         number;
  kdNotice:           number;
  kdWarning:          number;
  statsDays:          number;
  minSamples:         number;
  longShotWarnFactor: number;
  meleeMaxDistance:   number;
  avgWindow:          number;
}

export interface TKillWeapon {
  weapon_key:          string;
  label:               string;
  alias_of:            string | null;
  canonical:           string;
  aliases:             string[];
  kind:                TKillWeaponKind;
  kind_manual:         boolean;
  ignored:             boolean;
  samples:             number;
  p50:                 number | null;
  p95:                 number | null;
  p99:                 number | null;
  max:                 number | null;
  avg_p95:             number | null;
  suspicious_override: number | null;
  threshold:           number | null;
  stats_at:            string | null;
}

export interface TKillWeaponsResponse {
  data:     TKillWeapon[];
  /** Общий порог огнестрела (p99 по серверу) для стволов с малой статистикой */
  fallback: number | null;
  settings: TKillSettings;
}

export interface TKillPlayerPeriod {
  kills:     number;
  deaths:    number;
  teamKills: number;
  ratio:     number;
  weapons:   { weapon_key: string; weapon_name: string | null; n: number; avg: number | null; max: number | null }[];
}

export interface TKillPlayerSummary {
  week:   TKillPlayerPeriod;
  month:  TKillPlayerPeriod;
  /** Текущая серия: чужие агонии с последней своей */
  streak: number;
}

// ── Аналитика журнала убийств ───────────────────────────────────────

export type TKillLevel = 'notice' | 'warning' | null;

export interface TKillKpis {
  agonies:       number;
  fighters:      number;
  teamShare:     number | null;
  p50:           number | null;
  p95:           number | null;
  finishedShare: number | null;
  zoneShare:     number | null;
  detections:    number;
  detectionsOpen?: number;
}

export interface TKillBoardRow { uid: string; name: string; value: number; extra?: string; level?: TKillLevel; id?: number }

export interface TKillOverview {
  days:   number;
  from:   string;
  to:     string;
  kpis:   { cur: TKillKpis; prev: TKillKpis };
  hourly: boolean;
  series: { label: string; enemy: number; team: number }[];
  heat:   number[][];
  boards: { kills: TKillBoardRow[]; kd: TKillBoardRow[]; streak: TKillBoardRow[]; longest: TKillBoardRow[] };
}

export interface TKillStatus {
  total:         number;
  first:         string | null;
  last:          string | null;
  lastAgonyId:   number;
  gameMaxId:     number;
  gameLast:      string | null;
  behindRows:    number;
  lagSec:        number | null;
  enrichPending: number;
  enrichError:   string | null;
  backfill: {
    running: boolean; days: number; fromId: number; toId: number; cursor: number; imported: number;
    error: string | null; startedAt: number | null; finishedAt: number | null;
  };
}

export interface TKillSuspect {
  uid: string; name: string; score: number;
  parts: { longShot: number; kd: number; streak: number; avgRange: number };
  kills: number; deaths: number; ratio: number; maxStreak: number; overCount: number;
  reasons: { text: string; part: keyof TKillSuspect['parts']; link: Record<string, string> }[];
  detail: TKillSuspectDetail;
  openIncidents: number;
}

export interface TKillOverShot {
  id: number; distance: number; threshold: number; weapon: string; weapon_key: string;
  victim_uid: string | null; victim_name: string | null; wall: string; points: number;
}

export interface TKillSuspectDetail {
  longShot: { points: number; max: number; count: number; over2: number; shots: TKillOverShot[] };
  kd:       { points: number; max: number; kills: number; deaths: number; ratio: number; minKills: number; notice: number; warning: number };
  streak:   { points: number; max: number; best: number; notice: number; warning: number; ids: number[] };
  avgRange: { points: number; max: number; weapons: { key: string; label: string; avg: number; norm: number; n: number; points: number }[] };
}

export interface TKillPair {
  killer_uid: string; killer_name: string; victim_uid: string; victim_name: string;
  n: number; back: number; label: 'farm' | 'mutual' | 'watch' | 'feud';
}

export interface TKillSuspects {
  days: number;
  suspects: TKillSuspect[];
  pairs: { oneSided: TKillPair[]; mutual: TKillPair[] };
  teamKillers: { uid: string; name: string; team: string | null; teamLabel: string | null; teamKills: number; share: number; topVictim: string | null; topVictimN: number }[];
  detectors: { rule: 'streak' | 'kd' | 'longShot' | 'avgRange'; total: number; violation: number; falsePositive: number; noted: number; open: number }[];
}

export interface TKillGangs {
  days: number;
  hourly: boolean;
  buckets: string[];
  gangs: {
    team: string; label: string | null; fighters: number; kills: number; deaths: number; ratio: number; teamKills: number;
    enemy: string | null; enemyLabel: string | null; enemyKills: number; daily: number[];
  }[];
  matrix: { teams: { key: string; label: string | null }[]; counts: number[][] };
}

export interface TKillMap {
  days: number;
  total: number;
  cells: { grid: string; n: number; p50: number | null; over: number; zone: number; weapons: { label: string; n: number }[] }[];
  warzones: { name: string; x: number; y: number; r: number; n: number }[];
  /** [x, y, агоний] — место жертвы, сетка 25 м */
  points: [number, number, number][];
  /** Шаг сетки точек, м */
  step: number;
  /** Сколько агоний с точной позицией жертвы, а сколько — только по клетке 100 м */
  exact: number;
  byGrid: number;
  positionError: string | null;
  shots: TKillShot[];
}

export interface TKillShot {
  id: number; kx: number; ky: number; vx: number; vy: number; distance: number | null; over: boolean;
  weapon: string | null; killer_uid: string | null; killer_name: string | null;
  victim_uid: string | null; victim_name: string | null; wall: string;
}

export interface TKillWeaponAnalytics {
  days: number;
  weekStarts: string[];
  weapons: { key: string; label: string; kind: string; n: number; share: number; weekly: number[] }[];
}

export interface TKillWeaponHistogram {
  key: string; label: string; kind: string; days: number; samples: number;
  p50: number | null; p95: number | null; p99: number | null; max: number | null;
  threshold: number | null; override: number | null;
  step: number; bins: number[];
  overTop: { uid: string; name: string | null; n: number }[];
}

export interface TKillProfile {
  uid: string; days: number; name: string | null; team: string | null; teamLabel: string | null;
  kpis: {
    kills: number; deaths: number; ratio: number; teamKills: number; streak: number; maxStreak: number;
    p50: number | null; longest: number | null; overCount: number; finishShare: number | null; inZoneShare: number | null;
  };
  settings: { streakNotice: number; streakWarning: number };
  weapons: { key: string; label: string; n: number; share: number; p50: number | null; max: number; normP50: number | null; threshold: number | null; over: number }[];
  hist: { key: string; label: string; step: number; mine: number[]; all: number[]; threshold: number | null; normP95: number | null } | null;
  lives: { kills: number; from: string; to: string | null }[];
  victims: { uid: string | null; name: string; n: number }[];
  killers: { uid: string | null; name: string; n: number }[];
  /** kills/deaths — агонии; *Died — из них закончились смертью (добиты) */
  enemies: { team: string; label: string | null; kills: number; deaths: number; killsDied: number; deathsDied: number }[];
}

function killsQuery(p: TKillFilters): string {
  const q = new URLSearchParams();
  const set = (k: string, v: string | undefined) => { if (v && v.trim()) q.set(k, v.trim()); };
  set('player', p.player); set('killer', p.killer); set('victim', p.victim); set('weapon', p.weapon);
  set('minDist', p.minDist); set('maxDist', p.maxDist); set('from', p.from); set('to', p.to);
  if (p.teammates) q.set('teammates', '1');
  set('outcome', p.outcome); set('zone', p.zone); set('grid', p.grid);
  set('team', p.team); set('killerTeam', p.killerTeam); set('victimTeam', p.victimTeam); set('ids', p.ids);
  if (p.cursor) q.set('cursor', String(p.cursor));
  if (p.limit) q.set('limit', String(p.limit));
  return q.toString();
}

export interface TCursorPage<T> {
  data:       T[];
  nextCursor: number | null;
  hasMore:    boolean;
}

/** Запись logs_any как её отдаёт игра (текст не копируется в БД сайта) */
export interface TGameLogRow {
  id:        number;
  pid:       string | null;
  name:      string | null;
  info:      string | null;
  code:      string | null;
  date_time: string;
}

export interface TGameJournalRow extends TGameLogRow {
  label:    string | null;
  severity: TGameIncidentSeverity | null;
  incident: { id: number; status: TGameIncidentStatus; verdict: TGameIncidentVerdict | null; closed_login: string | null } | null;
}

export interface TGameIncident {
  id:           number;
  code:         string;
  label:        string;
  group:        string;
  severity:     TGameIncidentSeverity;
  description:  string | null;
  pid:          string;
  /** game — запись игры (logs_any); site — сработка детектора журнала убийств */
  source?:      'game' | 'site';
  count:        number;
  first_log_id: number;
  last_log_id:  number;
  first_seen:   string;
  last_seen:    string;
  status:       TGameIncidentStatus;
  verdict:      TGameIncidentVerdict | null;
  closed_login: string | null;
  closed_at:    string | null;
  /** Последняя запись серии; null — удалена из игровой БД */
  log:          TGameLogRow | null;
  last_comment: { login: string; comment: string; at: string } | null;
  /** Сработка детектора передач (ECON_RMT_*): пара и период для «Связи игроков» */
  rmt?:         { giver: string; receiver: string; from: string; to: string } | null;
}

export interface TGameIncidentEvent {
  id:          number;
  incident_id: number;
  user_id:     number | null;
  login:       string;
  action:      'closed' | 'reopened' | 'comment';
  verdict:     TGameIncidentVerdict | null;
  comment:     string | null;
  created_at:  string;
}

export interface TGameIncidentDetail {
  incident: TGameIncident;
  series:   TGameLogRow[];
  events:   TGameIncidentEvent[];
}

export interface TGameIncidentsSummary {
  counts: Record<TGameIncidentSeverity, number>;
  items:  TGameIncident[];
}

export interface TGameIncidentType {
  code:            string;
  group:           string;
  label:           string;
  description:     string | null;
  severity:        TGameIncidentSeverity;
  dedup_minutes:   number;
  enabled:         boolean;
  auto_registered: boolean;
  updated_at:      string;
  total:           number;
  open:            number;
}

export interface TGameIncidentTypesResponse {
  groups:            Record<string, string>;
  code_column_ready: boolean;
  types:             TGameIncidentType[];
}

export interface TGameEventsJournalParams {
  code?:     string;
  severity?: TGameIncidentSeverity[];
  group?:    string;
  coded?:    'coded' | 'uncoded';
  pid?:      string;
  name?:     string;
  text?:     string;
  from?:     string;
  to?:       string;
  cursor?:   number | null;
  limit?:    number;
}

export interface TGameIncidentsParams {
  status?:   TGameIncidentStatus | 'all';
  code?:     string;
  severity?: TGameIncidentSeverity[];
  group?:    string;
  pid?:      string;
  verdict?:  TGameIncidentVerdict;
  from?:     string;
  to?:       string;
  cursor?:   number | null;
  limit?:    number;
}

function territoryQuery(p: TTerritoryLogFilters): URLSearchParams {
  const q = new URLSearchParams();
  if (p.territory) q.set('territory', p.territory);
  if (p.side)      q.set('side',      p.side);
  if (p.player)    q.set('player',    p.player);
  if (p.group)     q.set('group',     p.group);
  if (p.event)     q.set('event',     p.event);
  if (p.war)       q.set('war',       p.war);
  if (p.from)      q.set('from',      p.from);
  if (p.to)        q.set('to',        p.to);
  if (p.cursor)    q.set('cursor',    String(p.cursor));
  if (p.limit)     q.set('limit',     String(p.limit));
  return q;
}

function gameEventsQuery(p: TGameEventsJournalParams & TGameIncidentsParams): URLSearchParams {
  const q = new URLSearchParams();
  if (p.status)          q.set('status',   p.status);
  if (p.code)            q.set('code',     p.code);
  if (p.severity?.length) q.set('severity', p.severity.join(','));
  if (p.group)           q.set('group',    p.group);
  if (p.coded)           q.set('coded',    p.coded);
  if (p.pid)             q.set('pid',      p.pid);
  if (p.name)            q.set('name',     p.name);
  if (p.text)            q.set('text',     p.text);
  if (p.verdict)         q.set('verdict',  p.verdict);
  if (p.from)            q.set('from',     p.from);
  if (p.to)              q.set('to',       p.to);
  if (p.cursor)          q.set('cursor',   String(p.cursor));
  if (p.limit)           q.set('limit',    String(p.limit));
  return q;
}

// ─── Логи общения (logs_messages + logs_calls) ────────────────────────

export type TCommsKind = 'personal' | 'group' | 'broadcast' | 'service';

export interface TCommsMessageRow {
  id_message: number;
  uid_send:   string | null;
  uid_got:    string | null;
  name_send:  string | null;
  name_got:   string | null;
  message:    string | null;
  date_time:  string;
}

export interface TCommsDialog {
  /** 'p:<uid>' | 'g:<название банды>' | 'c:<uid_got>:<name_got>' */
  key:          string;
  kind:         TCommsKind;
  partner_uid:  string | null;
  partner_name: string | null;
  /** Актуальное имя из таблицы players (может отличаться от RP-имени в логах) */
  partner_current_name: string | null;
  msg_count:    number;
  last_id:      number;
  last_time:    string;
  last_message: string | null;
  last_from_uid: string | null;
}

export interface TCommsDialogsResponse {
  uid:         string;
  player_name: string | null;
  dialogs:     TCommsDialog[];
}

export interface TCommsCall {
  id:           number;
  pid_send:     string;
  pid_got:      string;
  name_send:    string;
  name_got:     string;
  short_number: string;
  service:      boolean;
  date_time:    string;
  end_time:     string | null;
  duration_sec: number | null;
}

export interface TCommsThreadResponse {
  data:       TCommsMessageRow[];
  nextCursor: number | null;
  hasMore:    boolean;
  /** Курсор для догрузки более поздних сообщений (режим around/dir=newer) */
  newerCursor?: number | null;
  hasNewer?:    boolean;
  /** null/undefined — звонки не запрашивались (догрузка, нет права, не личный тред) */
  calls?:     TCommsCall[] | null;
}

export interface TCommsMessagesResponse {
  data:       TCommsMessageRow[];
  nextCursor: number | null;
  hasMore:    boolean;
  /** Фактическая нижняя граница периода (бэкенд ограничивает сутками без uid) */
  effectiveFrom?: string | null;
}

export interface TCommsCallsResponse {
  data:       TCommsCall[];
  nextCursor: number | null;
  hasMore:    boolean;
}

export interface TCommsPlayerHit {
  playerid: string;
  name:     string | null;
}

// ─── Статистика казино (/api/logs/casino/*) ───────────────────────────

export type TCasinoGame = 'blackjack' | 'slots' | 'wheel' | 'poker';

export interface TCasinoGameStat {
  game:      TCasinoGame;
  chips_in:  number;
  chips_out: number;
  profit:    number;
  /** chips_out / chips_in, null если ставок не было */
  rtp:       number | null;
  /** Партии: слоты — сессии, колесо — лесенки, блэкджек — раздачи, покер — бай-ины */
  rounds:    number;
  spins:     number | null;
  players:   number;
}

export interface TCasinoExchangeStat {
  /** $ за проданные фишки (в лог попадают только операции >= 25k$) */
  money_in:       number;
  money_out:      number;
  chips_sold:     number;
  chips_redeemed: number;
  free_chips:     number;
  buy_count:      number;
  sell_count:     number;
}

export interface TCasinoTotals {
  chips_in:  number;
  chips_out: number;
  profit:    number;
  rtp:       number | null;
  players:   number;
}

export interface TCasinoSummary {
  games:    TCasinoGameStat[];
  total:    TCasinoTotals;
  exchange: TCasinoExchangeStat;
}

export interface TCasinoSummaryResponse {
  /** Ключи — пресеты ('today', 'all', …) и/или 'custom' */
  periods: Record<string, TCasinoSummary>;
}

export interface TCasinoTimePoint {
  day:       string; // 'YYYY-MM-DD'
  game:      TCasinoGame;
  chips_in:  number;
  chips_out: number;
  rounds:    number;
}

export interface TCasinoSlotRow {
  slot_id:   number;
  position:  string | null;
  /** 1 = активен, 0 = убран, null — автомата больше нет в dh_slot_machines */
  status:    number | null;
  sessions:  number;
  spins:     number;
  chips_in:  number;
  chips_out: number;
  profit:    number;
  rtp:       number | null;
  players:   number;
}

export interface TCasinoTopPlayer {
  player_name: string;
  uid:         string | null;
  chips_in:    number;
  chips_out:   number;
  net:         number;
}

export interface TCasinoSlotSession {
  id: number; slot_id: number; player_name: string;
  duration: number; spins: number; chips_in: number; chips_out: number;
  insert_date: string;
}

export interface TCasinoWheelSession {
  id: number; player_name: string; bet: number;
  spins: number; chips_in: number; chips_out: number; insert_date: string;
}

export interface TCasinoMoneyOp {
  id: number; player_name: string | null; uid: string | null;
  modifier: string | null; amount: number; message: string | null; date_time: string;
}

export interface TCasinoSessionsResponse<T> {
  data:       T[];
  nextCursor: number | null;
  hasMore:    boolean;
}

export interface TAdminUser {
  id:          number;
  login:       string;
  email?:      string;
  is_active?:  number;
  created_at?: string;
  roles?:      string;   // через запятую, из ap_user_roles
}

export interface TRole {
  id:          number;
  name:        string;
  description: string | null;
  permissions: string[];
}

export interface TPermission {
  id:          number;
  key:         string;
  description: string | null;
}

// ─── OIDC clients (реестр в amazing_auth) ─────────────────────────────
export interface TOidcClient {
  id:                         number;
  client_id:                  string;
  client_name:                string;
  has_secret:                 boolean;
  token_endpoint_auth_method: string;
  grant_types:                string[];
  response_types:             string[];
  redirect_uris:              string[];
  post_logout_redirect_uris:  string[];
  first_party:                boolean;
  enabled:                    boolean;
  created_at:                 string;
  updated_at:                 string;
}

/** client_secret приходит только при создании/перегенерации (показать один раз). */
export interface TOidcClientMutation {
  client:         TOidcClient;
  client_secret?: string;
}

export interface TProfile {
  id:          number;
  username:    string;
  name:        string;
  steam_id:    string | null;
  created_at:  string;
  roles:       string[];
  permissions: string[];
}

export interface TApiError extends Error {
  status?: number;
  /** Тело ответа с ошибкой: машиночитаемые поля вроде unknown_classnames */
  data?: Record<string, unknown>;
}

export interface TPlayerPosition {
  uid:           string;
  player_name:   string;
  x:             number;
  y:             number;
  z:             number;
  health:        number;                   // 0-100
  weapons:       [string, string, string]; // [основное, пистолет, вторичное]
  dir:           number;                   // 0-360, север=0, по часовой
  activeWeapon:  number;                   // 0=нет, 1=основное, 2=пистолет, 3=вторичное
  player_color?: string;                   // HEX-цвет (#rrggbb), передаётся напрямую из SQF
  gang_id?:      string | number;          // 0 = без банды, иначе ID банды/группы
  // ── Рация (TFAR) ──
  kv?:           string;                   // личное КВ (активная SW-рация)
  dv?:           string;                   // личное ДВ (активная LR-рация)
  kv_group?:     string;                   // КВ группировки
  dv_group?:     string;                   // ДВ группировки
  kv_gang?:      string;                   // КВ банды
  dv_gang?:      string;                   // ДВ банды
  // ── Опциональные поля (новый HC-скрипт; старый их не шлёт) ──
  dog?:          number;                   // 1 = игрок пересажен в собаку
  dog_type?:     string;                   // 'police' | 'sanction'
  ammo?:         number;                   // патроны в магазине активного оружия
  recorded_at?:  string;
}

export type TFractionType = 0 | 1 | 2 | 3 | 4;

// Последнее ранение игрока (logs_agony / logs_agony_walk)
export interface TLastWound {
  uid_killer:  string | null;
  name_killer: string | null;
  gun_killer:  string | null;
  distance:    number | null;
  date_time:   string;
  source:      'agony' | 'walk';
}

export interface TGamePlayerInfo {
  playerid:      string;
  name:          string;
  cash:          number;
  bankacc:       number;
  illegal_cash:  number;
  chips:         number;
  deposit:       number;
  donorlevel:    number;
  blacklist:     boolean;
  kills:         number;
  deaths:        number;
  reputation:    number;
  rating:        number;
  inGameTime:    number;  // минуты
  insertTime:    string;  // дата регистрации
  tsName:        string;  // TeamSpeak никнейм
  fraction_type: TFractionType;
  fraction_name: string;
  fraction_code: string;
  fraction_rank: number;
}

// Инвентарь транспорта (Trunk/Gear)
export interface TVehicleInventory {
  /**
   * [ключ предмета, количество, state?]. У Trunk (виртуальные предметы) третьего
   * элемента нет; у Gear (физические) state — ресурс предмета (патроны,
   * прочность, заряд, вода…), семантика зависит от класса (см. gearState.ts).
   */
  items:  [string, number, number?][];
  /** Trunk — кг; Gear — единицы массы Arma */
  weight: number;
}

/** Снимок живого словаря игры (GET /api/game/dictionary) */
export interface TGameDict {
  version:     number;
  /** classname (lowercase) → displayName; все корни конфига вместе */
  names:       Record<string, string>;
  /** classname (lowercase) → CfgMagazines.count (ёмкость / максимум ресурса) */
  magCapacity: Record<string, number>;
}

export type TGameDictKind = 'CfgVehicles' | 'CfgWeapons' | 'CfgMagazines' | 'CfgGlasses' | 'VirtualItems';

export interface TGameDictHit {
  classname: string;
  name: string;
  kind: TGameDictKind;
}

export interface TGameDictCatalogRow extends TGameDictHit {
  /** Ёмкость магазина; null — не магазин */
  capacity: number | null;
}

/** 422 при сохранении: классов нет в словаре сервера, повтор с force: true */
export function unknownClassnamesOf(e: unknown): string[] | null {
  const data = (e as TApiError | null)?.data;
  return data?.code === 'unknown_classnames' && Array.isArray(data.unknown_classnames)
    ? data.unknown_classnames.map(String)
    : null;
}

export interface TVehicle {
  class:      string;
  name:       string;
  x:          number;
  y:          number;
  z:          number;
  crew:       string[];
  plate?:     string;                        // пусто = аренда
  uid_owner?: string;                        // пусто = нет владельца
  dir:        number;                        // 0-360, север=0, по часовой
  veh_type?:  'car' | 'air' | 'ship';       // тип: наземный / воздушный / морской
  veh_color?: string;                        // HEX-цвет (#rrggbb), передаётся из SQF
  alive?:          number;                   // 1 = живой, 0 = уничтоженный (wreck)
  controller_uid?: string;                   // UID оператора БПЛА (пусто = никто)
  trunk?:          TVehicleInventory;        // виртуальный багажник
  gear?:           TVehicleInventory;        // снаряжение (cop/med/army)
  net_id?:         string;                   // netId Arma-объекта (уникален per-экземпляр)
}

// Уничтоженный транспорт (ap_vehicle_destroyed)
export interface TDestroyedVehicle {
  destroyed_at: string;
  veh_key:      string;
  class:        string;
  veh_name:     string;
  plate:        string;
  x:            number;
  y:            number;
  z:            number;
  veh_type:     string;
  uid_owner:    string;
  owner_name:   string;
  rented:       boolean;
  veh_db_id:    number | null;
  inventory:    { trunk?: TVehicleInventory | null; gear?: TVehicleInventory | null } | null;
}

// Сессия управления БПЛА (ap_uav_control_history)
export interface TUavSession {
  vehicle_key:     string;
  class:           string;
  veh_name:        string;
  controller_uid:  string;
  controller_name: string;
  started_at:      string;
  ended_at:        string | null;
}

// Транспорт игрока из altislife.vehicles (страница управления)
export interface TAdminVehicle {
  id:          number;
  side:        string | null;
  classname:   string | null;
  type:        string | null;
  pid:         string | null;
  owner_name:  string | null;
  alive:       number | null;
  lives:       number | null;
  blacklist:   number | null;
  active:      number | null;
  plate:       string;
  color:       string | null;
  fuel:        number | null;
  name_fo:     string | null;
  price:       number | null;
  wname:       string | null;
  garage:      string | null;
  odometr:     number | null;
  perm:        string | null;
  insert_time: string | null;
  /** Отображаемое имя из словаря vehicleNames.json (по classname) */
  display_name?: string | null;
}

export interface TGaragePlayerHit {
  playerid:      string;
  name:          string | null;
  vehicle_count: number;
}

export interface TPlayerGarage {
  player: {
    playerid:    string;
    name:        string | null;
    cash:        number | null;
    bankacc:     number | null;
    blacklist:   number | null;
    donorlevel:  number | null;
    insert_time: string | null;
  };
  stats: {
    total:       number;
    total_price: number;
    active:      number;
    blacklisted: number;
    destroyed:   number;
    by_type:     Record<string, number>;
  };
  vehicles: TAdminVehicle[];
}

/** Организация из altislife.fractions (для селекта «сторона» транспорта). */
export interface TVehicleFraction {
  code: string;
  name: string;
}

/** Запись журнала передач транспорта (ap_vehicle_transfers). */
export interface TVehicleTransfer {
  id:            number;
  vehicle_id:    number;
  classname:     string | null;
  plate:         string | null;
  old_pid:       string | null;
  old_owner:     string | null;
  new_pid:       string;
  new_owner:     string | null;
  admin_user_id: number | null;
  admin_login:   string | null;
  created_at:    string;
}

export interface THistoryPosition {
  uid:           string;
  player_name:   string;
  x:             number;
  y:             number;
  z:             number;
  health:        number;        // 0-100
  dir:           number;        // 0-360
  active_weapon: number;        // 0-3
  wp_main:       string;
  wp_pistol:     string;
  wp_second:     string;
  player_color?: string;        // HEX-цвет (#rrggbb), передаётся из SQF
  // true — цвет записан в самом кадре (точное состояние жив/мёртв на момент
  // записи); false/нет — старая запись, цвет взят из текущей меты
  color_from_frame?: boolean;
  gang_id?:      string | number;
  // ── Рация на момент записи (личные — из позиций, групповые — из меты) ──
  kv?:           string;
  dv?:           string;
  kv_group?:     string;
  dv_group?:     string;
  kv_gang?:      string;
  dv_gang?:      string;
  recorded_at:   string;
}

export interface TCombatEvent {
  type:        'agony' | 'death';
  uid_killer:  string | null;
  uid_victim:  string | null;
  name_killer: string | null;
  name_victim: string | null;
  weapon:      string | null;
  distance:    string | null;
  date_time:   string;
}

export interface TGameMessage {
  id_message: number;
  uid_send:   string | null;
  uid_got:    string | null;
  name_send:  string | null;
  name_got:   string | null;
  message:    string | null;
  date_time:  string;
}

export interface THistoryVehicle {
  plate:       string;
  /** Стабильный ключ экземпляра: plate, иначе class:net_id (старые записи — plate). */
  veh_key?:    string;
  class:       string;
  veh_name:    string;
  x:           number;
  y:           number;
  z:           number;
  dir:         number;
  uid_owner:   string;
  crew_count:  number;
  /** UID-ы экипажа на момент кадра (null/нет — старая запись или пусто). */
  crew?:       string[] | null;
  /** Виртуальный багажник на момент кадра (null/нет — старая запись или пусто). */
  trunk?:      TVehicleInventory | null;
  /** Снаряжение (Gear) на момент кадра (null/нет — старая запись или пусто). */
  gear?:       TVehicleInventory | null;
  /** UID оператора БПЛА на момент кадра ('' = никто). */
  controller_uid?: string;
  /** Tombstone: объект исчез из потока (разобран/взорвался/удалён) — с этого кадра не рисуется. */
  gone?:       boolean;
  veh_type?:   'car' | 'air' | 'ship';
  veh_color?:  string;
  recorded_at: string;
}

export interface TGameVehicleInfo {
  plate:      string;
  classname:  string;
  type:       string;
  side:       string;
  alive:      boolean;
  blacklist:  boolean;
  fuel:       number;   // 0-1
  odometer:   number;
  color:      number;
  name_fo:    string;
  price:      number;
  pid:        string;
  owner_name: string;
  lives:      number;   // alive + lives (итого жизней)
}

// ─── Dashboard types ──────────────────────────────────────────────────

export interface TUserConsent {
  id:          number;
  document_id: number;
  accepted_at: string;
  ip_address:  string | null;
  user_agent:  string | null;
  slug:        string;
  title:       string;
  version:     string;
}

// ─── Auth users types ─────────────────────────────────────────────────

export type TUserStatus = 'email_unverified' | 'steam_unlinked' | 'active' | 'banned' | 'deleted';

export interface TUser {
  id:           number;
  login:        string;
  email:        string;
  display_name: string | null;
  avatar_url?:  string | null;
  is_active:    number;
  status:       TUserStatus;
  created_at:   string;
  roles?:       string | null;                        // строка через запятую в списке
  steam?:       { steam_id: string; steam_name: string | null; steam_avatar: string | null } | null;
  recent_ips?:  { ip_address: string; action: string; created_at: string }[];
  /** 2FA (TOTP) — только в карточке */
  totp_enabled?:    boolean;
  totp_enabled_at?: string | null;
}

/**
 * Строка сводной таблицы ap_user_summary. Поля закрытых групп бэкенд
 * отдаёт только при наличии права (finance.view, launcher.users.view) —
 * поэтому они optional: фронт по `'donate_total' in row` решает, рисовать
 * ли колонку/блок.
 */
export interface TUserSummary {
  id:                   number;
  login:                string;
  email:                string;
  display_name:         string | null;
  status:               TUserStatus;
  is_active:            number;
  roles:                string | null;
  steam_id:             string | null;
  has_totp:             number;
  created_at:           string | null;
  last_site_login_at:   string | null;
  last_activity_at:     string | null;
  active_restrictions:  number;
  /** Часов в игре за всё время (ночной пересчёт + по окончании сессии) */
  game_hours_total:     number;
  // finance.view
  donate_total?:        number;
  donate_30d?:          number;
  payments_count?:      number;
  first_payment_at?:    string | null;
  last_payment_at?:     string | null;
  // launcher.users.view
  launcher_last_seen_at?: string | null;
  launcher_version?:    string | null;
  mod_branch?:          string | null;
  in_game?:             number;
  devices_count?:       number;
  linked_accounts_count?: number;
}

/** Где найдено совпадение умного поиска */
export type TSearchSource = 'lk' | 'launcher' | 'telegram' | 'ip' | 'forum' | 'game';

/** Чем строка совпала с поисковым запросом: поле ЛК, токен лаунчера, Telegram, IP, ник форума или игры */
export interface TUserMatch {
  source?: TSearchSource;
  field: string;
  /** «логин», «старый ник», «ник сейчас» */
  label: string;
  value: string;
  /** Пояснение: «до 12.03.2024, сейчас Ivan_Petrov (#5321)» */
  note?: string;
  /** Форум: аккаунт, привязанный к ЛК */
  account?: { id: string; name: string; url: string | null };
}

/** Переключатель «Везде / ЛК / Форум / Игра» */
export type TSearchScope = 'all' | 'lk' | 'forum' | 'game';

export type TUserListRow = TUserSummary & {
  match: TUserMatch | null;
  matches?: TUserMatch[];
  /** Текущий ник в игре по Steam этого ЛК (в выдаче поиска) */
  game_name?: string | null;
  /** Игровой аккаунт этого ЛК совпал с запросом */
  game_matched?: boolean;
};

/** Аккаунт форума или игры, найденный поиском, но не связанный с ЛК */
export interface TUnlinkedHit {
  source: 'forum' | 'game';
  /** ID форума или SteamID */
  id: string;
  /** Текущий ник */
  name: string;
  /** Что совпало */
  match: TUserMatch;
  url: string | null;
  /** Игра: последний заход на сервер (ISO) */
  last_seen?: string | null;
}

export type TUserSortKey =
  | 'id' | 'login' | 'status' | 'created_at' | 'last_activity_at'
  | 'donate_total' | 'devices_count' | 'launcher_last_seen_at';

export interface TUserListParams {
  page?: number;
  limit?: number;
  q?: string;
  status?: string;
  sort?: TUserSortKey;
  dir?: 'asc' | 'desc';
  /** Был в лаунчере за N дней (launcher.users.view) */
  launcher_days?: number;
  /** Сейчас в игре (launcher.users.view) */
  in_game?: '1';
  /** Лаунчер НЕ на этой версии (launcher.users.view) */
  version_not?: string;
  /** Где искать q */
  scope?: TSearchScope;
  /** '0' — не искать по старым никам */
  old?: '0';
}

/** GET /api/users/player-brief/:steamId: карточка игрока при наведении (PlayerPeek) */
export interface TPlayerBrief {
  steam_id: string;
  name: string | null;
  in_game: boolean;
  online: boolean;
  /** null — игрок без ЛК */
  user: { id: number; login: string; display_name: string | null; status: string } | null;
  users_count: number;
  ban: { reason: string; expires_at: string | null } | null;
  fraction: { name: string; rank: number } | null;
  money: { cash: number; bank: number } | null;
  hours: number | null;
}

/** GET /api/users/player/:steamId/details/:part: разделы «Персонажа», только чтение */
export interface TPlayerInventory {
  game: { id: number; type: string; classname: string; data: string | null; insert_date: string | null; insert_type: string | null }[];
  /** type — weblk_products.specificationType (Vehicle, …); у старого бэкенда его нет */
  cabinet: { id: number; product_id: number; title: string | null; type?: string | null; image?: string | null; data: string | null; can_transfer: boolean; created_at: string | null }[];
  /** Нет у бэкенда до этой версии */
  withdrawn?: { id: number; product_id: number; title: string | null; type?: string | null; image?: string | null; data?: string | null; deleted_at: string | null }[];
}
export interface TPlayerProperty {
  houses: { id: number; type: string | null; pos: string | null; wname: string | null; price: number | null }[];
  vehicles: { total: number; alive: number };
}
export interface TPlayerViolations {
  wanted: { id: number; code: string; price: number; description: string | null; officer: string | null; officer_name: string | null; at: string | null }[];
  wanted_total: number;
  game_log: { id: number; reason: string | null; time: string | null; admin: string | null; at: string | null }[];
  site: { id: number; reason: string; source: string; expires_at: string | null; created_at: string; removed_at: string | null }[];
}
export interface TPlayerMail {
  id: number; type: string | null; dir: 'out' | 'in';
  from: string | null; to: string | null; from_name: string | null; to_name: string | null;
  theme: string | null; message: string | null; items: string | null;
  /** Вложения, разобранные сервером: имя по словарям игры */
  attachments?: { name: string; count: number; classname: string | null; note: string | null }[];
  price_from: number | null; price_target: number | null;
  status: string | null; at: string | null; ready_at: string | null;
}
export interface TPlayerDetails {
  inventory: TPlayerInventory;
  property: TPlayerProperty;
  violations: TPlayerViolations;
  mail: TPlayerMail[];
}

/** GET /api/users/player/:steamId/character: «Персонаж» на чтение */
export interface TPlayerCharacter {
  brief: TPlayerBrief;
  game: {
    playerid: string; name: string;
    cash: number; bankacc: number; illegal_cash: number; chips: number; deposit: number; ap: number | null;
    donorlevel: number; blacklist: boolean;
    kills: number; deaths: number; reputation: number; rating: number;
    inGameTime: number; insertTime: string; tsName: string;
    fraction_type: number; fraction_name: string; fraction_code: string; fraction_rank: number;
    /** Колонки players вне сущности (APb, конфеты, очередь в банк, тюрьма, доступы); нет — колонки нет в базе */
    extra?: Partial<Record<'ap_bonus' | 'sp' | 'bank_add' | 'use_shop' | 'use_compensation' | 'officer' | 'pdd_exam' | 'radiation'
      | 'arrest_time' | 'arrest_time_start' | 'arrest_escape' | 'arrest_coin' | 'slave', number>>;
  } | null;
  users: { id: number; login: string; display_name: string | null; status: string }[];
  /** Плитки карточки игрока; нет у бэкенда до этой версии, null — источника нет в базе */
  stats?: { donate_rub: number | null; payments: number | null; hours_30d: number | null };
}

/** GET /api/users/:id/summary: сводка + live-блок игры (ник персонажа, часы за 30 дней) */
export interface TUserSummaryDetail extends TUserSummary {
  updated_at: string;
  game: { name: string | null; hours_30d: number } | null;
}

export type TIpSource = 'site' | 'launcher' | 'forum';

export interface TUserIpOverview {
  max_per_day: number;
  signals: { site: boolean; forum: boolean };
  items: {
    ip: string; first_seen: string; last_seen: string;
    sources: Partial<Record<TIpSource, { first_seen: string; last_seen: string; count: number | null }>>;
    crowd: number;
    others: { user_id: number; sources: TIpSource[]; last_seen: string; user: TMultiaccUserBrief }[];
    signal: { types: string[]; counted: boolean } | null;
  }[];
}

export interface TUserIpLog {
  id:         number;
  ip_address: string;
  action:     string;
  user_agent: string | null;
  /** Таймзона браузера пользователя (IANA) на момент входа; null для старых записей. */
  client_tz:  string | null;
  created_at: string;
}

export interface TIpMatchUser {
  user_id:      number;
  login:        string;
  display_name: string | null;
  avatar_url:   string | null;
  email:        string;
  status:       string;
  count:        number;
  last_seen:    string;
  steam_id:     string | null;
  steam_name:   string | null;
}

export interface TIpInfo {
  status:      string;
  country:     string;
  countryCode: string;
  regionName:  string;
  city:        string;
  lat:         number;
  lon:         number;
  isp:         string;
  org:         string;
  as:          string;
  proxy:       boolean;
  hosting:     boolean;
  timezone:    string;
}

// ─── Warzone types ────────────────────────────────────────────────────

export interface TWarzoneActive {
  id:            number;
  id_warzone:    number;
  world:         string;
  fraction:      string;
  name:          string;
  name_warzone:  string;
  radius:        number;
  pos_x:         number;
  pos_y:         number;
  date_time:     string;
}

// ── Войны за территории (logs_territory_wars + territories) ─────────

export type TTerritoryEventGroup = 'war' | 'ownership' | 'admin' | 'system';

/** Строка лога войн; time — UTC ISO, war_id пуст вне войны и у старых записей. */
export interface TTerritoryLogRow {
  id:             number;
  time:           string;
  territory_id:   string;
  territory_name: string;
  event:          string;
  side_type:      number;
  side_id:        string;
  side_name:      string;
  pid:            string;
  player_name:    string;
  details:        string | null;
  war_id:         string;
}

export interface TTerritoryLogFilters {
  territory?: string;
  side?:      string;
  player?:    string;
  group?:     TTerritoryEventGroup | '';
  event?:     string;
  war?:       string;
  from?:      string;
  to?:        string;
  cursor?:    number | null;
  limit?:     number;
}

export type TWarStatus = 'challenge' | 'prep' | 'battle' | 'finished' | 'hung';
export type TWarResult = 'flag' | 'wipe' | 'battle' | 'walkover' | 'no_change' | 'cancelled';

export interface TWarSide {
  type:      number;
  id:        string;
  name:      string;
  roster:    string[];
  confirmed: string[] | null;
  alive:     number | null;
}

/** Карточка войны, собранная бэкендом из событий с одним war_id. Время — epoch-мс. */
export interface TWarCard {
  warId:         string;
  territoryId:   string;
  territoryName: string;
  status:        TWarStatus;
  result:        TWarResult | null;
  startedAt:     number;
  battleAt:      number | null;
  finishedAt:    number | null;
  attacker:      TWarSide | null;
  defender:      TWarSide | null;
  winner:        { type: number; id: string; name: string } | null;
  neutral:       boolean | null;
  openChallenge: boolean | null;
  fees:          number;
  rewards:       number;
  exclusions:    { pid: string; playerName: string; sideName: string; reason: string; ts: number }[];
  flagHolds:     number;
  eventsCount:   number;
}

export interface TTerritoryEventsPage extends TCursorPage<TTerritoryLogRow> {
  /** false — в игровой БД ещё нет колонки war_id (миграция не применена). */
  warIdReady: boolean;
}

export interface TTerritoryWarsPage extends TCursorPage<TWarCard> {
  names:      Record<string, string>;
  warIdReady: boolean;
}

export interface TTerritoryWarDetail {
  card:   TWarCard;
  events: TTerritoryLogRow[];
  names:  Record<string, string>;
}

/** Территория для слоя карты; координаты — игровые [x, y]. */
export interface TMapTerritory {
  id:          string;
  name:        string;
  type:        number;
  locked:      boolean;
  owner:       { type: number; id: string; name: string } | null;
  polygon:     [number, number][];
  center:      [number, number] | null;
  radius:      number;
  flagPoints:  [number, number][];
  spawnPoints: [number, number][];
}

export interface TMapTerritoriesLive {
  territories: TMapTerritory[];
  wars:        TWarCard[];
}

export interface TMapTerritoriesHistory {
  territories:     TMapTerritory[];
  /** endsAt — finishedAt либо рестарт, оборвавший войну. */
  wars:            (TWarCard & { endsAt: number })[];
  geometryChanges: { territoryId: string; event: string; at: number }[];
}

export interface TWarzoneEvent {
  id:            number;
  id_warzone:    number;
  world:         string;
  type:          string;   // 'wz_create' | 'wz_delete'
  fraction:      string;
  pid:           string;
  name:          string;
  name_warzone:  string;
  radius:        number;
  pos_x:         number;
  pos_y:         number;
  date_time:     string;
}

// ─── Dashboard types ──────────────────────────────────────────────────

export interface TShift {
  id:           number;
  user_id:      number;
  // login может быть null, если пользователь не разрезолвился в amazing_auth
  login:        string | null;
  avatar_url:   string | null;
  steam_avatar: string | null;
  started_at:   string;
  ended_at:     string | null;
  note:         string | null;
}

export type TTaskStatus   = 'open' | 'in_progress' | 'testing' | 'awaiting_release' | 'done' | 'cancelled';
export type TTaskPriority = 'low'  | 'normal'      | 'high' | 'critical';

/** Тип задачи из настраиваемого справочника (ap_task_types) */
export interface TTaskType {
  id:        number;
  name:      string;
  color:     string;
  sort:      number;
  canCreate: boolean;
}

/** Полная строка справочника типов (для настроек, settings.manage) */
export interface TTaskTypeAdmin {
  id:           number;
  name:         string;
  color:        string;
  sort:         number;
  active:       number;
  view_roles:   string[];
  create_roles: string[];
  legacy_key:   string | null;
}

export interface TTask {
  id:             number;
  title:          string;
  description:    string | null;
  status:         TTaskStatus;
  priority:       TTaskPriority;
  type_id:        number | null;
  type_name?:     string | null;
  type_color?:    string | null;
  attachments?:   string[];
  created_by:     number;
  creator_login?: string | null;
  creator_avatar?: string | null;
  created_at:     string;
  updated_at:     string;
  /** Последнее действие в задаче (комментарий, смена полей) — возвращается в списке */
  last_activity_at?: string | null;
  deadline:       string | null;
  assignee_count?: number;
  assignees?:     { user_id: number; login: string | null; avatar?: string | null; assigned_at?: string; assigned_by_login?: string | null }[];
  /** Задачу менял кто-то другой, а пользователь ещё не открывал её после изменения */
  unseen?:        boolean;
  /** Вердикт тестирования: passed | failed | null (не проверялось) */
  tests_status?:  'passed' | 'failed' | null;
  tests_by?:      number | null;
  tests_at?:      string | null;
  /** Логин поставившего вердикт (возвращается в детали) */
  tests_by_login?: string | null;
}

export interface TTaskComment {
  id:          number;
  user_id:     number;
  login:       string | null;
  avatar?:     string | null;
  body:        string;
  attachments?: string[];
  created_at:  string;
}

export interface TTaskDetail extends TTask {
  comments?: TTaskComment[];
  history?:  { id: number; user_id: number; login: string; action: string; created_at: string }[];
}

export type TNoteBoard = 'admin' | 'moderator';

export interface TNote {
  id:         number;
  user_id:    number;
  board?:     TNoteBoard;
  login:      string;
  avatar?:    string | null;
  title:      string | null;
  body:       string;
  is_pinned:  number;
  created_at: string;
  updated_at: string;
}

// ─── Staff chat (внутренний чат сотрудников) ──────────────────────────

export type TChatConversationType = 'channel' | 'group' | 'dm';

export interface TChatStaffMember {
  user_id: number;
  login:   string | null;
  avatar:  string | null;
  roles:   string[];
}

export interface TChatLastMessage {
  id:           number;
  sender_id:    number;
  sender_login: string | null;
  excerpt:      string;
  created_at:   string;
}

export interface TChatConversation {
  id:           number;
  type:         TChatConversationType;
  name:         string | null;
  created_by:   number | null;
  created_at:   string;
  muted:        boolean;
  my_role:      'owner' | 'member';
  unread:       number;
  last_message: TChatLastMessage | null;
  /** Для ЛС — собеседник */
  peer:         { user_id: number; login: string | null; avatar: string | null } | null;
  /** Для групп — участники */
  participants?: { user_id: number; login: string | null; avatar: string | null; role: 'owner' | 'member' }[];
}

export interface TChatMessage {
  id:              number;
  conversation_id: number;
  sender_id:       number;
  sender_login:    string | null;
  sender_avatar:   string | null;
  content:         string;
  reply_to:        { id: number; sender_id: number; sender_login: string | null; excerpt: string } | null;
  forwarded_from:  { user_id: number; login: string | null } | null;
  is_deleted:      boolean;
  edited_at:       string | null;
  pinned_at:       string | null;
  created_at:      string;
  /** Только в результатах поиска */
  conversation_name?: string | null;
}

// ─── Ticket reply templates ───────────────────────────────────────────

export interface TTicketTemplate {
  id:        number;
  parent_id: number | null;
  is_folder: number;
  name:      string;
  body:      string | null;
  sort:      number;
  active:    number;
}

// ─── Ticket types ─────────────────────────────────────────────────────

export type TTicketStatus = 'open' | 'in_progress' | 'resolved' | 'closed';
export type TTicketPriority = 'low' | 'normal' | 'high' | 'urgent';

export interface TTicket {
  id:               number;
  user_id:          number;
  subject:          string;
  status:           TTicketStatus;
  priority:         'low' | 'normal' | 'high' | 'urgent';
  category:         string | null;
  created_at:       string;
  updated_at:       string;
  closed_at:        string | null;
  /** Логин автора (пользователя), возвращается в списке */
  login?:           string | null;
  /** Количество сообщений, возвращается в списке */
  message_count?:   number;
  /** Исполнители через запятую, возвращается в списке */
  assignee_logins?: string | null;
  /** Кто написал последнее сообщение ('user' | 'admin'), возвращается в списке */
  last_sender_type?: 'user' | 'admin' | null;
  /** Чья очередь действовать: 'operator' — нужен ответ поддержки, 'user' — ждём игрока */
  awaiting?: 'operator' | 'user';
  /** Обращение создано из диалога с помощником — откуда (лаунчер / сайт) */
  flow_platform?: FlowPlatform | null;
}

export interface TTicketMessage {
  id:          number;
  ticket_id:   number;
  sender_id:   number;
  sender_type: 'user' | 'admin';
  body:        string;
  created_at:  string;
  login?:      string | null;
  avatar?:     string | null;
  read_at?:    string | null;
}

export interface TTicketEvent {
  id:          number;
  actor_id:    number | null;
  actor_type:  'user' | 'admin' | 'system';
  event_type:  string;
  details:     Record<string, unknown> | null;
  created_at:  string;
  actor_login: string | null;
}

// ─── User restriction types ───────────────────────────────────────────

export type TRestrictionType = 'avatar' | 'service' | 'tickets';

export interface TUserRestriction {
  id:                number;
  type:              TRestrictionType;
  /** Комментарий только для администрации */
  reason:            string;
  /** Комментарий для пользователя (виден пользователю) */
  reason_public:     string;
  expires_at:        string | null;   // null = бессрочно
  created_by:        number | null;
  created_by_login:  string | null;
  created_by_name:   string | null;
  created_at:        string;
  removed_at?:       string | null;
  removed_by?:       number | null;
  removed_by_login?: string | null;
}

export interface TUserRestrictionsHistoryPage {
  items: TUserRestriction[];
  total: number;
  page:  number;
  pages: number;
}

export interface TTicketUserInfo {
  login:        string;
  email:        string;
  display_name: string | null;
  avatar_url:   string | null;
  user_status:  string;
  steam_id:     string | null;
  steam_name:   string | null;
  steam_avatar: string | null;
  consents:     { slug: string; title: string; version: string; accepted_at: string }[];
  created_at:   string | null;
}

/**
 * Предложение закрыть обращение. Привязано к ответу оператора, а не к тикету:
 * предложили — игрок отказался и дописал — предложили снова.
 */
export interface TTicketCloseOffer {
  id:               number;
  message_id:       number;
  status:           'pending' | 'accepted' | 'declined';
  answered_at:      string | null;
  created_at:       string;
  offered_by_login: string | null;
}

export interface TTicketDetail extends TTicket {
  messages:        TTicketMessage[];
  internal_notes:  { id: number; admin_id: number; login?: string; avatar?: string | null; body: string; created_at: string }[];
  assignees:       { admin_id: number; login: string; avatar?: string | null; assigned_at?: string }[];
  user_info?:      TTicketUserInfo;
  events:          TTicketEvent[];
  close_offers?:   TTicketCloseOffer[];
  auto_close_at?:  string | null;
  auto_close_days?: number;
  /** Кнопки, по которым игрок прошёл в помощнике */
  flow_path?:      string[] | null;
  /** Снимок диалога с помощником на момент создания обращения */
  flow_dialog?:    FlowDialogSnapshot | null;
}

// Технические данные, собранные лаунчером по тикету
export type TDiagKind = 'snapshot' | 'files' | 'files_deep' | 'arma_rpt' | 'launcher_log';

export interface TDiagRequestItem {
  kind:   TDiagKind;
  status: 'pending' | 'done' | 'declined' | 'failed';
  label:  string;
  note?:  string;
}

export interface TDiagRequestAdmin {
  id:           number;
  message_id:   number | null;
  requested_by: number | null;
  from_bot:     boolean;
  created_at:   string;
  updated_at:   string;
  items:        TDiagRequestItem[];
}

export interface TDiagRecordAdmin {
  id:         number;
  kind:       TDiagKind;
  label:      string;
  message_id: number | null;
  /** Отчёт целиком: пути, версии, железо. Для журнала — сведения о файле */
  payload:    Record<string, unknown> | null;
  has_blob:   boolean;
  /** Запись ссылается на файл журнала, а в хранилище сервера его нет */
  blob_lost?: boolean;
  blob_size:  number | null;
  created_at: string;
}

export interface TDiagnosticsAdmin {
  requests: TDiagRequestAdmin[];
  records:  TDiagRecordAdmin[];
}

// ─── Token helpers ─────────────────────────────────────────────────────
// Единая авторизация (OIDC): тот же токен в localStorage, что и у UserAuthContext.
export const TOKEN_KEY         = 'user_access_token';
export const REFRESH_TOKEN_KEY = 'user_refresh_token';

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}
export function removeToken(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

// ─── Uploads session ──────────────────────────────────────────────────
// Приватная статика (/uploads/tickets|tasks) закрыта на backend HttpOnly-cookie:
// обычные <img src> не умеют слать Authorization, поэтому перед показом вложений
// нужно получить cookie через POST /api/uploads/session (с Bearer-токеном).
let _uploadsSessionAt = 0;

export async function ensureUploadsSession(): Promise<void> {
  // Cookie живёт 12 часов — обновляем не чаще раза в час
  if (Date.now() - _uploadsSessionAt < 60 * 60 * 1000) return;
  const token = getToken();
  if (!token) return;
  try {
    const res = await fetch(`${BASE}/api/uploads/session`, {
      method:      'POST',
      headers:     { Authorization: `Bearer ${token}` },
      credentials: 'include',
    });
    if (res.ok) _uploadsSessionAt = Date.now();
  } catch {
    // Некритично: вложения не отобразятся до следующей попытки
  }
}

// ─── Core fetch wrapper ───────────────────────────────────────────────
let _refreshPromise: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  // Дедупликация: если уже идёт обновление — ждём его
  if (_refreshPromise) return _refreshPromise;

  // OIDC silent-renew (oidc-client-ts): обновляет 'user_access_token' в localStorage.
  // Ленивый импорт — чтобы модуль oidc (обращается к window) не грузился при
  // импорте adminApi в node-окружении (юнит-тесты хелперов).
  _refreshPromise = import('./oidc')
    .then(({ getValidAccessToken }) => getValidAccessToken())
    .then(token => !!token)
    .catch(() => false);

  try {
    return await _refreshPromise;
  } finally {
    _refreshPromise = null;
  }
}

// ─── Новости ──────────────────────────────────────────────────────────
export interface TNewsAdminItem {
  id:           number;
  slug:         string;
  title:        string;
  excerpt:      string | null;
  cover_url:    string | null;
  is_published: boolean;
  published_at: string | null;
  created_by:   number;
  updated_by:   number | null;
  created_at:   string;
  updated_at:   string;
}

export interface TNewsAdminFull extends TNewsAdminItem {
  body_json: string | null;
  body_html: string;
}

export interface TNewsPayload {
  slug:          string;
  title:         string;
  excerpt?:      string | null;
  cover_url?:    string | null;
  body_json?:    string | null;
  body_html?:    string;
  is_published?: boolean;
  published_at?: string | null;
}

// ─── Лаунчер ──────────────────────────────────────────────────────────
export interface TLauncherJob {
  branch:     string;
  /** build — сборка из watch-папки; promote — перенос версии из другой ветки */
  kind?:      'build' | 'promote';
  status:     'running' | 'done' | 'error';
  startedAt:  string;
  finishedAt?: string;
  filesDone:  number;
  filesTotal: number;
  bytesDone:  number;
  error?:     string;
  version?:   string;
}

export interface TLauncherVersionSummary {
  id:        string;
  createdAt: string;
  totalSize: number;
  fileCount: number;
  /** Подписан ли манифест: неподписанную версию лаунчер не установит */
  signed?:   boolean;
  /** Сборка до ручной нумерации: id — время сборки */
  legacy?:   boolean;
  promotedFrom?: { branch: string; version: string } | null;
}

export interface TLauncherServerBranch {
  activeVersion: string | null;
  isPublic:      boolean;
  versions:      TLauncherVersionSummary[];
  /** Предложение для формы сборки: старшая версия ветки +1 */
  nextVersion?:  string;
}

export interface TLauncherPromoteDiff {
  added:         number;
  changed:       number;
  removed:       number;
  downloadBytes: number;
  totalSize:     number;
  against:       string | null;
  nextVersion:   string;
}

export interface TLauncherPluginExpected {
  version:      string | null;
  dlls:         { name: string; arch: 'x64' | 'x86' | 'unknown'; sha256: string }[];
  package_path: string;
  computed_at:  string;
}

export interface TLauncherOverview {
  branches: { id: number; name: string; title: string; created_at: string }[];
  server: {
    branches:  Record<string, TLauncherServerBranch>;
    watchDirs: string[];
    jobs:      TLauncherJob[];
    chunkSize: number;
    signing?:  { enabled: boolean; publicKey: string; error: string | null };
    disk?: {
      watch: Record<string, number>;
      chunks: { count: number; bytes: number };
      uploads: number;
      measuredAt: string;
    } | null;
  } | null;
  server_error: string | null;
  guard: {
    connected:     boolean;
    enabled:       boolean;
    degraded:      boolean;
    policy:        string | null;
    allowed_count: number;
  };
}

/** Состояние фона-сцены для редактора. */
export interface TLauncherSceneState {
  mode:        'news' | 'scene';
  published:   SceneConfig | null;
  draft:       SceneConfig | null;
  base_bg_url: string | null;
}

/** Файл медиатеки фона-сцены. */
export interface TLauncherSceneAsset {
  id:         number;
  kind:       'image' | 'sprite';
  name:       string;
  url:        string;
  width:      number;
  height:     number;
  bytes:      number;
  created_by: number | null;
  created_at: string;
}

/** Ссылка-иконка на главной странице лаунчера. */
export interface TLauncherLink {
  id:        number;
  title:     string;
  tooltip:   string | null;
  url:       string;
  icon_url:  string | null;
  sort:      number;
  is_active: number;
}

/** Файл из манифеста версии (просмотр в админке, только чтение). */
export interface TLauncherManifest {
  branch:  string;
  version: string;
  files:   { path: string; size: number; sha256: string }[];
}

/** Файл watch-папки — будущая сборка ветки. */
export interface TLauncherWatchFile {
  path:  string;
  size:  number;
  mtime: string;
}

export interface TLauncherCode {
  id:                number;
  branch_id:         number;
  branch_name:       string;
  code:              string;
  max_activations:   number;
  expires_at:        string | null;
  is_active:         number;
  note:              string | null;
  created_by:        number | null;
  created_at:        string;
  activations_count: number;
}

export interface TLauncherServerKey {
  id:           number;
  name:         string;
  key_prefix:   string;
  branches:     string[];
  last_used_at: string | null;
  last_branch:  string | null;
  last_version: string | null;
  last_ip:      string | null;
  created_at:   string;
  revoked_at:   string | null;
}

export interface TLauncherWhitelistEntry {
  id:         number;
  steam_id:   string;
  note:       string | null;
  added_by:   number | null;
  created_at: string;
}

export interface TLauncherExemption {
  id:         number;
  user_id:    number;
  login:      string | null;
  note:       string | null;
  added_by:   number | null;
  created_at: string;
}

/** Файл в раздаче дистрибутивов (канал обновлений / инсталлер) */
export interface TDistribFile {
  name:  string;
  size:  number;
  mtime: string;
  /** Публичная ссылка на скачивание с сервера раздачи */
  url:   string;
}

export interface TDistribTarget {
  files: TDistribFile[];
  /** latest.json канала обновлений или installer.json — как есть */
  meta:  Record<string, unknown> | null;
}

export interface TLauncherDistrib {
  updater:   TDistribTarget | null;
  installer: TDistribTarget | null;
  /** Сервер раздачи недоступен — текст причины */
  error:     string | null;
}

export interface TLauncherSession {
  user_id:           number;
  login:             string | null;
  steam_id:          string | null;
  launcher_version:  string | null;
  mod_branch:        string | null;
  mod_version:       string | null;
  in_game:           number;
  details:           unknown;
  last_heartbeat_at: string;
  created_at:        string;
}

/** Параметры запуска Arma 3: определения формы, редактируемые в админке */
export interface TGameParamOption {
  title: string;
  value: string;
}

export interface TGameParamDefinition {
  type: 'radio' | 'checkbox' | 'select' | 'number' | 'checkbox-group';
  title: string;
  tooltip?: string;
  /** Аргумент командной строки, например «-skipIntro» */
  parameter: string;
  values?: TGameParamOption[];
  items?: TGameParamOption[];
  min?: number;
  max?: number;
  step?: number;
  value: unknown;
  default: unknown;
  /** Параметр применяется только при взведённом чекбоксе слева */
  checked?: { value: boolean; default: boolean };
}

export interface TGameParamsGroup {
  id: string;
  title: string;
  canReset?: boolean;
  resetTitle?: string;
  items: string[];
}

export interface TGameParamsStructure {
  items: Record<string, TGameParamDefinition>;
  groups: TGameParamsGroup[];
}

// ─── Статистика (раздел «Статистика», право stats.view) ───────────────
export interface TStatsKpi {
  key: string;
  label: string;
  value: number;
  prev: number;
  /** Изменение к прошлому периоду, % (null — сравнивать не с чем) */
  delta: number | null;
  /** Куда «хорошо» расти: по умолчанию вверх (для тикетов — вниз) */
  good?: 'up' | 'down';
  unit?: 'rub' | 'count' | 'pct';
}

export interface TStatsSeries {
  key: string;
  label: string;
  points: { day: string; value: number }[];
  prev: { day: string; value: number }[];
}

export interface TStatsFunnelStep {
  event: string;
  label: string;
  hint: string;
  value: number;
  prev: number;
  /** Доля дошедших с предыдущего шага, % */
  conversion: number | null;
  conversionPrev: number | null;
}

export interface TStatsSummary {
  kpis: TStatsKpi[];
  series: TStatsSeries[];
  bars: { label: string; value: number; prev: number }[];
  funnel?: TStatsFunnelStep[];
  suggestions: { tone: 'bad' | 'good' | 'info'; text: string }[];
}

/** Вариант внешней ссылки: именованный адрес, между которыми переключаются */
export interface TLinkVariant {
  id:    string;
  label: string;
  url:   string;
}

export interface TSiteLink {
  key:      string;
  group:    string;
  title:    string;
  /** Адрес из каталога, когда вариант не выбран */
  fallback: string;
  /** Поддерживает служебный вариант «Актуальная сборка» */
  auto?:    boolean;
  /** Поддерживает служебный вариант «Инсталлер» */
  installer?: boolean;
  hint?:    string;
  /** id выбранного варианта; пусто = значение по умолчанию */
  active:   string;
  variants: TLinkVariant[];
  /** Адрес, который сейчас реально отдаётся сайту и лаунчеру */
  resolved: string;
}

/** Сводка для виджета «Лаунчер» на дашборде (launcher.status.view). */
export interface TLauncherDashboard {
  distribution: {
    online:   boolean;
    /** Причина недоступности сервера раздачи */
    error:    string | null;
    branches: { name: string; title: string; active_version: string | null }[];
    /** Идущая пересборка манифеста, если есть */
    job:      { branch: string; files_done: number; files_total: number } | null;
  };
  launchers: {
    active:   number;
    in_game:  number;
    /** Сидят не на активной версии своей ветки */
    outdated: number;
  };
  /** null — поток позиций от headless-клиента протух, числа сейчас нет */
  players_in_game: number | null;
  /** Сутки снимков с шагом 5 минут */
  series: {
    t:                 string;
    players_in_game:   number | null;
    launchers_active:  number;
    launchers_in_game: number;
  }[];
}

export interface TLauncherVersionStats {
  launcher: { version: string | null; count: number }[];
  mod:      { branch: string | null; version: string | null; count: number }[];
}

export interface TNetAggRow {
  key:       string | null;
  reports:   number;
  users:     number;
  avg_speed: number | null;
  cache_hit: number | null;
  errors:    number;
}

export interface TNetSummary {
  days:      number;
  by_day:    TNetAggRow[];
  by_source: TNetAggRow[];
  by_region: TNetAggRow[];
  by_isp:    TNetAggRow[];
}

export interface TNetReport {
  id:               number;
  user_id:          number;
  login:            string | null;
  kind:             'test' | 'download';
  branch:           string | null;
  version:          string | null;
  source_url:       string;
  bytes:            number;
  duration_ms:      number;
  avg_speed:        number;
  cache_hit_ratio:  number | null;
  latency_ms:       number | null;
  retries:          number;
  switches:         number;
  error:            string | null;
  isp:              string | null;
  region:           string | null;
  launcher_version: string | null;
  created_at:       string;
}

export interface TLauncherDeviceUserRow {
  user_id:    number;
  login:      string | null;
  steam_id:   string | null;
  first_seen: string;
  last_seen:  string;
}

export interface TLauncherUserDevice {
  device: {
    id:            number;
    machine_guid:  string | null;
    board_serial:  string | null;
    disk_serial:   string | null;
    computer_name: string | null;
    windows_sid:   string | null;
    components:    unknown;
    first_seen:    string;
    last_seen:     string;
  };
  users:          TLauncherDeviceUserRow[];
  steam_accounts: {
    steam_id64: string; persona_name: string | null; last_seen: string;
    /** Аккаунты сайта с этим Steam (по сводке) */
    owners?: { user_id: number; login: string | null }[];
  }[];
  /** Идентификаторы железа из сигналов устройства и где ещё они встречаются */
  identifiers?: {
    type: string; value: string; first_seen: string; last_seen: string;
    matches: { device_id: number; computer_name: string | null; user_id: number; login: string | null }[];
  }[];
}

export interface TLauncherMultiaccount {
  device_id:     number;
  computer_name: string | null;
  last_seen:     string;
  users:         TLauncherDeviceUserRow[];
}

/** Подозрение на спуфера железа: средний скоринг, решает админ */
export interface TLauncherMultiaccSuspicion {
  id:                number;
  device_id:         number;
  matched_device_id: number;
  user_id:           number;
  login:             string | null;
  score:             number;
  signals:           { type: string; value: string; weight: number }[] | null;
  status:            'new' | 'dismissed' | 'confirmed';
  created_at:        string;
  updated_at:        string;
}

/** Блок «Лаунчер» в карточке пользователя */
export interface TLauncherUserProfile {
  /** Есть устройство, сессия или установка — лаунчер стоял */
  installed:     boolean;
  last_activity: string | null;
  session:       TLauncherSession | null;
  devices:       TLauncherUserDevice[];
  /** Сколько установок лаунчера привязано к аккаунту */
  installs:      number;
  /** Установки, с которых заходили и другие аккаунты */
  shared_installs: {
    token:      string;
    first_seen: string;
    last_seen:  string;
    users:      { user_id: number; login: string | null; first_seen: string; last_seen: string }[];
  }[];
}

export interface TLauncherNotification {
  id:              number;
  title:           string;
  body_html:       string;
  type:            'push' | 'modal';
  severity:        'error' | 'warning' | 'success' | 'info';
  target:          'all' | 'branch' | 'users';
  target_branch:   string | null;
  expires_at:      string | null;
  is_active:       number;
  created_by:      number | null;
  created_at:      string;
  delivered_count: number;
  closed_count:    number;
}

export interface TLauncherNotificationRecipient {
  user_id:      number;
  login:        string | null;
  /** null у адресатов target=users, до которых оповещение так и не дошло */
  delivered_at: string | null;
  closed_at:    string | null;
}

// ─── Эталон установки Arma 3 ──────────────────────────────────────────

export type TBaselineFileState = 'baseline' | 'ignore';
export type TBaselineRuleState = 'ignore' | 'forbidden';
export type TGameCheckStatus = 'ok' | 'issues' | 'outdated' | 'pending';

/** Правило области эталона: какие файлы игры сверяются (пресет лаунчера) */
export interface TBaselineScopeRule {
  mask:    string;
  include: boolean;
}

export interface TBaselineStatusInfo {
  /** scope: null — эталон снят со всей папки игры, до пресета области */
  accepted: {
    id: number; buildid: string; exe_version: string; accepted_at: string | null; files: number;
    scope: TBaselineScopeRule[] | null;
  } | null;
  draft: {
    id: number; buildid: string; exe_version: string; created_at: string; files: number;
    scope: TBaselineScopeRule[] | null;
  } | null;
  steam: {
    client:            string | null;
    server:            string | null;
    client_changed_at: string | null;
    server_changed_at: string | null;
    checked_at:        string | null;
  };
  steam_newer:           boolean;
  players_newer:         number;
  players_newer_buildid: string | null;
  newer_days:            number;
}

export interface TBaselineDiffEntry {
  path:  string;
  kind:  'added' | 'changed' | 'removed';
  state: TBaselineFileState | null;
  size:  number;
}

/** Узел дерева принятого эталона: папка со сводкой поддерева или файл */
export interface TBaselineTreeNode {
  name:    string;
  path:    string;
  dir:     boolean;
  files:   number;
  size:    number;
  ignored: number;
  sha256:  string | null;
  state:   TBaselineFileState | null;
}

export interface TBaselineRule {
  id:      number;
  pattern: string;
  state:   TBaselineRuleState;
  note:    string | null;
  enabled: number;
}

export interface TGameCheckPlayer {
  user_id:    number;
  login:      string | null;
  buildid:    string | null;
  status:     TGameCheckStatus;
  changed:    number;
  missing:    number;
  extra:      number;
  forbidden:  number;
  checked_at: string;
}

export interface TGameCheckVerdict {
  buildid:          string | null;
  exe_version:      string | null;
  status:           TGameCheckStatus;
  baseline_id:      number | null;
  changed:          number;
  missing:          number;
  extra:            number;
  forbidden:        number;
  paths:            { changed: string[]; missing: string[]; extra: string[]; forbidden: string[] } | null;
  launcher_version: string | null;
  checked_at:       string;
}

// ─── Справочник кодов ошибок ──────────────────────────────────────────

/** Источник кода ошибки; он же префикс в самом коде: INS, LNC, API, AUT, DST. */
export type TErrorSource = 'installer' | 'launcher' | 'backend' | 'auth' | 'distribution';

/**
 * Карточка кода. Две группы полей неспроста: `player_*` игрок видит на
 * экране, `admin_*` читает поддержка — в причинах и порядке разбора
 * регулярно всплывают пути и адреса, которые игроку показывать незачем.
 */
export interface TErrorCode {
  code:           string;
  source:         TErrorSource;
  title:          string;
  player_summary: string;
  player_action:  string;
  admin_causes:   string;
  admin_steps:    string;
  updated_by:     number | null;
  updated_at:     string;
}

/** Строка списка в админке: со счётчиком срабатываний за неделю. */
export interface TErrorCodeRow extends TErrorCode {
  hits_7d: number;
}

export type TErrorCodePayload = Omit<TErrorCode, 'updated_by' | 'updated_at'>;

/** Отчёт о срабатывании: `case_no` игрок диктует в поддержку. */
export interface TErrorReport {
  id:          number;
  case_no:     string;
  code:        string;
  source:      TErrorSource;
  /** Технический текст ошибки — на экране игрока его нет */
  detail:      string | null;
  app_version: string | null;
  os_version:  string | null;
  /** Пуст у отчётов из установщика: он работает до всякого входа */
  user_id:     number | null;
  ticket_id:   number | null;
  created_at:  string;
}

// ── Заметки и пины на игрока ──────────────────────────────────────────
/** Чья карточка: аккаунт сайта или игрок без ЛК по SteamID */
export type TNoteSubject = { userId: number } | { steamId: string };
export type TNoteImportance = 'info' | 'warn' | 'important';
export type TNoteVisibility = 'all' | 'leadership' | 'author';
export type TPinTone = 'neutral' | 'good' | 'warn' | 'bad';
export interface TNoteLink { kind: 'ticket' | 'ban' | 'link' | 'legacy_note'; id: number }

export interface TPlayerNote {
  id: number;
  user_id: number | null;
  steam_id: string | null;
  author_id: number | null;
  author_login: string | null;
  importance: TNoteImportance;
  category_id: number | null;
  visibility: TNoteVisibility;
  kind: 'manual' | 'auto';
  /** Автозаметка: ban | restriction | ap_grant | mail */
  source: string | null;
  body: string;
  pinned: boolean;
  parent_id: number | null;
  links: TNoteLink[];
  created_at: string;
  updated_at: string | null;
  can_edit: boolean;
  can_hide: boolean;
  /** Скрыта: такие приходят только в списке со скрытыми */
  deleted_at: string | null;
}

export interface TPlayerPin {
  id: number; text: string; tone: TPinTone; visibility: 'all' | 'leadership';
  author_id: number; author_login: string | null; expires_at: string | null; created_at: string; can_remove: boolean;
}

export interface TLegacyNote {
  id: number;
  category: string | null;
  admin_steam_id: string | null;
  admin_name: string | null;
  /** HTML старого кабинета, уже очищенный на сервере */
  html: string;
  created_at: string | null;
  copied_to: number | null;
}

export interface TNotesBundle {
  subject: { userId: number | null; steamId: string | null };
  notes: TPlayerNote[];
  pins: TPlayerPin[];
  legacy: TLegacyNote[];
  categories: { id: number; name: string }[];
}

export interface TNoteFlags {
  important: number;
  pinned: { id: number; body: string; importance: TNoteImportance }[];
  pins: TPlayerPin[];
}

export interface TNoteInput {
  body?: string; importance?: TNoteImportance; visibility?: TNoteVisibility;
  category_id?: number | null; pinned?: boolean; parent_id?: number | null; links?: TNoteLink[];
}

export interface TNoteHistoryItem {
  id: number; action: 'edit' | 'delete' | 'restore' | 'pin' | 'unpin';
  editor_id: number | null; editor_login: string | null;
  body: string | null; importance: string | null; visibility: string | null; category_id: number | null; at: string | null;
}

const noteBase = (s: TNoteSubject) =>
  'userId' in s ? `/api/users/${s.userId}` : `/api/users/player/${encodeURIComponent(s.steamId)}`;

// ── Правка персонажа и история (/api/admin/players) ──────────────────
export type TPlayerEditOp = 'set' | 'add';
export interface TPlayerEditableField {
  key: string;
  label: string;
  /** offline — только когда игрок не в игре */
  mode: 'offline' | 'any';
  kind: 'int' | 'num' | 'str' | 'enum';
  ops: TPlayerEditOp[];
  min?: number;
  values?: number[];
  max_len?: number;
  value: string | number | null;
}
export interface TPlayerEditable {
  online: boolean;
  admin_has_steam: boolean;
  fields: TPlayerEditableField[];
  can_vip: boolean;
  can_inventory: boolean;
}
export interface TPlayerEditChange { field: string; op: TPlayerEditOp; value: string | number; was?: string | number | null }
export interface TPlayerEditResult {
  applied: { field: string; label: string; before: unknown; after: unknown }[];
  online: boolean;
}
export interface TPlayerHistoryItem {
  key: string;
  kind: 'edit' | 'ap' | 'inventory' | 'ban' | 'note';
  source: 'new' | 'old' | 'game' | 'site';
  /** По Москве, «ГГГГ-ММ-ДД чч:мм:сс» */
  at: string;
  who: string | null;
  title: string;
  detail: string | null;
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  opts?: { skipAuthRedirect?: boolean }
): Promise<T> {
  const doFetch = (token: string | null) =>
    fetch(`${BASE}${path}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

  let res = await doFetch(getToken());

  // Access token истёк — попытаться обновить и повторить запрос
  if (res.status === 401 && !opts?.skipAuthRedirect) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      res = await doFetch(getToken());
    }
  }

  if (res.status === 401 && !opts?.skipAuthRedirect) {
    // Без перезагрузки: страница остаётся как есть (с открытыми формами), а
    // UserAuthProvider показывает окно «Сессия истекла» с повторным входом
    trackNow('auth_forced_logout', 'panel', { reason: 'api_401', path: window.location.pathname, api: `${method} ${path}`.slice(0, 200), status: 401 });
    window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT));
    const error = new Error('Сессия истекла, войдите снова') as TApiError;
    error.status = 401;
    throw error;
  }

  if (!res.ok) {
    const data = await res.json().catch(() => ({})) as { error?: string };
    const error = new Error(data.error ?? `Ошибка ${res.status}`) as TApiError;
    error.status = res.status;
    error.data = data;
    throw error;
  }

  return res.json() as Promise<T>;
}

/** GET файла (не JSON) с тем же обновлением токена, что и request. */
async function requestBlob(path: string): Promise<{ blob: Blob; filename: string | null }> {
  const doFetch = (token: string | null) =>
    fetch(`${BASE}${path}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  let res = await doFetch(getToken());
  if (res.status === 401 && await tryRefresh()) res = await doFetch(getToken());
  if (!res.ok) {
    const data = await res.json().catch(() => ({})) as { error?: string };
    const error = new Error(data.error ?? `Ошибка ${res.status}`) as TApiError;
    error.status = res.status;
    throw error;
  }
  const disposition = res.headers.get('Content-Disposition') ?? '';
  const filename = /filename="([^"]+)"/.exec(disposition)?.[1] ?? null;
  return { blob: await res.blob(), filename };
}

/** История «Акции и подарки» — одинаковая у игрока и в карточке админа. */
export interface TPromoHistory {
  steam_id?: string | null;
  issued_codes: {
    code: string; title: string; issued_at: string; expires_at: string | null;
    status: 'unused' | 'used' | 'expired'; image_url: string | null; promo_id: number | null;
  }[];
  used_codes:   { code: string; name: string | null; type: string; classname: string | null }[];
  gifts:        {
    promo_id: number; title: string; image_url: string | null; claimed_at: string;
    rewards: { kind: string; title: string; hint: string | null; items?: { name: string; count: number }[] }[];
  }[];
}

// ─── Инциденты (права incidents.view / incidents.manage) ─────────────
export type TIncidentSeverity = 'critical' | 'warning';
export type TIncidentKind = 'down' | 'degraded';
export type TIncidentComponentStatus = 'ok' | 'degraded' | 'down' | 'maintenance' | 'network' | 'unconfigured' | 'disabled' | 'unknown';
export type TIncidentPeriod = '24h' | '7d' | '30d';
export type TIncidentTypeFilter = 'down' | 'degraded' | 'maintenance';

export interface TIncidentComponent {
  key:          string;
  label:        string;
  severity:     TIncidentSeverity;
  status:       TIncidentComponentStatus;
  since:        string | null;
  detail:       string | null;
  last_check:   string | null;
  incident_id:  number | null;
  latency_ms:   number | null;
  slow_ms:      number | null;
  value:        number | null;
  warn_pct:     number | null;
  window_until: string | null;
}

export interface TIncident {
  id:               number;
  component:        string;
  label:            string;
  severity:         TIncidentSeverity;
  kind:             TIncidentKind;
  is_maintenance:   boolean;
  started_at:       string;
  ended_at:         string | null;
  last_error:       string | null;
  ack_by:           number | null;
  ack_login:        string | null;
  ack_at:           string | null;
  note:             string | null;
  classified_login: string | null;
  classified_at:    string | null;
}

export interface TIncidentSegment {
  kind:        'down' | 'degraded' | 'maintenance';
  from:        string;
  to:          string | null;
  incident_id: number;
  error:       string | null;
}

export interface TIncidentTimelineComponent {
  key:        string;
  label:      string;
  severity:   TIncidentSeverity;
  enabled:    boolean;
  uptime_pct: number | null;
  downtime_s: number;
  segments:   TIncidentSegment[];
  windows:    { from: string; to: string }[];
}

export interface TIncidentTimeline {
  period:     TIncidentPeriod;
  from:       string;
  to:         string;
  step_min:   number;
  coverage:   string[];
  components: TIncidentTimelineComponent[];
  summary: {
    uptime_critical_pct: number | null;
    incidents:           number;
    degradations:        number;
    maintenance:         number;
    downtime_s:          number;
    mttr_s:              number | null;
    worst:               { key: string; label: string; downtime_s: number } | null;
  };
}

export interface TIncidentMetricPoint {
  at:         string;
  checks:     number;
  failures:   number;
  slow:       number;
  lat_avg_ms: number | null;
  lat_max_ms: number | null;
  lat_p95_ms: number | null;
  value:      number | null;
}

export interface TMaintenanceWindow { start: string; every_h: number; duration_min: number }

export interface TIncidentComponentOverride {
  enabled?:          boolean;
  severity?:         TIncidentSeverity;
  down_after_s?:     number;
  degraded_after_s?: number;
  timeout_ms?:       number;
  slow_ms?:          number;
  url?:              string;
  api_key?:          string;
  warn_pct?:         number;
  windows?:          TMaintenanceWindow[];
}

export interface TIncidentComponentConfig {
  key:          string;
  label:        string;
  how:          string;
  address_note: string | null;
  has_url:      boolean;
  has_api_key:  boolean;
  has_warn_pct: boolean;
  has_latency:  boolean;
  can_disable:  boolean;
  defaults: {
    severity:         TIncidentSeverity;
    down_after_s:     number;
    degraded_after_s: number;
    timeout_ms:       number;
    slow_ms:          number | null;
    warn_pct:         number | null;
    windows:          TMaintenanceWindow[];
  };
  overrides: TIncidentComponentOverride;
  effective: TIncidentComponentOverride & { enabled: boolean; severity: TIncidentSeverity; windows: TMaintenanceWindow[] };
}

export interface TIncidentConfig {
  components:             TIncidentComponentConfig[];
  retention_days:         number;
  metrics_retention_days: number;
}

// ─── Игровые промокоды (право promocodes.manage) ─────────────────────
export type TPromoType = 'discount' | 'add' | 'add_new';

export interface TPromoCatalogGroup {
  allKey: string | null;
  title: string;
  items: { key: string; title: string }[];
}

export interface TPromoCode {
  name: string;
  code: string;
  type: TPromoType;
  available_for: string;
  hide: string;            // '0' — виден в игровом магазине
  discount: number;
  max_count: number;
  person_count: number;
  now_count: number;
  items_classnames: string; // SQF-массив строкой
  add_items: string;        // SQF-массив строкой
  perm: string;             // '1' — бессрочный
  stop_date: string | null;
  uses_count: number;
}

export interface TPromoUse {
  id: number;
  type: string;
  code: string;
  classname: string;
  pid: string;
  classname_title: string | null;
}

export interface TPromoItemHit {
  classname: string;
  name: string;
  kind: 'vehicle' | 'weapon' | 'item';
}

export interface TPromoCreateBody {
  /** Создать, даже если классов наград нет в словаре сервера */
  force?: boolean;
  name: string;
  type: TPromoType;
  available_for: string;               // 'all' | SteamID64
  visible_in_shop: boolean;
  discount: number;                    // % для discount
  max_count: number;                   // 0 = без общего лимита
  person_count: number;                // 0 = без лимита на игрока
  items_classnames: string[];          // ключи каталога (discount)
  add_items: (string | [string, number])[]; // награды (add/add_new)
  days: number;                        // 0 = бессрочный
  prefix: string;
  /** Свой код целиком; пусто — код генерируется из префикса */
  code?: string;
}

// ─── Акции лаунчера (продающий блок, право launcher.manage) ──────────
export type TLauncherPromoKind = 'action' | 'gift' | 'product' | 'banner';
export type TLauncherGiftCondition = 'none' | 'login' | 'hours';
export type TLauncherPromoTarget = 'none' | 'vip' | 'perm_item' | 'perm_vehicle' | 'donat_inventory';
export type TGiftVehType = 'Car' | 'Air' | 'Ship';

export interface TGiftMailItem {
  kind: 'v_item' | 'gear' | 'candy' | 'apb';
  classname?: string;
  count: number;
  /** Патроны в магазине (gear): число знает только игра, задаётся вручную */
  rounds?: number;
}

export type TGiftReward =
  | { kind: 'vehicle_garage'; classname: string; vtype: TGiftVehType; price?: number }
  | { kind: 'vehicle_life'; classname: string; vtype: TGiftVehType; lives: number; price?: number }
  | { kind: 'vehicle_shop'; classname: string; vtype: TGiftVehType }
  | { kind: 'vehicle_ext'; classname: string; vtype: TGiftVehType; days: number; price?: number }
  | { kind: 'perm_item'; classname: string; ptype: string; value: number }
  | { kind: 'vip'; level: number; days: number }
  | { kind: 'mail'; items: TGiftMailItem[]; theme?: string }
  | { kind: 'candy'; count: number; theme?: string }
  | { kind: 'apb'; count: number; theme?: string };

/** Payload create/update акции — зеркало parsePromoBody на бэкенде */
export interface TLauncherPromoBody {
  kind: TLauncherPromoKind;
  title: string;
  /** Сохранить, даже если классов нет в словаре сервера */
  force?: boolean;
  body?: string;
  badge_text?: string;
  button_text?: string;
  button_url?: string;
  in_game_shop?: boolean;
  code_mode?: 'personal' | 'shared' | null;
  shared_code?: string;
  promo_name?: string;
  promo_type?: TPromoType | null;
  promo_discount?: number;
  promo_items?: string[];
  promo_add_items?: (string | [string, number])[];
  promo_days?: number;
  promo_prefix?: string;
  gift_condition?: TLauncherGiftCondition | null;
  gift_hours?: number;
  gift_rewards?: TGiftReward[];
  target_kind?: TLauncherPromoTarget;
  target_classname?: string;
  target_has?: boolean;
  starts_at?: string | null;
  ends_at?: string | null;
  sort?: number;
  is_active?: boolean;
}

export interface TLauncherPromoRow {
  id: number;
  kind: TLauncherPromoKind;
  title: string;
  body: string | null;
  image_url: string | null;
  badge_text: string | null;
  button_text: string | null;
  button_url: string | null;
  in_game_shop: number;
  code_mode: 'personal' | 'shared' | null;
  shared_code: string | null;
  promo_name: string | null;
  promo_type: TPromoType | null;
  promo_discount: number;
  promo_items_json: string | null;
  promo_add_items_json: string | null;
  promo_days: number;
  promo_prefix: string | null;
  gift_condition: TLauncherGiftCondition | null;
  gift_hours: number;
  gift_rewards_json: string | null;
  target_kind: TLauncherPromoTarget;
  target_classname: string | null;
  target_has: number;
  starts_at: string | null;
  ends_at: string | null;
  sort: number;
  is_active: number;
  created_at: string;
  archived_at: string | null;
  counters: { views: number; opens: number; clicks: number; activations: number; claims: number; dismissals: number };
}

/** «Кто воспользовался» акцией — зеркало PromoUsageRow бэкенда */
export interface TLauncherPromoUsage {
  user_id: number | null;
  login: string | null;
  steam_id: string | null;
  action: 'issued' | 'used' | 'claimed' | 'dismissed';
  at: string | null;
  code: string | null;
  rewards: { kind: string; title: string; hint: string | null }[] | null;
}

// ─── API endpoints ────────────────────────────────────────────────────
/** Запись колокольчика с сервера (GET /api/notifications) */
export interface TNotificationItem {
  id:        string;
  type:      string;
  title:     string;
  body:      string;
  link?:     string;
  timestamp: string;
  read:      boolean;
}

export type TNotificationGroup = 'tickets' | 'tasks' | 'shifts' | 'chat' | 'system';

/** Настройка доставки одного типа события (GET /api/notifications/prefs) */
export interface TNotificationPref {
  type:  string;
  group: TNotificationGroup;
  label: string;
  hint:  string;
  site:  boolean;
  tg:    boolean;
}

export type TTgMode = 'off' | 'staff' | 'roles' | 'all';

/** Привязка Telegram в профиле (GET /api/me/telegram) */
export interface TQuietSettings {
  quiet_from:     number | null;
  quiet_to:       number | null;
  incidents_loud: boolean;
}

/** Сводки в Telegram по расписанию (GET /api/notifications/digest) */
export interface TDigestSchedule {
  daily:    boolean;
  weekly:   boolean;
  monthly:  boolean;
  /** Минуты от полуночи по Москве */
  time:     number;
  sections: string[];
}

export interface TDigestSettings {
  /** Доступно ли «Утро» (ежедневная сводка) */
  morning:  boolean;
  /** Разделы недельной и месячной сводки, доступные по правам */
  sections: { key: string; label: string }[];
  schedule: TDigestSchedule;
}

export interface TMyTelegram {
  bot_username: string | null;
  bot_online:   boolean;
  can_link:     boolean;
  /** Почему привязка недоступна: not_configured | off | not_allowed | need_2fa | inactive */
  reason:       string | null;
  link: { tg_username: string | null; tg_name: string | null; linked_at: string; blocked: boolean } | null;
}

/** Telegram в карточке пользователя (GET /api/users/:id/telegram) */
export interface TUserTelegram {
  configured: boolean;
  /** Может ли человек пользоваться ботом сейчас */
  allowed:    boolean;
  /** Почему нет: not_configured | off | not_allowed | need_2fa | inactive | unknown */
  reason:     string | null;
  link: { tg_username: string | null; tg_name: string | null; linked_at: string; blocked: boolean } | null;
}

/** Страница бота в админке (GET /api/settings/telegram) */
/** Пороги алертов бота: 0 — алерт выключен */
export interface TTgAlerts {
  big_payment_rub: number;
  online_drop_pct: number;
  online_min:      number;
}

export interface TTelegramAdmin {
  configured: boolean;
  settings:   { mode: TTgMode; roles: string[]; alerts: TTgAlerts };
  roles:      string[];
  status: {
    online:    boolean;
    heartbeat: { at: string; telegram_ok: boolean; bot_username: string | null; error: string | null; version: string | null } | null;
    queue:     { pending: number; failed: number; sent: number };
  };
  links: { user_id: number; login: string | null; tg_username: string | null; tg_name: string | null; linked_at: string; blocked: boolean }[];
}

export interface TForumLink {
  user_id:         number;
  forum_user_id:   number;
  forum_username:  string;
  /** email | steam | password | new | admin */
  link_method:     string;
  linked_by_admin: number | null;
  linked_at:       string;
}

export interface TForumProfile {
  forum_user_id:  number;
  username:       string;
  user_title:     string | null;
  user_state:     string;
  is_staff:       boolean;
  is_banned:      boolean;
  ban_reason:     string | null;
  ban_end:        string | null;
  message_count:  number;
  reaction_score: number;
  trophy_points:  number;
  warning_points: number;
  register_date:  string | null;
  last_activity:  string | null;
  /** Аватар на форуме (только у привязанных) */
  avatar_url:     string | null;
  /** Расширенный профиль; null — ещё не выгружен */
  extra:          TForumProfileExtra | null;
  synced_at:      string;
}

export interface TForumProfileExtra {
  location:  string | null;
  website:   string | null;
  about:     string | null;
  signature: string | null;
  groups:    string[];
  fields:    { id: string; title: string; value: string }[];
}

export type TForumCommandInput =
  | { action: 'rename'; username: string }
  | { action: 'title'; title: string }
  | { action: 'ban'; days: number; reason: string }
  | { action: 'unban' };

export interface TForumCommand {
  id:         number;
  action:     'rename' | 'title' | 'ban' | 'unban';
  payload:    Record<string, unknown> | null;
  /** pending — ждёт форума (аддон забирает раз в минуту) */
  status:     'pending' | 'done' | 'failed';
  error:      string | null;
  created_by: string;
  created_at: string;
  done_at:    string | null;
}

export interface TUserForum {
  link:        TForumLink | null;
  profile:     TForumProfile | null;
  profile_url: string | null;
  /** Последние правки аккаунта форума из панели */
  commands:    TForumCommand[];
  ips?:        { ip: string; first_seen: string; last_seen: string }[];
  /** Смены ника на форуме, свежие сверху */
  name_history?: TForumNameChange[];
}

export interface TForumNameChange {
  old_username: string;
  new_username: string;
  change_date:  string;
  /** false — смена скрыта в профиле форума */
  visible:      boolean;
}

/** Как игрок получил ник: первый, через ЛК, в обход ЛК, неизвестно (нет таблицы смен ЛК) */
export type TGameNameVia = 'first' | 'lk' | 'outside' | 'unknown';

export interface TGameNameHistory {
  steam_id: string | null;
  current:  string | null;
  /** Последнее событие игрока на сервере */
  last_seen?: string | null;
  names: {
    name: string; current: boolean; from: string | null; to: string | null;
    /** Сколько раз игрок был под этим ником (клан-теги, возвраты к старому) */
    periods?: number;
    via: TGameNameVia; cost: number | null;
  }[];
  lk_available: boolean;
}

export interface TGameNameMatches {
  names: string[];
  matches: {
    steam_id: string;
    game_name: string | null;
    names: string[];
    user: { id: number; login: string | null; display_name: string | null; status: string | null } | null;
  }[];
}

export interface TForumSearchItem extends TForumProfile {
  profile_url:    string;
  linked_user_id: number | null;
}

/** GET /api/admin/fractions: фракции только на просмотр */
/** Период статистики фракций */
export type TFractionPeriod = 'today' | '7' | '30' | '90';
export interface TFraction {
  /** available — fractions.isAvailable: фракция свободна для аренды */
  code: string; name: string; type: number; available: boolean;
  /** Скрыта из списка в админке (настройка сайта: на сервере не используется) */
  hidden?: boolean;
  bank: number; bp: number; rating: number; ap: number;
  rent_price: number | null; rent_until: string | null;
  max_members: number | null; members: number; online: number;
  color: string;
  leader: { steam_id: string; name: string; rank: number } | null;
  /** Наличные + банк + депозиты состава */
  wealth: number;
  /** За период по нынешнему составу; null — источника нет в базе */
  avg_online: number | null; donate_rub: number | null; kills: number | null; deaths: number | null;
}
export interface TFractionMember {
  steam_id: string; name: string; rank: number; online: boolean; last_seen: string | null;
  cash: number; bank: number; illegal: number; deposit: number; donorlevel: number;
  hours: number | null; kills: number | null; deaths: number | null; donate_rub: number | null;
}
export interface TFractionDetail {
  period: TFractionPeriod;
  hidden?: boolean;
  fraction: TFraction;
  stats: {
    hours: number | null; payers: number | null;
    cash_bank: number; deposits: number; deposit_holders: number; illegal: number; illegal_holders: number;
    richest: { name: string; value: number } | null; vip: number; vip2: number;
    online_days: { day: string; avg: number }[];
    /** count — убийства (logs_death), agonies — ранения до агонии */
    victims: { code: string | null; name: string; color: string; count: number; agonies: number }[];
  };
  members: TFractionMember[];
  money_log: { id: number; account: string; name: string | null; mode: string | null; value: number; comment: string | null; at: string | null }[];
  bp_log: { id: number; steam_id: string | null; modifier: string | null; sum: number; comment: string | null; at: string | null }[];
}

export const adminApi = {

  // ── Заметки и пины на игрока (/api/users/…/notes) ──────────────────
  playerNotes: {
    get: (s: TNoteSubject, hidden = false) =>
      request<TNotesBundle>('GET', `${noteBase(s)}/notes${hidden ? '?hidden=1' : ''}`),
    flags: (s: TNoteSubject) => request<TNoteFlags>('GET', `${noteBase(s)}/notes/flags`),
    create: (s: TNoteSubject, input: TNoteInput) => request<TPlayerNote>('POST', `${noteBase(s)}/notes`, input),
    copyLegacy: (s: TNoteSubject, legacyId: number) =>
      request<TPlayerNote>('POST', `${noteBase(s)}/notes/legacy/${legacyId}/copy`),
    update: (id: number, input: TNoteInput) => request<TPlayerNote>('PATCH', `/api/users/notes/${id}`, input),
    hide: (id: number) => request<{ ok: true }>('DELETE', `/api/users/notes/${id}`),
    restore: (id: number) => request<{ ok: true }>('POST', `/api/users/notes/${id}/restore`),
    history: (id: number) => request<{ items: TNoteHistoryItem[] }>('GET', `/api/users/notes/${id}/history`),
    addPin: (s: TNoteSubject, input: { text: string; tone?: TPinTone; visibility?: 'all' | 'leadership'; expires_at?: string | null }) =>
      request<TPlayerPin>('POST', `${noteBase(s)}/pins`, input),
    removePin: (id: number) => request<{ ok: true }>('DELETE', `/api/users/pins/${id}`),
    categories: () => request<{ items: { id: number; name: string }[] }>('GET', '/api/users/note-categories'),
    createCategory: (name: string) => request<{ id: number; name: string }>('POST', '/api/users/note-categories', { name }),
    renameCategory: (id: number, name: string) => request<{ ok: true }>('PATCH', `/api/users/note-categories/${id}`, { name }),
    deleteCategory: (id: number) => request<{ ok: true }>('DELETE', `/api/users/note-categories/${id}`),
  },

  // ── Правка персонажа и история (/api/admin/players) ────────────────
  playerAdmin: {
    /** «Войти как игрок»: доступно только владельцам из настроек сервера */
    impersonateAllowed: () => request<{ allowed: boolean }>('GET', '/api/admin/players/impersonate/allowed'),
    impersonate: (input: { user_id: number; mode: 'view' | 'full'; minutes: 30 | 120; totp_code: string }) =>
      request<{ token: string; expires_at: number; login: string }>('POST', '/api/admin/players/impersonate', input),
    editable: (steamId: string) => request<TPlayerEditable>('GET', `/api/admin/players/${steamId}/editable`),
    edit: (steamId: string, changes: TPlayerEditChange[], reason: string) =>
      request<TPlayerEditResult>('POST', `/api/admin/players/${steamId}/edit`, { changes, reason }),
    deactivateVip: (steamId: string, reason: string) =>
      request<{ ok: true }>('POST', `/api/admin/players/${steamId}/vip/deactivate`, { reason }),
    products: (q: string) =>
      request<{ items: { id: number; title: string; type: string | null }[] }>('GET', `/api/admin/players/products?q=${encodeURIComponent(q)}`),
    give: (steamId: string, input: { product_id: number; lives?: number | null; can_transfer?: boolean; reason: string }) =>
      request<{ id: number }>('POST', `/api/admin/players/${steamId}/inventory`, input),
    withdraw: (steamId: string, itemId: number, reason: string) =>
      request<{ ok: true }>('POST', `/api/admin/players/${steamId}/inventory/${itemId}/withdraw`, { reason }),
    restore: (steamId: string, itemId: number, reason: string) =>
      request<{ ok: true }>('POST', `/api/admin/players/${steamId}/inventory/${itemId}/restore`, { reason }),
    history: (steamId: string, kinds: string[] = []) =>
      request<{ items: TPlayerHistoryItem[] }>('GET', `/api/admin/players/${steamId}/history${kinds.length ? `?kinds=${kinds.join(',')}` : ''}`),
  },

  fractions: {
    list: (period: TFractionPeriod = '30') => request<{ items: TFraction[] }>('GET', `/api/admin/fractions?period=${period}`),
    get: (code: string, period: TFractionPeriod = '30') =>
      request<TFractionDetail>('GET', `/api/admin/fractions/${encodeURIComponent(code)}?period=${period}`),
    setHidden: (code: string, hidden: boolean) =>
      request<{ ok: boolean; hidden: boolean }>('PUT', `/api/admin/fractions/${encodeURIComponent(code)}/hidden`, { hidden }),
  },

  // ── Telegram-бот: привязка из профиля и страница в админке ─────────
  telegram: {
    mine: () =>
      request<TMyTelegram>('GET', '/api/me/telegram'),

    linkCode: () =>
      request<{ url: string; expires_at: string }>('POST', '/api/me/telegram/link-code'),

    unlinkMine: () =>
      request<{ ok: boolean }>('DELETE', '/api/me/telegram'),

    admin: () =>
      request<TTelegramAdmin>('GET', '/api/settings/telegram'),

    saveAdmin: (data: { mode: TTgMode; roles: string[]; alerts?: TTgAlerts }) =>
      request<{ ok: boolean }>('PUT', '/api/settings/telegram', data),

    unlinkUser: (userId: number) =>
      request<{ ok: boolean }>('DELETE', `/api/settings/telegram/links/${userId}`),

    test: () =>
      request<{ ok: boolean; online: boolean }>('POST', '/api/settings/telegram/test'),
  },

  // ── Колокольчик: история и настройки уведомлений ───────────────────
  notifications: {
    list: (params: { limit?: number; before?: string } = {}) => {
      const q = new URLSearchParams();
      if (params.limit)  q.set('limit', String(params.limit));
      if (params.before) q.set('before', params.before);
      const qs = q.toString();
      return request<{ items: TNotificationItem[]; unread: number }>('GET', `/api/notifications${qs ? `?${qs}` : ''}`);
    },

    markRead: (ids: string[]) =>
      request<{ ok: boolean }>('POST', '/api/notifications/read', { ids }),

    markAllRead: () =>
      request<{ ok: boolean }>('POST', '/api/notifications/read', { all: true }),

    clear: () =>
      request<{ ok: boolean }>('DELETE', '/api/notifications'),

    prefs: () =>
      request<{ items: TNotificationPref[]; groups: Record<TNotificationGroup, string> }>('GET', '/api/notifications/prefs'),

    savePrefs: (items: { type: string; site: boolean; tg: boolean }[]) =>
      request<{ ok: boolean }>('PUT', '/api/notifications/prefs', { items }),

    /** Тихие часы: минуты от полуночи по Москве, null — выключены */
    settings: () =>
      request<TQuietSettings>('GET', '/api/notifications/settings'),

    saveSettings: (s: TQuietSettings) =>
      request<TQuietSettings>('PUT', '/api/notifications/settings', s),

    digest: () =>
      request<TDigestSettings>('GET', '/api/notifications/digest'),

    saveDigest: (s: TDigestSchedule) =>
      request<TDigestSettings>('PUT', '/api/notifications/digest', s),
  },

  // ── Profile ───────────────────────────────────────────────────────
  profile: {
    get: () =>
      request<TProfile>('GET', '/api/profile'),

    update: (data: { name?: string; steam_id?: string }) =>
      request<{ ok: boolean }>('PUT', '/api/profile', data),

    changePassword: (currentPassword: string, newPassword: string) =>
      request<{ ok: boolean }>('POST', '/api/profile/password', { currentPassword, newPassword }),
  },

  // ── Admins ────────────────────────────────────────────────────────
  admins: {
    list: () =>
      request<TAdminUser[]>('GET', '/api/admins'),

    create: (data: { login: string; email: string; password: string; role_id?: number }) =>
      request<{ ok: boolean; id?: number }>('POST', '/api/admins', data),

    update: (id: number, data: Partial<{ email: string; is_active: boolean; role_id: number }>) =>
      request<{ ok: boolean }>('PATCH', `/api/admins/${id}`, data),

    remove: (id: number) =>
      request<{ ok: boolean }>('DELETE', `/api/admins/${id}`),
  },

  // ── Roles ─────────────────────────────────────────────────────────
  roles: {
    list: () =>
      request<TRole[]>('GET', '/api/roles'),

    permissions: () =>
      request<TPermission[]>('GET', '/api/roles/permissions'),

    create: (data: { name: string; description?: string; permissions?: string[] }) =>
      request<{ ok: boolean }>('POST', '/api/roles', data),

    update: (id: number, data: { name?: string; description?: string; permissions?: string[] }) =>
      request<{ ok: boolean }>('PATCH', `/api/roles/${id}`, data),

    remove: (id: number) =>
      request<{ ok: boolean }>('DELETE', `/api/roles/${id}`),
  },

  // ── OIDC clients ──────────────────────────────────────────────────
  oidcClients: {
    list: () =>
      request<TOidcClient[]>('GET', '/api/oidc-clients'),

    create: (data: {
      client_id:                   string;
      client_name:                 string;
      token_endpoint_auth_method?: string;
      grant_types?:                string[];
      redirect_uris:               string[];
      post_logout_redirect_uris?:  string[];
      first_party?:                boolean;
      enabled?:                    boolean;
    }) =>
      request<TOidcClientMutation>('POST', '/api/oidc-clients', data),

    update: (id: number, data: Partial<{
      client_name:                string;
      token_endpoint_auth_method: string;
      grant_types:                string[];
      redirect_uris:              string[];
      post_logout_redirect_uris:  string[];
      first_party:                boolean;
      enabled:                    boolean;
      regenerate_secret:          boolean;
    }>) =>
      request<TOidcClientMutation>('PATCH', `/api/oidc-clients/${id}`, data),

    remove: (id: number) =>
      request<{ ok: boolean }>('DELETE', `/api/oidc-clients/${id}`),
  },

  // ── Players ───────────────────────────────────────────────────────
  players: {
    online: () =>
      request<TPlayerPosition[]>('GET', '/api/players/online'),

    history: (params: { from: string; to: string; uid?: string; limit?: number }) => {
      const qs = new URLSearchParams(
        Object.fromEntries(
          Object.entries(params)
            .filter(([, v]) => v !== undefined)
            .map(([k, v]) => [k, String(v)])
        ) as Record<string, string>
      ).toString();
      return request<THistoryPosition[]>('GET', `/api/players/history?${qs}`);
    },

    // Границы доступных данных истории (для сегментной загрузки)
    historyMeta: (params: { from: string; to: string }) => {
      const qs = new URLSearchParams(params).toString();
      return request<{ first_at: string | null; last_at: string | null }>('GET', `/api/players/history/meta?${qs}`);
    },

    profile: (uid: string) =>
      request<TGamePlayerInfo>('GET', `/api/players/${uid}/profile`),

    // Последнее ранение игрока (кем / оружие / дистанция).
    // at (ISO) — «на момент времени»: для карточки в режиме просмотра записи
    lastWound: (uid: string, at?: string) =>
      request<{ wound: TLastWound | null }>(
        'GET',
        `/api/players/${encodeURIComponent(uid)}/last-wound${at ? `?at=${encodeURIComponent(at)}` : ''}`,
      ),

    // Название банды (числовой id, altislife.gangs) или организации
    // (строковый код, altislife.fractions) по gang_id группы
    gangName: (id: string | number) =>
      request<{ id: string; name: string | null; kind: 'gang' | 'org' }>('GET', `/api/players/gang/${encodeURIComponent(String(id))}`),

    // Имя владельца по uid (для арендованного транспорта без номерного знака)
    ownerName: (uid: string) =>
      request<{ uid: string; name: string }>('GET', `/api/players/owner/${encodeURIComponent(uid)}`),

    heatmap: (params: { type: string; from: string; to: string }) => {
      const qs = new URLSearchParams(params).toString();
      return request<Array<[number, number, number]>>('GET', `/api/players/heatmap?${qs}`);
    },

    // История для виджета «События»: последние агонии/смерти и сообщения
    // в формате socket-событий combat_update / messages_update
    mapEventsRecent: (limit = 50) =>
      request<{ combat: TCombatEvent[]; messages: TGameMessage[] }>(
        'GET', `/api/players/map-events/recent?limit=${limit}`,
      ),

    // События за период (панель «События» в режиме просмотра записи);
    // date_time — абсолютный UTC ISO для синхронизации с таймлайном
    mapEventsRange: (params: { from: string; to: string; limit?: number }) => {
      const qs = new URLSearchParams({
        from: params.from, to: params.to,
        ...(params.limit ? { limit: String(params.limit) } : {}),
      }).toString();
      return request<{ combat: TCombatEvent[]; messages: TGameMessage[] }>(
        'GET', `/api/players/map-events/recent?${qs}`,
      );
    },
  },

  // ── Game (живой словарь из игры) ───────────────────────────────────
  game: {
    dictionary: () => request<TGameDict>('GET', '/api/game/dictionary'),
    dictionarySearch: (q: string, kinds?: readonly TGameDictKind[]) => {
      const params = new URLSearchParams({ q });
      if (kinds?.length) params.set('kinds', kinds.join(','));
      return request<{ items: TGameDictHit[] }>('GET', `/api/game/dictionary/search?${params}`);
    },
    dictionaryCatalog: (kind: TGameDictKind) =>
      request<{ items: TGameDictCatalogRow[] }>('GET', `/api/game/dictionary/catalog?kind=${kind}`),
  },

  // ── Vehicles ──────────────────────────────────────────────────────
  vehicles: {
    info: (plate: string) =>
      request<TGameVehicleInfo>('GET', `/api/players/vehicles/${encodeURIComponent(plate)}`),

    history: (params: { from: string; to: string; plate?: string; limit?: number }) => {
      const qs = new URLSearchParams(
        Object.fromEntries(
          Object.entries(params)
            .filter(([, v]) => v !== undefined)
            .map(([k, v]) => [k, String(v)])
        ) as Record<string, string>
      ).toString();
      return request<THistoryVehicle[]>('GET', `/api/players/vehicles/history?${qs}`);
    },

    // Уничтоженный транспорт за период (фильтр на карте)
    destroyed: (params: { from: string; to: string; limit?: number }) => {
      const qs = new URLSearchParams({
        from: params.from, to: params.to,
        ...(params.limit ? { limit: String(params.limit) } : {}),
      }).toString();
      return request<TDestroyedVehicle[]>('GET', `/api/players/vehicles/destroyed?${qs}`);
    },

    // История операторов БПЛА по ключу техники
    uavHistory: (params: { key: string; from?: string; to?: string; limit?: number }) => {
      const qs = new URLSearchParams({
        key: params.key,
        ...(params.from  ? { from:  params.from } : {}),
        ...(params.to    ? { to:    params.to } : {}),
        ...(params.limit ? { limit: String(params.limit) } : {}),
      }).toString();
      return request<TUavSession[]>('GET', `/api/players/vehicles/uav-history?${qs}`);
    },
  },

  // ── Admin Vehicles (CRUD altislife.vehicles) ──────────────────────
  adminVehicles: {
    list: (params?: {
      search?: string; plate?: string; type?: string;
      alive?: 'alive' | 'dead' | 'all';
      page?: number; per_page?: number;
    }) => {
      const q = new URLSearchParams();
      if (params?.search)   q.set('search',   params.search);
      if (params?.plate)    q.set('plate',    params.plate);
      if (params?.type)     q.set('type',     params.type);
      if (params?.alive)    q.set('alive',    params.alive);
      if (params?.page)     q.set('page',     String(params.page));
      if (params?.per_page) q.set('per_page', String(params.per_page));
      const qs = q.toString();
      return request<{ data: TAdminVehicle[]; total: number; page: number; per_page: number }>(
        'GET', `/api/admin/vehicles${qs ? `?${qs}` : ''}`
      );
    },

    get: (id: number) =>
      request<TAdminVehicle>('GET', `/api/admin/vehicles/${id}`),

    update: (id: number, data: Partial<{
      pid: string; classname: string; plate: string; side: string; type: string;
      name_fo: string; wname: string; garage: string;
      alive: number; blacklist: number; active: number; lives: number;
      color: number; price: number; fuel: number; odometr: number; perm: string;
      /** Сохранить classname, которого нет в словаре сервера */
      force: boolean;
    }>) =>
      request<TAdminVehicle>('PATCH', `/api/admin/vehicles/${id}`, data),

    remove: (id: number) =>
      request<{ ok: boolean }>('DELETE', `/api/admin/vehicles/${id}`),

    // Поиск игроков (имя/UID) для карточки гаража
    searchPlayers: (q: string) =>
      request<TGaragePlayerHit[]>('GET', `/api/admin/vehicles/players?q=${encodeURIComponent(q)}`),

    // Гараж игрока: карточка + статистика + весь транспорт
    garage: (pid: string) =>
      request<TPlayerGarage>('GET', `/api/admin/vehicles/player/${encodeURIComponent(pid)}/garage`),

    // Организации (fractions) для селекта «сторона» — кешируются на бэкенде
    fractions: () =>
      request<TVehicleFraction[]>('GET', '/api/admin/vehicles/fractions'),

    // Массовая передача владельца (pid + name_fo → SteamID нового владельца)
    transfer: (ids: number[], newPid: string) =>
      request<{ ok: boolean; transferred: number; new_owner: { playerid: string; name: string | null } }>(
        'POST', '/api/admin/vehicles/transfer', { ids, new_pid: newPid }
      ),

    // История передач транспорта
    transfers: (params?: { pid?: string; vehicle_id?: number; page?: number; per_page?: number }) => {
      const q = new URLSearchParams();
      if (params?.pid)        q.set('pid',        params.pid);
      if (params?.vehicle_id) q.set('vehicle_id', String(params.vehicle_id));
      if (params?.page)       q.set('page',       String(params.page));
      if (params?.per_page)   q.set('per_page',   String(params.per_page));
      const qs = q.toString();
      return request<{ data: TVehicleTransfer[]; total: number; page: number; per_page: number }>(
        'GET', `/api/admin/vehicles/transfers${qs ? `?${qs}` : ''}`
      );
    },
  },

  // ── Shifts ────────────────────────────────────────────────────────
  shifts: {
    active: () =>
      request<TShift[]>('GET', '/api/shifts/active'),

    start: () =>
      request<{ ok: boolean }>('POST', '/api/shifts/start'),

    end: (note?: string) =>
      request<{ ok: boolean }>('POST', '/api/shifts/end', { note }),

    forceEnd: (userId: number) =>
      request<{ ok: boolean }>('POST', `/api/shifts/end/${userId}`),

    history: (params?: { page?: number; limit?: number; user_id?: number; search?: string }) => {
      const qs = params
        ? new URLSearchParams(
            Object.fromEntries(
              Object.entries(params)
                .filter(([, v]) => v !== undefined)
                .map(([k, v]) => [k, String(v)])
            ) as Record<string, string>
          ).toString()
        : '';
      return request<{ data: TShift[]; total: number }>('GET', `/api/shifts/history${qs ? `?${qs}` : ''}`);
    },
  },

  // ── Tasks ─────────────────────────────────────────────────────────
  tasks: {
    // scope=all — все задачи (нужно tasks.view.all); по умолчанию «свои + открытые»
    list: (params?: { status?: TTaskStatus; assignee?: number; type?: number; scope?: 'all' | 'my' }) => {
      const qs = params
        ? new URLSearchParams(
            Object.fromEntries(
              Object.entries(params)
                .filter(([, v]) => v !== undefined)
                .map(([k, v]) => [k, String(v)])
            ) as Record<string, string>
          ).toString()
        : '';
      return request<TTask[]>('GET', `/api/tasks${qs ? `?${qs}` : ''}`);
    },

    get: (id: number) =>
      request<TTaskDetail>('GET', `/api/tasks/${id}`),

    create: (data: { title: string; description?: string; priority?: TTaskPriority; type_id?: number | null; deadline?: string; assignees?: number[]; attachments?: string[] }) =>
      request<TTask>('POST', '/api/tasks', data),

    update: (id: number, data: Partial<{ status: TTaskStatus; priority: TTaskPriority; type_id: number | null; deadline: string | null; title: string; description: string | null; attachments: string[] | null }>) =>
      request<{ ok: boolean }>('PATCH', `/api/tasks/${id}`, data),

    take: (id: number) =>
      request<{ ok: boolean }>('POST', `/api/tasks/${id}/take`),

    /** Вердикт тестирования (только для задач «На тестировании») */
    setTestVerdict: (id: number, verdict: 'passed' | 'failed') =>
      request<{ ok: boolean }>('POST', `/api/tasks/${id}/test-verdict`, { verdict }),

    /** Ручной порядок задач на доске: id в новом порядке (внутри колонки/группы) */
    reorder: (orderedIds: number[]) =>
      request<{ ok: boolean }>('POST', '/api/tasks/reorder', { orderedIds }),

    /** Порядок групп (типов задач) на доске: id типов в новом порядке */
    reorderTypes: (orderedIds: number[]) =>
      request<{ ok: boolean }>('POST', '/api/tasks/types/reorder', { orderedIds }),

    addAssignee: (id: number, user_id: number) =>
      request<{ ok: boolean }>('POST', `/api/tasks/${id}/assignees`, { user_id }),

    removeAssignee: (id: number, user_id: number) =>
      request<{ ok: boolean }>('DELETE', `/api/tasks/${id}/assignees/${user_id}`),

    comment: (id: number, body: string, attachments?: string[]) =>
      request<{ ok: boolean }>('POST', `/api/tasks/${id}/comments`, { body, attachments }),

    editComment: (id: number, cid: number, body: string) =>
      request<{ ok: boolean }>('PATCH', `/api/tasks/${id}/comments/${cid}`, { body }),

    deleteComment: (id: number, cid: number) =>
      request<{ ok: boolean }>('DELETE', `/api/tasks/${id}/comments/${cid}`),

    // Загрузка изображения-вложения (для описания задачи или комментария)
    upload: (file: File): Promise<{ url: string }> => {
      const fd = new FormData();
      fd.append('file', file);
      const token = getToken();
      return fetch(`${BASE}/api/tasks/upload`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      }).then(async r => {
        const data = await r.json();
        if (!r.ok) throw new Error(data?.error || 'Ошибка загрузки');
        return data as { url: string };
      });
    },

    // Типы задач: видимые пользователю (с флагом canCreate)
    types: () =>
      request<TTaskType[]>('GET', '/api/tasks/types'),

    // Типы задач: полный справочник (settings.manage)
    typesAdmin: () =>
      request<TTaskTypeAdmin[]>('GET', '/api/tasks/types/admin'),

    createType: (data: { name: string; color?: string; sort?: number; view_roles?: string[]; create_roles?: string[] }) =>
      request<{ ok: boolean; id: number }>('POST', '/api/tasks/types', data),

    updateType: (id: number, data: Partial<{ name: string; color: string; sort: number; active: number; view_roles: string[]; create_roles: string[] }>) =>
      request<{ ok: boolean }>('PATCH', `/api/tasks/types/${id}`, data),

    deleteType: (id: number) =>
      request<{ ok: boolean; deactivated?: boolean; deleted?: boolean; used?: number }>('DELETE', `/api/tasks/types/${id}`),
  },

  // ── Notes ─────────────────────────────────────────────────────────
  notes: {
    list: (board?: TNoteBoard) =>
      request<TNote[]>('GET', `/api/notes${board ? `?board=${board}` : ''}`),

    create: (data: { title?: string; body: string; board?: TNoteBoard }) =>
      request<TNote>('POST', '/api/notes', data),

    update: (id: number, data: Partial<{ title: string; body: string; is_pinned: number }>) =>
      request<{ ok: boolean }>('PATCH', `/api/notes/${id}`, data),

    delete: (id: number) =>
      request<{ ok: boolean }>('DELETE', `/api/notes/${id}`),
  },

  // ── Users (all auth users) ────────────────────────────────────────
  users: {
    list: (params?: TUserListParams) => {
      const qs = params
        ? new URLSearchParams(
            Object.fromEntries(
              Object.entries(params)
                .filter(([, v]) => v !== undefined && v !== '')
                .map(([k, v]) => [k, String(v)])
            ) as Record<string, string>
          ).toString()
        : '';
      return request<{ data: TUserListRow[]; total: number; page: number; limit: number; sort: TUserSortKey; dir: 'ASC' | 'DESC'; unlinked?: TUnlinkedHit[] }>(
        'GET', `/api/users${qs ? `?${qs}` : ''}`
      );
    },

    get: (id: number) =>
      request<TUser>('GET', `/api/users/${id}`),

    summary: (id: number) =>
      request<TUserSummaryDetail>('GET', `/api/users/${id}/summary`),

    playerBrief: (steamId: string) =>
      request<TPlayerBrief>('GET', `/api/users/player-brief/${encodeURIComponent(steamId)}`),

    playerCharacter: (steamId: string) =>
      request<TPlayerCharacter>('GET', `/api/users/player/${encodeURIComponent(steamId)}/character`),
    playerDetails: <K extends keyof TPlayerDetails>(steamId: string, part: K) =>
      request<TPlayerDetails[K]>('GET', `/api/users/player/${encodeURIComponent(steamId)}/details/${part}`),

    create: (data: { login: string; email: string; password: string; role_id?: number }) =>
      request<{ ok: boolean; id: number }>('POST', '/api/users', data),

    update: (id: number, data: Partial<{
      login: string; email: string; display_name: string | null;
      is_active: boolean; status: TUserStatus; role_id: number | null;
    }>) =>
      request<{ ok: boolean }>('PATCH', `/api/users/${id}`, data),

    /** IP сайта, лаунчера и форума одной таблицей, с теми, кто ещё на них бывал */
    ipOverview: (id: number) =>
      request<TUserIpOverview>('GET', `/api/users/${id}/ip-overview`),
    /** Игровые ники: текущий, смены через ЛК и в обход ЛК */
    nameHistory: (id: number) =>
      request<TGameNameHistory>('GET', `/api/users/${id}/name-history`),
    /** Другие игроки, носившие ники из истории пользователя */
    nameMatches: (id: number) =>
      request<TGameNameMatches>('GET', `/api/users/${id}/name-matches`),
    getIps: (id: number, params?: { page?: number; limit?: number }) => {
      const qs = params
        ? new URLSearchParams(
            Object.fromEntries(
              Object.entries(params)
                .filter(([, v]) => v !== undefined)
                .map(([k, v]) => [k, String(v)])
            ) as Record<string, string>
          ).toString()
        : '';
      return request<{ data: TUserIpLog[]; total: number; page: number; limit: number; most_probable: string | null; top_ips: { ip_address: string; count: number }[] }>(
        'GET', `/api/users/${id}/ips${qs ? `?${qs}` : ''}`
      );
    },

    ipInfo: (ip: string) =>
      request<TIpInfo>('GET', `/api/users/ip-check?ip=${encodeURIComponent(ip)}`),

    /** Серверный вердикт VPN без раскрытия IP (право users.vpn_status) */
    vpnStatus: (id: number) =>
      request<{ verdict: 'clean' | 'likely_vpn' | 'vpn' | null; ip_known?: boolean }>(
        'GET', `/api/users/${id}/vpn-status`
      ),

    steamLink: (id: number, steam_id: string) =>
      request<{ ok: boolean; steam_id: string; steam_name: string | null; steam_avatar: string | null }>(
        'POST', `/api/users/${id}/steam-link`, { steam_id }
      ),

    steamUnlink: (id: number) =>
      request<{ ok: boolean }>('DELETE', `/api/users/${id}/steam-link`),

    /** Форум (XenForo): связка, снимок профиля; ips — только при admin.manage */
    forum: (id: number) =>
      request<TUserForum>('GET', `/api/users/${id}/forum`),
    forumLink: (id: number, forum_user_id: number) =>
      request<TForumLink>('POST', `/api/users/${id}/forum`, { forum_user_id }),
    forumUnlink: (id: number) =>
      request<{ ok: boolean }>('DELETE', `/api/users/${id}/forum`),
    forumCommand: (id: number, body: TForumCommandInput) =>
      request<TForumCommand>('POST', `/api/users/${id}/forum/commands`, body),
    forumSearch: (q: string) =>
      request<TForumSearchItem[]>('GET', `/api/users/forum-search?q=${encodeURIComponent(q)}`),

    /** Telegram-бот в карточке: привязка и доступ (users.view) */
    telegram: (id: number) =>
      request<TUserTelegram>('GET', `/api/users/${id}/telegram`),

    /** Отвязать Telegram (admin.manage) */
    telegramUnlink: (id: number) =>
      request<{ ok: boolean }>('DELETE', `/api/users/${id}/telegram`),

    /** Админский сброс 2FA (право users.2fa.reset; для цели с ролью — users.2fa.reset.admin) */
    resetTotp: (id: number) =>
      request<{ ok: boolean }>('DELETE', `/api/users/${id}/totp`),

    deleteAvatar: (id: number) =>
      request<{ ok: boolean }>('DELETE', `/api/users/${id}/avatar`),

    /** HTML-отчёт по ч. 7 ст. 14 152-ФЗ (право users.export_data) */
    dataExport: (id: number, detailed = false) =>
      requestBlob(`/api/users/${id}/data-export${detailed ? '?detailed=1' : ''}`),

    /** Удаление учётной записи по п. 12.17 Политики (право users.delete_account) */
    deleteAccount: (id: number, data: { confirmLogin: string; reason: string }) =>
      request<{ ok: boolean; steam_id: string | null; files_removed: number }>(
        'POST', `/api/users/${id}/delete-account`, data,
      ),

    promos: (id: number) =>
      request<TPromoHistory>('GET', `/api/users/${id}/promos`),

    getConsents: (id: number) =>
      request<TUserConsent[]>('GET', `/api/users/${id}/consents`),

    getRestrictions: (id: number) =>
      request<TUserRestriction[]>('GET', `/api/users/${id}/restrictions`),

    getRestrictionsHistory: (id: number, page = 1) =>
      request<TUserRestrictionsHistoryPage>('GET', `/api/users/${id}/restrictions/history?page=${page}`),

    addRestriction: (id: number, data: { type: TRestrictionType; reason?: string; reason_public?: string; expires_at?: string | null }) =>
      request<{ ok: boolean }>('POST', `/api/users/${id}/restrictions`, data),

    removeRestriction: (id: number, type: TRestrictionType) =>
      request<{ ok: boolean }>('DELETE', `/api/users/${id}/restrictions/${type}`),

    ipMatches: (ip: string, excludeId?: number) => {
      const qs = new URLSearchParams({ ip, ...(excludeId ? { exclude_id: String(excludeId) } : {}) }).toString();
      return request<TIpMatchUser[]>('GET', `/api/users/ip-matches?${qs}`);
    },
  },

  // ── Tickets (admin) ───────────────────────────────────────────────
  tickets: {
    /** status 'active' — открытые и в работе разом (очередь); limit — до 100, по умолчанию 25 */
    list: (params?: { status?: TTicketStatus | 'active'; category?: string; search?: string; page?: number; limit?: number; user_id?: number }) => {
      const qs = params
        ? new URLSearchParams(
            Object.fromEntries(
              Object.entries(params)
                .filter(([, v]) => v !== undefined)
                .map(([k, v]) => [k, String(v)])
            ) as Record<string, string>
          ).toString()
        : '';
      return request<{ data: TTicket[]; total: number }>('GET', `/api/tickets${qs ? `?${qs}` : ''}`);
    },

    /** Счётчики для меню: открытые, просроченные по SLA и сам порог SLA в минутах */
    queueSummary: () =>
      request<{ open: number; overdue: number; sla_minutes: number }>('GET', '/api/tickets/queue-summary'),

    get: (id: number) =>
      request<TTicketDetail>('GET', `/api/tickets/${id}`),

    /** reason — причина смены статуса, попадает в событие (массовое «Закрыть с причиной») */
    update: (id: number, data: Partial<{ status: TTicketStatus; priority: TTicketPriority; reason: string }>) =>
      request<{ ok: boolean }>('PATCH', `/api/tickets/${id}`, data),

    reply: (id: number, body: string, attachments?: string[], offerClose?: boolean) =>
      request<{ ok: boolean }>('POST', `/api/tickets/${id}/messages`, { body, attachments, offer_close: offerClose }),

    internalNote: (id: number, body: string) =>
      request<{ ok: boolean }>('POST', `/api/tickets/${id}/internal-notes`, { body }),

    deleteNote: (id: number, noteId: number) =>
      request<{ ok: boolean }>('DELETE', `/api/tickets/${id}/internal-notes/${noteId}`),

    assign: (id: number, admin_id?: number) =>
      request<{ ok: boolean }>('POST', `/api/tickets/${id}/assign`, admin_id ? { admin_id } : undefined),

    unassign: (id: number, admin_id: number) =>
      request<{ ok: boolean }>('DELETE', `/api/tickets/${id}/assignees/${admin_id}`),

    steamLink: (id: number, steam_id: string) =>
      request<{ ok: boolean }>('POST', `/api/tickets/${id}/steam-manual-link`, { steam_id }),

    messageRatings: (id: number) =>
      request<{ data: { message_id: number; up: number; down: number }[] }>('GET', `/api/tickets/${id}/message-ratings`),

    /** Технические данные, собранные лаунчером: полные отчёты и запросы */
    diagnostics: (id: number) =>
      request<TDiagnosticsAdmin>('GET', `/api/tickets/${id}/diagnostics`),

    /** Запросить данные у игрока: в переписке появится карточка с пунктами */
    requestDiagnostics: (id: number, kinds: TDiagKind[], note?: string) =>
      request<{ ok: boolean; id: number }>('POST', `/api/tickets/${id}/diagnostics/request`, { kinds, note }),

    /** Прямая ссылка на журнал игры — скачивается с токеном в заголовке */
    diagnosticBlobUrl: (id: number, recordId: number) =>
      `${BASE}/api/tickets/${id}/diagnostics/${recordId}/raw`,

    createForUser: (user_id: number, subject: string, body: string, category?: string) =>
      request<{ ok: boolean; ticket_id: number }>('POST', '/api/tickets/admin-create', { user_id, subject, body, category }),

    changeTopic: (id: number, topic: string | null) =>
      request<{ ok: boolean }>('PATCH', `/api/tickets/${id}/topic`, { topic }),

    /** Активные темы, видимые текущему администратору по ролям */
    topics: () =>
      request<{ data: TTicketTopic[] }>('GET', '/api/tickets/topics'),

    templates: {
      list: (q?: string, all?: boolean) => {
        const params = new URLSearchParams();
        if (q) params.set('q', q);
        if (all) params.set('all', '1');
        const qs = params.toString();
        return request<{ data: TTicketTemplate[]; search: boolean }>(
          'GET', `/api/tickets/templates${qs ? `?${qs}` : ''}`
        );
      },

      create: (data: { parent_id?: number | null; is_folder?: boolean; name: string; body?: string; sort?: number }) =>
        request<{ ok: boolean; id: number }>('POST', '/api/tickets/templates', data),

      update: (id: number, data: Partial<{ parent_id: number | null; name: string; body: string; sort: number; active: number | boolean }>) =>
        request<{ ok: boolean }>('PATCH', `/api/tickets/templates/${id}`, data),

      delete: (id: number) =>
        request<{ ok: boolean }>('DELETE', `/api/tickets/templates/${id}`),
    },
  },

  // ── Staff chat ────────────────────────────────────────────────────
  chat: {
    conversations: () =>
      request<{ data: TChatConversation[] }>('GET', '/api/chat/conversations'),

    createChannel: (name: string) =>
      request<{ ok: boolean; id: number }>('POST', '/api/chat/conversations', { type: 'channel', name }),

    createGroup: (name: string, member_ids: number[]) =>
      request<{ ok: boolean; id: number }>('POST', '/api/chat/conversations', { type: 'group', name, member_ids }),

    openDm: (user_id: number) =>
      request<{ ok: boolean; id: number; existing?: boolean }>('POST', '/api/chat/conversations', { type: 'dm', user_id }),

    deleteConversation: (id: number) =>
      request<{ ok: boolean }>('DELETE', `/api/chat/conversations/${id}`),

    messages: (id: number, params?: { before?: number; limit?: number }) => {
      const q = new URLSearchParams();
      if (params?.before) q.set('before', String(params.before));
      if (params?.limit)  q.set('limit',  String(params.limit));
      const qs = q.toString();
      return request<{ data: TChatMessage[]; hasMore: boolean }>(
        'GET', `/api/chat/conversations/${id}/messages${qs ? `?${qs}` : ''}`
      );
    },

    send: (id: number, content: string, reply_to_id?: number) =>
      request<TChatMessage>('POST', `/api/chat/conversations/${id}/messages`, { content, reply_to_id }),

    edit: (messageId: number, content: string) =>
      request<TChatMessage>('PATCH', `/api/chat/messages/${messageId}`, { content }),

    remove: (messageId: number) =>
      request<{ ok: boolean }>('DELETE', `/api/chat/messages/${messageId}`),

    forward: (messageId: number, conversation_id: number) =>
      request<TChatMessage>('POST', `/api/chat/messages/${messageId}/forward`, { conversation_id }),

    addMembers: (id: number, user_ids: number[]) =>
      request<{ ok: boolean }>('POST', `/api/chat/conversations/${id}/participants`, { user_ids }),

    removeMember: (id: number, userId: number) =>
      request<{ ok: boolean }>('DELETE', `/api/chat/conversations/${id}/participants/${userId}`),

    markRead: (id: number, last_message_id: number) =>
      request<{ ok: boolean }>('POST', `/api/chat/conversations/${id}/read`, { last_message_id }),

    setMute: (id: number, muted: boolean) =>
      request<{ ok: boolean; muted: boolean }>('PATCH', `/api/chat/conversations/${id}/mute`, { muted }),

    pinned: (id: number) =>
      request<{ data: TChatMessage[] }>('GET', `/api/chat/conversations/${id}/pinned`),

    pin: (messageId: number) =>
      request<TChatMessage>('POST', `/api/chat/messages/${messageId}/pin`),

    unpin: (messageId: number) =>
      request<{ ok: boolean }>('DELETE', `/api/chat/messages/${messageId}/pin`),

    search: (q: string, conversation_id?: number) => {
      const params = new URLSearchParams({ q });
      if (conversation_id) params.set('conversation_id', String(conversation_id));
      return request<{ data: TChatMessage[] }>('GET', `/api/chat/search?${params}`);
    },

    staff: () =>
      request<{ data: TChatStaffMember[] }>('GET', '/api/chat/staff'),
  },

  // ── Warzones ──────────────────────────────────────────────────────
  warzones: {
    active: () =>
      request<TWarzoneActive[]>('GET', '/api/warzones/active'),

    events: (limit = 20) =>
      request<TWarzoneEvent[]>('GET', `/api/warzones/events?limit=${limit}`),

    // События ВП за период для режима «История»; date_time — UTC ISO
    history: (params: { from: string; to: string }) =>
      request<TWarzoneEvent[]>('GET', `/api/warzones/history?from=${encodeURIComponent(params.from)}&to=${encodeURIComponent(params.to)}`),

    // Слой территорий: геометрия + войны с последнего рестарта
    territories: () =>
      request<TMapTerritoriesLive>('GET', '/api/warzones/territories'),

    // Слой территорий в «Истории»: интервалы войн и правки контуров после from
    territoriesHistory: (params: { from: string; to: string }) =>
      request<TMapTerritoriesHistory>('GET', `/api/warzones/territories/history?from=${encodeURIComponent(params.from)}&to=${encodeURIComponent(params.to)}`),
  },

  // ── Servers ────────────────────────────────────────────────────────
  servers: {
    // fresh=true — обойти серверный кэш (для кнопки «Проверить»)
    status: (fresh = false) =>
      request<{
        ts3:   { online: boolean; name: string | null; players: number; maxPlayers: number; ping: number | null; map?: string | null; error?: string | null; configured: boolean };
        arma3: { online: boolean; name: string | null; players: number; maxPlayers: number; ping: number | null; map?: string | null; error?: string | null; configured: boolean };
      }>('GET', `/api/servers/status${fresh ? '?fresh=1' : ''}`),

    // Живая проверка по данным из формы (без сохранения в настройки)
    test: (data:
      | { type: 'arma3'; host: string; port: number; queryPort?: string; rconPort?: number; rconPassword?: string }
      | { type: 'teamspeak3'; host: string; port: number; queryPort?: number; webQueryPort?: number; webQueryKey?: string; vsid?: number; webQuerySsl?: boolean }
    ) =>
      request<{ online: boolean; name: string | null; players: number; maxPlayers: number; ping: number | null; map?: string | null; error?: string | null; configured: boolean }>(
        'POST', '/api/servers/test', data,
      ),
  },

  // ── Incidents (здоровье компонентов системы) ─────────────────────
  incidents: {
    health: () =>
      request<{ components: TIncidentComponent[]; checked_at: string }>('GET', '/api/incidents/health'),

    list: (params: {
      status?:    'active' | 'resolved' | 'all';
      type?:      TIncidentTypeFilter;
      component?: string;
      from?:      string;
      to?:        string;
      cursor?:    number | null;
      limit?:     number;
    }) => {
      const q = new URLSearchParams();
      if (params.status)    q.set('status',    params.status);
      if (params.type)      q.set('type',      params.type);
      if (params.component) q.set('component', params.component);
      if (params.from)      q.set('from',      params.from);
      if (params.to)        q.set('to',        params.to);
      if (params.cursor)    q.set('cursor',    String(params.cursor));
      if (params.limit)     q.set('limit',     String(params.limit));
      return request<{ items: TIncident[]; next_cursor: number | null }>('GET', `/api/incidents?${q}`);
    },

    timeline: (period: TIncidentPeriod) =>
      request<TIncidentTimeline>('GET', `/api/incidents/timeline?period=${period}`),

    metrics: (component: string, period: TIncidentPeriod) =>
      request<{ component: string; period: TIncidentPeriod; from: string; to: string; step_min: number; points: TIncidentMetricPoint[] }>(
        'GET', `/api/incidents/metrics?component=${encodeURIComponent(component)}&period=${period}`,
      ),

    ack: (id: number) =>
      request<{ incident: TIncident | null }>('POST', `/api/incidents/${id}/ack`),

    note: (id: number, note: string) =>
      request<{ incident: TIncident | null }>('PATCH', `/api/incidents/${id}/note`, { note }),

    classify: (id: number, maintenance: boolean) =>
      request<{ incident: TIncident | null }>('POST', `/api/incidents/${id}/classify`, { maintenance }),

    getConfig: () =>
      request<TIncidentConfig>('GET', '/api/incidents/config'),

    saveConfig: (data: { components: Record<string, TIncidentComponentOverride>; retention_days?: number; metrics_retention_days?: number }) =>
      request<{ ok: boolean }>('PUT', '/api/incidents/config', data),
  },

  // ── Settings ──────────────────────────────────────────────────────
  settings: {
    get: () =>
      request<{ settings: Record<string, string | null> }>('GET', '/api/settings'),

    update: (data: Partial<{
      maintenance_enabled:           boolean;
      maintenance_text:              string;
      maintenance_password:          string;
      ticket_auto_close_days:        number;
      ticket_auto_close_message:     string;
      /** SLA очереди: минут без ответа поддержки до пометки «Просрочено» */
      ticket_sla_minutes:            number;
      /** SLA игровых тикетов: минут, пока тикет из игры никто не взял */
      game_ticket_sla_minutes:       number;
      admin_support_avatar_url:      string;
      allowed_email_domains:         string;
      ticket_max_attachment_size_mb: number;
      email_brand_name:              string;
      email_accent_color:            string;
      email_logo_url:                string;
      email_footer_text:             string;
      arma3_server_host:             string;
      arma3_server_port:             number;
      arma3_query_port:              string;
      arma3_rcon_port:               number;
      arma3_rcon_password:           string;
      ts3_server_host:               string;
      ts3_server_port:               number;
      ts3_query_port:                number;
      ts3_webquery_port:             number;
      ts3_webquery_key:              string;
      ts3_vsid:                      number;
      ts3_webquery_ssl:              boolean;
      /** on | hidden | off */
      section_news_site:             string;
      section_news_launcher:         boolean;
    }>) =>
      request<{ ok: boolean }>('PUT', '/api/settings', data),

    /** Реестр внешних ссылок: каталог с вариантами и текущим значением */
    links: () =>
      request<{ links: TSiteLink[] }>('GET', '/api/links/admin'),

    saveLink: (key: string, data: { active: string; variants: TLinkVariant[] }) =>
      request<{ ok: boolean }>('PUT', `/api/links/admin/${encodeURIComponent(key)}`, data),

    uploadAvatar: (file: File, onProgress?: TUploadProgress): Promise<{ ok: boolean; url: string }> => {
      assertImageSize(file);
      const fd = new FormData();
      fd.append('avatar', file);
      return xhrUpload(`${BASE}/api/settings/admin-avatar/upload`, fd, onProgress);
    },

    deleteAvatar: () =>
      request<{ ok: boolean }>('DELETE', '/api/settings/admin-avatar'),

    // Сброс кеша организаций (fractions): селект «сторона» в разделе
    // транспорта + кеш обогащения игроков
    resetFractionsCache: () =>
      request<{ ok: boolean }>('POST', '/api/settings/cache/fractions/reset'),

    // ── Темы обращений ──────────────────────────────────────────────
    getTopics: () =>
      request<{ data: TTicketTopic[] }>('GET', '/api/settings/ticket-topics/all'),

    createTopic: (data: Omit<TTicketTopic, 'id'>) =>
      request<{ ok: boolean; id: number }>('POST', '/api/settings/ticket-topics', data),

    updateTopic: (id: number, data: Partial<Omit<TTicketTopic, 'id'>>) =>
      request<{ ok: boolean }>('PUT', `/api/settings/ticket-topics/${id}`, data),

    deleteTopic: (id: number) =>
      request<{ ok: boolean }>('DELETE', `/api/settings/ticket-topics/${id}`),
  },

  // ── Logs ──────────────────────────────────────────────────────────
  logs: {
    siteLogs: (params: {
      actor?:  string;
      action?: string;
      entity?: string;
      from?:   string;
      to?:     string;
      cursor?: number | null;
      limit?:  number;
      /** Скрыть служебные действия (heartbeat, аналитика и т.п.) */
      hideService?: boolean;
    }) => {
      const q = new URLSearchParams();
      if (params.actor)  q.set('actor',  params.actor);
      if (params.action) q.set('action', params.action);
      if (params.entity) q.set('entity', params.entity);
      if (params.from)   q.set('from',   params.from);
      if (params.to)     q.set('to',     params.to);
      if (params.cursor) q.set('cursor', String(params.cursor));
      if (params.limit)  q.set('limit',  String(params.limit));
      if (params.hideService) q.set('hide_service', '1');
      return request<TAuditLogsResponse>('GET', `/api/logs/site?${q}`);
    },

    authLogs: (params: {
      actor?:  string;
      action?: string;
      from?:   string;
      to?:     string;
      cursor?: number | null;
      limit?:  number;
    }) => {
      const q = new URLSearchParams();
      if (params.actor)  q.set('actor',  params.actor);
      if (params.action) q.set('action', params.action);
      if (params.from)   q.set('from',   params.from);
      if (params.to)     q.set('to',     params.to);
      if (params.cursor) q.set('cursor', String(params.cursor));
      if (params.limit)  q.set('limit',  String(params.limit));
      return request<TAuditLogsResponse>('GET', `/api/logs/auth?${q}`);
    },

    moneySearch: (params: {
      q?:         string;
      uid?:       string;
      type?:      string;
      modifier?:  string;
      from?:      string;
      to?:        string;
      amountMin?: string;
      amountMax?: string;
      /** Подстрока комментария операции */
      text?:      string;
      cursor?:    number | null;
      limit?:     number;
    }) => {
      const q = new URLSearchParams();
      if (params.q)         q.set('q',        params.q);
      if (params.text)      q.set('text',      params.text);
      if (params.uid)       q.set('uid',       params.uid);
      if (params.type)      q.set('type',      params.type);
      if (params.modifier)  q.set('modifier',  params.modifier);
      if (params.from)      q.set('from',      params.from);
      if (params.to)        q.set('to',        params.to);
      if (params.amountMin) q.set('amountMin', params.amountMin);
      if (params.amountMax) q.set('amountMax', params.amountMax);
      if (params.cursor)    q.set('cursor',    String(params.cursor));
      if (params.limit)     q.set('limit',     String(params.limit));
      return request<TMoneySearchResponse>('GET', `/api/logs/money/search?${q}`);
    },

    playerProfile: (uid: string, params: {
      cursor?:        number | null;
      limit?:         number;
      type?:          string;
      modifier?:      string;
      historyLimit?:  number;
      historyOffset?: number;
      historyFrom?:   string;
      historyTo?:     string;
      opsFrom?:       string;
      opsTo?:         string;
      skipHistory?:   boolean;
    }) => {
      const q = new URLSearchParams();
      if (params.cursor)                        q.set('cursor',        String(params.cursor));
      if (params.limit)                         q.set('limit',          String(params.limit));
      if (params.type)                          q.set('type',           params.type);
      if (params.modifier)                      q.set('modifier',       params.modifier);
      if (params.historyLimit)                  q.set('historyLimit',   String(params.historyLimit));
      if (params.historyOffset !== undefined)   q.set('historyOffset',  String(params.historyOffset));
      if (params.historyFrom)                   q.set('historyFrom',    params.historyFrom);
      if (params.historyTo)                     q.set('historyTo',      params.historyTo);
      if (params.opsFrom)                       q.set('opsFrom',        params.opsFrom);
      if (params.opsTo)                         q.set('opsTo',          params.opsTo);
      if (params.skipHistory)                   q.set('skipHistory',    'true');
      return request<TPlayerMoneyProfile>('GET', `/api/logs/money/player/${encodeURIComponent(uid)}?${q}`);
    },

    playerHistoryCount: (uid: string, params: { historyFrom?: string; historyTo?: string; type?: string; modifier?: string }) => {
      const q = new URLSearchParams();
      if (params.historyFrom) q.set('historyFrom', params.historyFrom);
      if (params.historyTo)   q.set('historyTo',   params.historyTo);
      if (params.type)        q.set('type',        params.type);
      if (params.modifier)    q.set('modifier',    params.modifier);
      return request<{ count: number }>('GET', `/api/logs/money/player/${encodeURIComponent(uid)}/count?${q}`);
    },

    // ── Связь игроков ─────────────────────────────────────────────────
    moneyPair: (params: { a: string; b: string; from: string; to: string; channels?: string[] }) => {
      const q = new URLSearchParams({ a: params.a, b: params.b, from: params.from, to: params.to });
      if (params.channels?.length) q.set('channels', params.channels.join(','));
      return request<TMoneyPairResponse>('GET', `/api/logs/money/pair?${q}`);
    },

    moneyCounterparties: (params: { uid: string; from: string; to: string }) =>
      request<TCounterpartiesResponse>('GET', `/api/logs/money/counterparties?${new URLSearchParams(params)}`),

    rmt: {
      settings: () =>
        request<{ settings: TRmtSettings; status: TRmtDetectorStatus | null }>('GET', '/api/logs/money/rmt/settings'),
      saveSettings: (patch: Partial<TRmtSettings>) =>
        request<{ settings: TRmtSettings }>('PUT', '/api/logs/money/rmt/settings', patch),
      addExclusion: (a: string, b: string, note: string) =>
        request<{ settings: TRmtSettings }>('POST', '/api/logs/money/rmt/exclusions', { a, b, note }),
      removeExclusion: (a: string, b: string) =>
        request<{ settings: TRmtSettings }>('DELETE', `/api/logs/money/rmt/exclusions?${new URLSearchParams({ a, b })}`),
      run: () =>
        request<{ pairs: number; opened: number; updated: number; status: TRmtDetectorStatus | null }>('POST', '/api/logs/money/rmt/run'),
    },

    // ── Статистика казино ─────────────────────────────────────────────
    casinoSummary: (params: { periods?: string; from?: string; to?: string }) => {
      const q = new URLSearchParams();
      if (params.periods) q.set('periods', params.periods);
      if (params.from)    q.set('from',    params.from);
      if (params.to)      q.set('to',      params.to);
      return request<TCasinoSummaryResponse>('GET', `/api/logs/casino/summary?${q}`);
    },

    casinoTimeseries: (params: { from?: string; to?: string }) => {
      const q = new URLSearchParams();
      if (params.from) q.set('from', params.from);
      if (params.to)   q.set('to',   params.to);
      return request<{ data: TCasinoTimePoint[] }>('GET', `/api/logs/casino/timeseries?${q}`);
    },

    casinoSlots: (params: { from?: string; to?: string }) => {
      const q = new URLSearchParams();
      if (params.from) q.set('from', params.from);
      if (params.to)   q.set('to',   params.to);
      return request<{ data: TCasinoSlotRow[] }>('GET', `/api/logs/casino/slots?${q}`);
    },

    casinoPlayers: (params: { game?: TCasinoGame | 'all'; from?: string; to?: string; limit?: number }) => {
      const q = new URLSearchParams();
      if (params.game)  q.set('game',  params.game);
      if (params.from)  q.set('from',  params.from);
      if (params.to)    q.set('to',    params.to);
      if (params.limit) q.set('limit', String(params.limit));
      return request<{ winners: TCasinoTopPlayer[]; losers: TCasinoTopPlayer[] }>('GET', `/api/logs/casino/players?${q}`);
    },

    casinoSessions: <T = TCasinoSlotSession | TCasinoWheelSession | TCasinoMoneyOp>(params: {
      game:    TCasinoGame;
      player?: string;
      from?:   string;
      to?:     string;
      cursor?: number | null;
      limit?:  number;
    }) => {
      const q = new URLSearchParams({ game: params.game });
      if (params.player) q.set('player', params.player);
      if (params.from)   q.set('from',   params.from);
      if (params.to)     q.set('to',     params.to);
      if (params.cursor) q.set('cursor', String(params.cursor));
      if (params.limit)  q.set('limit',  String(params.limit));
      return request<TCasinoSessionsResponse<T>>('GET', `/api/logs/casino/sessions?${q}`);
    },

    vehicleNames: () =>
      request<Record<string, string>>('GET', '/api/logs/vehicles/names'),

    weaponNames: () =>
      request<Record<string, string>>('GET', '/api/logs/weapons/names'),

    vehiclesSearch: (params: {
      q?:        string;
      pid?:      string;
      type?:     string;
      from?:     string;
      to?:       string;
      priceMin?: string;
      priceMax?: string;
      cursor?:   number | null;
      limit?:    number;
    }) => {
      const q = new URLSearchParams();
      if (params.q)        q.set('q',        params.q);
      if (params.pid)      q.set('pid',      params.pid);
      if (params.type)     q.set('type',     params.type);
      if (params.from)     q.set('from',     params.from);
      if (params.to)       q.set('to',       params.to);
      if (params.priceMin) q.set('priceMin', params.priceMin);
      if (params.priceMax) q.set('priceMax', params.priceMax);
      if (params.cursor)   q.set('cursor',   String(params.cursor));
      if (params.limit)    q.set('limit',    String(params.limit));
      return request<TVehicleSearchResponse>('GET', `/api/logs/vehicles/search?${q}`);
    },

    vehicleProfile: (pid: string, params: {
      cursor?: number | null;
      limit?:  number;
      type?:   string;
      from?:   string;
      to?:     string;
    }) => {
      const q = new URLSearchParams();
      if (params.cursor) q.set('cursor', String(params.cursor));
      if (params.limit)  q.set('limit',  String(params.limit));
      if (params.type)   q.set('type',   params.type);
      if (params.from)   q.set('from',   params.from);
      if (params.to)     q.set('to',     params.to);
      return request<TPlayerVehicleProfile>('GET', `/api/logs/vehicles/player/${encodeURIComponent(pid)}?${q}`);
    },

    resourcesSearch: (params: {
      pid?:     string;
      type?:    string;
      item?:    string;
      from?:    string;
      to?:      string;
      orderBy?: string;
      cursor?:  number | null;
      limit?:   number;
    }) => {
      const q = new URLSearchParams();
      if (params.pid)     q.set('pid',     params.pid);
      if (params.type)    q.set('type',    params.type);
      if (params.item)    q.set('item',    params.item);
      if (params.from)    q.set('from',    params.from);
      if (params.to)      q.set('to',      params.to);
      if (params.orderBy) q.set('orderBy', params.orderBy);
      if (params.cursor)  q.set('cursor',  String(params.cursor));
      if (params.limit)   q.set('limit',   String(params.limit));
      return request<TResourceSearchResponse>('GET', `/api/logs/resources/search?${q}`);
    },

    resourceProfile: (pid: string, params: {
      cursor?:    number | null;
      limit?:     number;
      type?:      string;
      item?:      string;
      from?:      string;
      to?:        string;
      orderBy?:   string;
      skipStats?: boolean;
    }) => {
      const q = new URLSearchParams();
      if (params.cursor)    q.set('cursor',    String(params.cursor));
      if (params.limit)     q.set('limit',     String(params.limit));
      if (params.type)      q.set('type',      params.type);
      if (params.item)      q.set('item',      params.item);
      if (params.from)      q.set('from',      params.from);
      if (params.to)        q.set('to',        params.to);
      if (params.orderBy)   q.set('orderBy',   params.orderBy);
      if (params.skipStats) q.set('skipStats', 'true');
      return request<TPlayerResourceProfile>('GET', `/api/logs/resources/player/${encodeURIComponent(pid)}?${q}`);
    },

    resourceHistoryCount: (pid: string, params: { type?: string; item?: string; from?: string; to?: string }) => {
      const q = new URLSearchParams();
      if (params.type) q.set('type', params.type);
      if (params.item) q.set('item', params.item);
      if (params.from) q.set('from', params.from);
      if (params.to)   q.set('to',   params.to);
      return request<{ count: number }>('GET', `/api/logs/resources/player/${encodeURIComponent(pid)}/count?${q}`);
    },

    resourceFinds: (params: {
      pid?:    string;
      from?:   string;
      to?:     string;
      cursor?: number | null;
      limit?:  number;
    }) => {
      const q = new URLSearchParams();
      if (params.pid)    q.set('pid',    params.pid);
      if (params.from)   q.set('from',   params.from);
      if (params.to)     q.set('to',     params.to);
      if (params.cursor) q.set('cursor', String(params.cursor));
      if (params.limit)  q.set('limit',  String(params.limit));
      return request<TResourceFindsResponse>('GET', `/api/logs/resources/finds?${q}`);
    },

    // Логи БПЛА: kind = 'create' (создание) | 'use' (использование)
    bplaSearch: (kind: 'create' | 'use', params: {
      pid?:       string;
      classname?: string;
      from?:      string;
      to?:        string;
      cursor?:    number | null;
      limit?:     number;
    }) => {
      const q = new URLSearchParams();
      if (params.pid)       q.set('pid',       params.pid);
      if (params.classname) q.set('classname', params.classname);
      if (params.from)      q.set('from',      params.from);
      if (params.to)        q.set('to',        params.to);
      if (params.cursor)    q.set('cursor',    String(params.cursor));
      if (params.limit)     q.set('limit',     String(params.limit));
      return request<TBplaSearchResponse>('GET', `/api/logs/bpla/${kind}?${q}`);
    },

    // Логи использования сердец Моммы (logs_momma_heart)
    mommaHeartSearch: (params: {
      pid?:    string;
      from?:   string;
      to?:     string;
      cursor?: number | null;
      limit?:  number;
    }) => {
      const q = new URLSearchParams();
      if (params.pid)    q.set('pid',    params.pid);
      if (params.from)   q.set('from',   params.from);
      if (params.to)     q.set('to',     params.to);
      if (params.cursor) q.set('cursor', String(params.cursor));
      if (params.limit)  q.set('limit',  String(params.limit));
      return request<TMommaHeartSearchResponse>('GET', `/api/logs/momma-heart/search?${q}`);
    },

    // ── Войны за территории (logs_territory_wars) ─────────────────────
    territory: {
      events: (params: TTerritoryLogFilters) =>
        request<TTerritoryEventsPage>('GET', `/api/logs/territory/events?${territoryQuery(params)}`),

      wars: (params: TTerritoryLogFilters) =>
        request<TTerritoryWarsPage>('GET', `/api/logs/territory/wars?${territoryQuery(params)}`),

      war: (warId: string) =>
        request<TTerritoryWarDetail>('GET', `/api/logs/territory/wars/${encodeURIComponent(warId)}`),

      territories: () =>
        request<{ id: string; name: string; type: number }[]>('GET', '/api/logs/territory/territories'),
    },

    // ── Журнал убийств (агонии) и пороги детекторов ──────────────────
    kills: {
      search: (params: TKillFilters) =>
        request<TCursorPage<TKillRow>>('GET', `/api/logs/kills/search?${killsQuery(params)}`),

      weapons: () =>
        request<TKillWeaponsResponse>('GET', '/api/logs/kills/weapons'),

      patchWeapon: (key: string, patch: Partial<{ label: string; alias_of: string | null; kind: TKillWeaponKind | null; ignored: boolean; suspicious_override: number | null }>) =>
        request<{ ok: true }>('PATCH', `/api/logs/kills/weapons/${encodeURIComponent(key)}`, patch),

      recompute: () =>
        request<{ ok: true; fallback: number | null }>('POST', '/api/logs/kills/weapons/recompute'),

      settings: () =>
        request<{ settings: TKillSettings }>('GET', '/api/logs/kills/settings'),

      saveSettings: (patch: Partial<TKillSettings>) =>
        request<{ settings: TKillSettings }>('PUT', '/api/logs/kills/settings', patch),

      player: (uid: string) =>
        request<TKillPlayerSummary>('GET', `/api/logs/kills/player/${encodeURIComponent(uid)}`),

      overview: (days: number, team: boolean) =>
        request<TKillOverview>('GET', `/api/logs/kills/overview?days=${days}${team ? '&team=1' : ''}`),

      status: () =>
        request<TKillStatus>('GET', '/api/logs/kills/status'),

      importDays: (days: number) =>
        request<{ backfill: TKillStatus['backfill'] }>('POST', '/api/logs/kills/import', { days }),

      suspects: (days: number) =>
        request<TKillSuspects>('GET', `/api/logs/kills/suspects?days=${days}`),

      gangs: (days: number) =>
        request<TKillGangs>('GET', `/api/logs/kills/gangs?days=${days}`),

      map: (p: { days: number; weapon?: string; group?: string; onlyOver?: boolean; team?: boolean }) => {
        const q = new URLSearchParams({ days: String(p.days) });
        if (p.weapon) q.set('weapon', p.weapon);
        if (p.group) q.set('group', p.group);
        if (p.onlyOver) q.set('onlyOver', '1');
        if (p.team) q.set('team', '1');
        return request<TKillMap>('GET', `/api/logs/kills/map?${q}`);
      },

      weaponAnalytics: (days: number) =>
        request<TKillWeaponAnalytics>('GET', `/api/logs/kills/weapons/analytics?days=${days}`),

      weaponHistogram: (key: string, days: number) =>
        request<TKillWeaponHistogram>('GET', `/api/logs/kills/weapons/${encodeURIComponent(key)}/histogram?days=${days}`),

      profile: (uid: string, days: number, weapon?: string) =>
        request<TKillProfile>('GET', `/api/logs/kills/player/${encodeURIComponent(uid)}/profile?days=${days}${weapon ? `&weapon=${encodeURIComponent(weapon)}` : ''}`),
    },

    // ── События игры (logs_any) и игровые инциденты ──────────────────
    events: {
      journal: (params: TGameEventsJournalParams) =>
        request<TCursorPage<TGameJournalRow>>('GET', `/api/logs/events/journal?${gameEventsQuery(params)}`),

      incidents: (params: TGameIncidentsParams) =>
        request<TCursorPage<TGameIncident>>('GET', `/api/logs/events/incidents?${gameEventsQuery(params)}`),

      summary: (limit = 15) =>
        request<TGameIncidentsSummary>('GET', `/api/logs/events/summary?limit=${limit}`),

      incident: (id: number) =>
        request<TGameIncidentDetail>('GET', `/api/logs/events/incidents/${id}`),

      close: (id: number, verdict: TGameIncidentVerdict, comment: string) =>
        request<{ incident: TGameIncident }>('POST', `/api/logs/events/incidents/${id}/close`, { verdict, comment }),

      reopen: (id: number, comment: string) =>
        request<{ incident: TGameIncident }>('POST', `/api/logs/events/incidents/${id}/reopen`, { comment }),

      comment: (id: number, comment: string) =>
        request<{ event: TGameIncidentEvent }>('POST', `/api/logs/events/incidents/${id}/comments`, { comment }),

      types: () =>
        request<TGameIncidentTypesResponse>('GET', '/api/logs/events/types'),

      createType: (data: { code: string; label: string; description: string | null; severity: TGameIncidentSeverity; dedup_minutes: number }) =>
        request<{ type: TGameIncidentType }>('POST', '/api/logs/events/types', data),

      updateType: (code: string, patch: Partial<{ label: string; description: string | null; severity: TGameIncidentSeverity; dedup_minutes: number; enabled: boolean }>) =>
        request<{ type: TGameIncidentType }>('PATCH', `/api/logs/events/types/${encodeURIComponent(code)}`, patch),
    },

    // ── Логи общения (почта + звонки) ────────────────────────────────
    commsPlayers: (q: string) =>
      request<TCommsPlayerHit[]>('GET', `/api/logs/comms/players?q=${encodeURIComponent(q)}`),

    commsDialogs: (params: { uid: string; from?: string; to?: string }) => {
      const q = new URLSearchParams({ uid: params.uid });
      if (params.from) q.set('from', params.from);
      if (params.to)   q.set('to',   params.to);
      return request<TCommsDialogsResponse>('GET', `/api/logs/comms/dialogs?${q}`);
    },

    commsThread: (params: {
      a: string; b: string;
      from?: string; to?: string;
      cursor?: number | null; limit?: number;
      around?: number | null; dir?: 'newer';
      skipCalls?: boolean;
    }) => {
      const q = new URLSearchParams({ a: params.a, b: params.b });
      if (params.from)      q.set('from',      params.from);
      if (params.to)        q.set('to',        params.to);
      if (params.cursor)    q.set('cursor',    String(params.cursor));
      if (params.limit)     q.set('limit',     String(params.limit));
      if (params.around)    q.set('around',    String(params.around));
      if (params.dir)       q.set('dir',       params.dir);
      if (params.skipCalls) q.set('skipCalls', 'true');
      return request<TCommsThreadResponse>('GET', `/api/logs/comms/thread?${q}`);
    },

    commsGroup: (params: {
      name: string;
      from?: string; to?: string;
      cursor?: number | null; limit?: number;
      around?: number | null; dir?: 'newer';
    }) => {
      const q = new URLSearchParams({ name: params.name });
      if (params.from)   q.set('from',   params.from);
      if (params.to)     q.set('to',     params.to);
      if (params.cursor) q.set('cursor', String(params.cursor));
      if (params.limit)  q.set('limit',  String(params.limit));
      if (params.around) q.set('around', String(params.around));
      if (params.dir)    q.set('dir',    params.dir);
      return request<TCommsThreadResponse>('GET', `/api/logs/comms/group?${q}`);
    },

    commsChannel: (params: {
      channel: string; name?: string;
      from?: string; to?: string;
      cursor?: number | null; limit?: number;
      around?: number | null; dir?: 'newer';
    }) => {
      const q = new URLSearchParams({ channel: params.channel });
      if (params.name)   q.set('name',   params.name);
      if (params.from)   q.set('from',   params.from);
      if (params.to)     q.set('to',     params.to);
      if (params.cursor) q.set('cursor', String(params.cursor));
      if (params.limit)  q.set('limit',  String(params.limit));
      if (params.around) q.set('around', String(params.around));
      if (params.dir)    q.set('dir',    params.dir);
      return request<TCommsThreadResponse>('GET', `/api/logs/comms/channel?${q}`);
    },

    commsSearch: (params: {
      q?: string; uid?: string; name?: string; kind?: TCommsKind;
      from?: string; to?: string;
      cursor?: number | null; limit?: number;
    }) => {
      const q = new URLSearchParams();
      if (params.q)      q.set('q',      params.q);
      if (params.uid)    q.set('uid',    params.uid);
      if (params.name)   q.set('name',   params.name);
      if (params.kind)   q.set('kind',   params.kind);
      if (params.from)   q.set('from',   params.from);
      if (params.to)     q.set('to',     params.to);
      if (params.cursor) q.set('cursor', String(params.cursor));
      if (params.limit)  q.set('limit',  String(params.limit));
      return request<TCommsMessagesResponse>('GET', `/api/logs/comms/search?${q}`);
    },

    commsCalls: (params: {
      uid?: string; kind?: 'service' | 'p2p';
      from?: string; to?: string;
      cursor?: number | null; limit?: number;
    }) => {
      const q = new URLSearchParams();
      if (params.uid)    q.set('uid',    params.uid);
      if (params.kind)   q.set('kind',   params.kind);
      if (params.from)   q.set('from',   params.from);
      if (params.to)     q.set('to',     params.to);
      if (params.cursor) q.set('cursor', String(params.cursor));
      if (params.limit)  q.set('limit',  String(params.limit));
      return request<TCommsCallsResponse>('GET', `/api/logs/comms/calls?${q}`);
    },
  },

  // ─── Game Tickets (admin_issues из game DB) ────────────────────────────
  gameTickets: {
    list: (params?: Record<string, string | number>) => {
      const q = new URLSearchParams();
      if (params) Object.entries(params).forEach(([k, v]) => q.set(k, String(v)));
      return request<{ data: any[] }>('GET', `/api/admin/game-tickets?${q}`);
    },
    get: (id: number) =>
      request<{ ticket: any; player: any }>('GET', `/api/admin/game-tickets/${id}`),
    reply: (id: number, body: string) =>
      request<{ ok: boolean }>('POST', `/api/admin/game-tickets/${id}/reply`, { body }),
    stats: () =>
      request<{ open_count: number }>('GET', `/api/admin/game-tickets/stats`),
  },

  // ── Rules (редактор правил) ───────────────────────────────────────
  rules: {
    list: () =>
      request<{ sections: TRulesNode[] }>('GET', '/api/rules/admin').then(r => r.sections),

    create: (data: { parent_id?: number | null; slug?: string; title: string; body_html?: string; is_hidden?: boolean }) =>
      request<TRulesNode>('POST', '/api/rules/admin', data),

    update: (id: number, data: Partial<{ slug: string; title: string; body_html: string; is_hidden: boolean; parent_id: number | null }>) =>
      request<TRulesNode>('PATCH', `/api/rules/admin/${id}`, data),

    remove: (id: number) =>
      request<{ ok: boolean }>('DELETE', `/api/rules/admin/${id}`),

    reorder: (items: { id: number; parent_id: number | null; position: number }[]) =>
      request<{ ok: boolean }>('PUT', '/api/rules/admin/reorder', { items }),

    upload: (file: File, onProgress?: TUploadProgress): Promise<{ ok: boolean; url: string }> => {
      assertImageSize(file);
      const fd = new FormData();
      fd.append('image', file);
      return xhrUpload(`${BASE}/api/rules/admin/upload`, fd, onProgress);
    },
  },

  // ── FAQ (частые вопросы на главной) ───────────────────────────────
  faq: {
    list: () =>
      request<{ items: TFaqItem[] }>('GET', '/api/faq/admin').then(r => r.items),

    create: (data: { question: string; answer_html?: string; is_hidden?: boolean }) =>
      request<TFaqItem>('POST', '/api/faq/admin', data),

    update: (id: number, data: Partial<{ question: string; answer_html: string; is_hidden: boolean }>) =>
      request<TFaqItem>('PATCH', `/api/faq/admin/${id}`, data),

    remove: (id: number) =>
      request<{ ok: boolean }>('DELETE', `/api/faq/admin/${id}`),

    reorder: (items: { id: number; position: number }[]) =>
      request<{ ok: boolean }>('PUT', '/api/faq/admin/reorder', { items }),
  },

  // ── Files (файловое хранилище) ────────────────────────────────────
  files: {
    list: () =>
      request<TStorageFile[]>('GET', '/api/files/admin'),

    update: (id: number, data: Partial<{
      title: string; description: string | null; access: 'public' | 'auth';
      inline_view: boolean; is_active: boolean; password: string; clear_password: boolean;
      slug: string;
    }>) =>
      request<TStorageFile>('PATCH', `/api/files/admin/${id}`, data),

    remove: (id: number) =>
      request<{ ok: boolean }>('DELETE', `/api/files/admin/${id}`),

    stats: (id: number) =>
      request<TStorageFileStats>('GET', `/api/files/admin/${id}/stats`),

    // Лог скачиваний: пагинация, фильтры (авторизация/даты), поиск
    downloads: (id: number, params: {
      page?:  number;
      limit?: number;
      auth?:  'auth' | 'anon' | '';
      from?:  string;
      to?:    string;
      q?:     string;
    }) => {
      const qs = new URLSearchParams();
      if (params.page)  qs.set('page',  String(params.page));
      if (params.limit) qs.set('limit', String(params.limit));
      if (params.auth)  qs.set('auth',  params.auth);
      if (params.from)  qs.set('from',  params.from);
      if (params.to)    qs.set('to',    params.to);
      if (params.q)     qs.set('q',     params.q);
      return request<TFileDownloadsPage>('GET', `/api/files/admin/${id}/downloads?${qs}`);
    },

    /**
     * Загрузка через XMLHttpRequest — fetch не умеет отдавать прогресс
     * отправки тела (upload.onprogress).
     */
    upload: (
      file: File,
      meta: {
        title?: string; description?: string; access: 'public' | 'auth';
        password?: string; inline_view?: boolean; slug?: string;
      },
      onProgress?: (loaded: number, total: number) => void,
    ): Promise<TStorageFile> => {
      const fd = new FormData();
      fd.append('file', file);
      if (meta.title)       fd.append('title', meta.title);
      if (meta.description) fd.append('description', meta.description);
      fd.append('access', meta.access);
      if (meta.password)    fd.append('password', meta.password);
      if (meta.inline_view) fd.append('inline_view', '1');
      if (meta.slug)        fd.append('slug', meta.slug);
      return xhrUpload<TStorageFile>(`${BASE}/api/files/admin/upload`, fd, onProgress);
    },

    /** Замена файла: новый файл по той же ссылке, старый удаляется с диска. */
    replace: (
      id: number,
      file: File,
      onProgress?: (loaded: number, total: number) => void,
    ): Promise<TStorageFile> => {
      const fd = new FormData();
      fd.append('file', file);
      return xhrUpload<TStorageFile>(`${BASE}/api/files/admin/${id}/replace`, fd, onProgress);
    },
  },

  // ── CMS-страницы (/pages) ──────────────────────────────────────────
  pages: {
    list: () =>
      request<TCmsPage[]>('GET', '/api/pages/admin'),

    get: (id: number) =>
      request<TCmsPage>('GET', `/api/pages/admin/${id}`),

    create: (data: TCmsPagePayload) =>
      request<TCmsPage>('POST', '/api/pages/admin', data),

    update: (id: number, data: Partial<TCmsPagePayload> & { clear_password?: boolean }) =>
      request<TCmsPage>('PATCH', `/api/pages/admin/${id}`, data),

    remove: (id: number) =>
      request<{ ok: boolean }>('DELETE', `/api/pages/admin/${id}`),

    stats: (id: number) =>
      request<TCmsPageStats>('GET', `/api/pages/admin/${id}/stats`),

    // Журнал визитов: пагинация, фильтры (авторизация/даты), поиск
    visits: (id: number, params: {
      page?: number; limit?: number; auth?: 'auth' | 'anon' | '';
      from?: string; to?: string; q?: string;
    }) => {
      const qs = new URLSearchParams();
      if (params.page)  qs.set('page',  String(params.page));
      if (params.limit) qs.set('limit', String(params.limit));
      if (params.auth)  qs.set('auth',  params.auth);
      if (params.from)  qs.set('from',  params.from);
      if (params.to)    qs.set('to',    params.to);
      if (params.q)     qs.set('q',     params.q);
      return request<TCmsVisitsPage>('GET', `/api/pages/admin/${id}/visits?${qs}`);
    },

    versions: (id: number) =>
      request<TCmsPageVersion[]>('GET', `/api/pages/admin/${id}/versions`),

    versionContent: (id: number, verId: number) =>
      request<TCmsPageVersion & { body_json: string | null; body_html: string }>(
        'GET', `/api/pages/admin/${id}/versions/${verId}`),

    restoreVersion: (id: number, verId: number) =>
      request<TCmsPage>('POST', `/api/pages/admin/${id}/versions/${verId}/restore`),

    /** Загрузка медиа контента (картинка до 30 МБ / видео MP4-WebM до 200 МБ). */
    uploadAsset: (
      file: File,
      target?: { page_id?: number; category_id?: number },
      onProgress?: (loaded: number, total: number) => void,
    ): Promise<{ ok: boolean; id: number; url: string; mime: string; is_video: boolean }> => {
      if (file.type.startsWith('image/')) assertImageSize(file); // у видео свой лимит (200 МБ)
      const fd = new FormData();
      fd.append('file', file);
      if (target?.page_id)     fd.append('page_id', String(target.page_id));
      if (target?.category_id) fd.append('category_id', String(target.category_id));
      return xhrUpload(`${BASE}/api/pages/admin/assets`, fd, onProgress);
    },

    categories: {
      list: () =>
        request<TCmsCategory[]>('GET', '/api/pages/admin/categories'),

      get: (id: number) =>
        request<TCmsCategory>('GET', `/api/pages/admin/categories/${id}`),

      create: (data: TCmsCategoryPayload) =>
        request<TCmsCategory>('POST', '/api/pages/admin/categories', data),

      update: (id: number, data: Partial<TCmsCategoryPayload>) =>
        request<TCmsCategory>('PATCH', `/api/pages/admin/categories/${id}`, data),

      remove: (id: number) =>
        request<{ ok: boolean }>('DELETE', `/api/pages/admin/categories/${id}`),
    },
  },

  // ── Новости (право news.manage) ─────────────────────────────────────
  news: {
    list: () =>
      request<TNewsAdminItem[]>('GET', '/api/news/admin'),

    get: (id: number) =>
      request<TNewsAdminFull>('GET', `/api/news/admin/${id}`),

    create: (data: TNewsPayload) =>
      request<TNewsAdminFull>('POST', '/api/news/admin', data),

    update: (id: number, data: Partial<TNewsPayload>) =>
      request<TNewsAdminFull>('PATCH', `/api/news/admin/${id}`, data),

    remove: (id: number) =>
      request<{ ok: boolean }>('DELETE', `/api/news/admin/${id}`),

    /** Загрузка изображения (обложка/контент); сервер ужимает до WebP */
    uploadAsset: (file: File, onProgress?: TUploadProgress): Promise<{ url: string; size: number; mime: string }> => {
      assertImageSize(file);
      const fd = new FormData();
      fd.append('file', file);
      return xhrUpload(`${BASE}/api/news/admin/assets`, fd, onProgress);
    },
  },

  // ── Игровые промокоды (право promocodes.manage) ─────────────────────
  promocodes: {
    catalog: () =>
      request<{ groups: TPromoCatalogGroup[] }>('GET', '/api/promocodes/catalog'),

    searchItems: (query: string) =>
      request<{ items: TPromoItemHit[] }>('GET', `/api/promocodes/items?query=${encodeURIComponent(query)}`),

    list: () =>
      request<{ codes: TPromoCode[] }>('GET', '/api/promocodes/codes'),

    create: (body: TPromoCreateBody) =>
      request<{ code: string }>('POST', '/api/promocodes/codes', body),

    deactivate: (code: string) =>
      request<{ ok: true }>('POST', `/api/promocodes/codes/${encodeURIComponent(code)}/deactivate`),

    uses: (code: string) =>
      request<{ uses: TPromoUse[] }>('GET', `/api/promocodes/codes/${encodeURIComponent(code)}/uses`),
  },

  // ── Поддержка в ЛК: сценарий-мастер и свои обращения (любой пользователь) ──
  support: {
    start: () =>
      request<FlowSession>('POST', '/api/support/sessions', { platform: 'web' }),
    session: (id: number) =>
      request<FlowSession>('GET', `/api/support/sessions/${id}`),
    step: (id: number, input: { edgeId: string } | { input: string } | { helped: boolean } | { action: true }) =>
      request<FlowSession>('POST', `/api/support/sessions/${id}/step`, input),
    recent: () =>
      request<FlowSessionBrief[]>('GET', '/api/support/sessions/my'),
    /** Игрок завершает диалог сам (исход closed) */
    finish: (id: number, data: { reason: FlowCloseReason; comment?: string }) =>
      request<{ ok: boolean }>('POST', `/api/support/sessions/${id}/finish`, data),
    createTicket: (id: number, data: { subject: string; description: string; attachments: string[] }) =>
      request<{ ok: boolean; ticket_id: number }>('POST', `/api/support/sessions/${id}/ticket`, data),
    /** Куда грузить скриншоты до появления тикета (для useUploadQueue) */
    uploadUrl: (id: number) => `${BASE}/api/support/sessions/${id}/upload`,

    myTickets: (q: { scope?: 'all' | 'active' | 'archive'; page?: number; limit?: number }) => {
      const params = new URLSearchParams();
      if (q.scope) params.set('scope', q.scope);
      if (q.page) params.set('page', String(q.page));
      if (q.limit) params.set('limit', String(q.limit));
      return request<{ data: TMyTicketRow[]; total: number; page: number; limit: number }>('GET', `/api/tickets/my?${params}`);
    },
    restriction: () =>
      request<{ restriction: boolean; title?: string; comment?: string | null; expires_at?: string | null }>('GET', '/api/tickets/my/restriction'),
  },

  // ── Сценарий помощника поддержки (право support.flow.manage) ────────
  supportFlow: {
    get: () =>
      request<{
        draft: FlowV2 | null; published: FlowV2 | null;
        published_at: string | null; updated_at: string | null; issues: FlowValidation;
      }>('GET', '/api/support/admin/flow'),
    saveDraft: (flow: FlowV2) =>
      request<{ ok: boolean; flow: FlowV2; issues: FlowValidation }>('PUT', '/api/support/admin/flow', { flow }),
    publish: () =>
      request<{ ok: boolean }>('POST', '/api/support/admin/flow/publish'),
    stats: (q: { from?: string; to?: string; platform?: FlowPlatform }) => {
      const params = new URLSearchParams();
      if (q.from) params.set('from', q.from);
      if (q.to) params.set('to', q.to);
      if (q.platform) params.set('platform', q.platform);
      return request<FlowStats>('GET', `/api/support/admin/flow/stats?${params.toString()}`);
    },
  },

  // ── Лаунчер (право launcher.manage; sessions/versionStats/multiaccounts/
  //    userProfile/userDevices — ещё и launcher.users.view) ──────────────
  launcher: {
    overview: () =>
      request<TLauncherOverview>('GET', '/api/launcher/admin/overview'),

    /** Дистрибутивы: канал автообновления и инсталлер со ссылками на скачивание */
    distrib: () =>
      request<TLauncherDistrib>('GET', '/api/launcher/admin/distrib'),

    /** Лёгкая сводка для виджета на дашборде (право launcher.status.view) */
    dashboard: () =>
      request<TLauncherDashboard>('GET', '/api/launcher/admin/dashboard'),

    branches: {
      /** Без источника — только пустая ветка для самой первой заливки */
      create: (name: string, title: string, source?: { from: string; from_version: string; version?: string }) =>
        request<{ ok: boolean; name: string; title: string; version: string | null }>(
          'POST', '/api/launcher/admin/branches', { name, title, ...source },
        ),

      remove: (name: string) =>
        request<{ ok: boolean }>('DELETE', `/api/launcher/admin/branches/${encodeURIComponent(name)}`),

      rebuild: (name: string, version: string) =>
        request<{ ok: boolean; job: TLauncherJob }>('POST', `/api/launcher/admin/branches/${encodeURIComponent(name)}/rebuild`, { version }),

      job: (name: string) =>
        request<TLauncherJob>('GET', `/api/launcher/admin/branches/${encodeURIComponent(name)}/job`),

      activate: (name: string, version: string) =>
        request<{ ok: boolean; activeVersion: string }>(
          'POST',
          `/api/launcher/admin/branches/${encodeURIComponent(name)}/activate`,
          { version },
        ),

      setVisibility: (name: string, isPublic: boolean) =>
        request<{ ok: boolean; isPublic: boolean }>('POST', `/api/launcher/admin/branches/${encodeURIComponent(name)}/visibility`, { isPublic }),

      promoteDiff: (to: string, from: string, fromVersion: string) =>
        request<TLauncherPromoteDiff>(
          'GET',
          `/api/launcher/admin/branches/${encodeURIComponent(to)}/diff?from=${encodeURIComponent(from)}&fromVersion=${encodeURIComponent(fromVersion)}`,
        ),

      promote: (to: string, from: string, fromVersion: string, version: string, activate: boolean) =>
        request<{ ok: boolean; job: TLauncherJob; activated: boolean }>(
          'POST',
          `/api/launcher/admin/branches/${encodeURIComponent(to)}/promote`,
          { from, from_version: fromVersion, version, activate },
        ),

      removeVersion: (name: string, version: string) =>
        request<{ ok: boolean }>('DELETE', `/api/launcher/admin/branches/${encodeURIComponent(name)}/versions/${encodeURIComponent(version)}`),

      manifest: (name: string, version: string) =>
        request<TLauncherManifest>('GET', `/api/launcher/admin/branches/${encodeURIComponent(name)}/versions/${encodeURIComponent(version)}/manifest`),

      watchFiles: (name: string) =>
        request<{ files: TLauncherWatchFile[] }>('GET', `/api/launcher/admin/branches/${encodeURIComponent(name)}/watch-files`),

      removeWatchFile: (name: string, path: string) =>
        request<{ ok: boolean }>('DELETE', `/api/launcher/admin/branches/${encodeURIComponent(name)}/watch-files?path=${encodeURIComponent(path)}`),
    },

    gc: () =>
      request<{ ok: boolean; removed: number; freedBytes: number }>('POST', '/api/launcher/admin/gc'),

    codes: {
      list: (branch?: string) =>
        request<TLauncherCode[]>('GET', `/api/launcher/admin/codes${branch ? `?branch=${encodeURIComponent(branch)}` : ''}`),

      create: (data: { branch: string; code?: string; max_activations?: number; expires_at?: string | null; note?: string }) =>
        request<{ ok: boolean; id: number; code: string }>('POST', '/api/launcher/admin/codes', data),

      update: (id: number, data: { is_active?: boolean; max_activations?: number; expires_at?: string | null; note?: string | null }) =>
        request<{ ok: boolean }>('PATCH', `/api/launcher/admin/codes/${id}`, data),

      remove: (id: number) =>
        request<{ ok: boolean }>('DELETE', `/api/launcher/admin/codes/${id}`),

      activations: (id: number) =>
        request<{ user_id: number; login: string | null; activated_at: string }[]>('GET', `/api/launcher/admin/codes/${id}/activations`),
    },

    serverKeys: {
      list: () =>
        request<{ items: TLauncherServerKey[]; download_url: string }>('GET', '/api/launcher/admin/server-keys'),
      create: (name: string, branches: string[]) =>
        request<{ key: string; item: TLauncherServerKey }>('POST', '/api/launcher/admin/server-keys', { name, branches }),
      revoke: (id: number) =>
        request<{ ok: boolean }>('POST', `/api/launcher/admin/server-keys/${id}/revoke`),
    },

    whitelist: {
      list: () =>
        request<TLauncherWhitelistEntry[]>('GET', '/api/launcher/admin/whitelist'),
      add: (steam_id: string, note?: string) =>
        request<{ ok: boolean }>('POST', '/api/launcher/admin/whitelist', { steam_id, note }),
      remove: (id: number) =>
        request<{ ok: boolean }>('DELETE', `/api/launcher/admin/whitelist/${id}`),
    },

    exemptions: {
      list: () =>
        request<TLauncherExemption[]>('GET', '/api/launcher/admin/exemptions'),
      add: (user_id: number, note?: string) =>
        request<{ ok: boolean }>('POST', '/api/launcher/admin/exemptions', { user_id, note }),
      remove: (id: number) =>
        request<{ ok: boolean }>('DELETE', `/api/launcher/admin/exemptions/${id}`),
    },

    settings: {
      get: () =>
        request<Record<string, string>>('GET', '/api/launcher/admin/settings'),
      patch: (data: Record<string, string>) =>
        request<{ ok: boolean; plugin_expected?: TLauncherPluginExpected; plugin_error?: string }>(
          'PATCH', '/api/launcher/admin/settings', data),
    },

    tsPlugin: {
      get: () =>
        request<{ expected: TLauncherPluginExpected | null }>('GET', '/api/launcher/admin/ts-plugin'),
      recompute: () =>
        request<{ expected: TLauncherPluginExpected }>('POST', '/api/launcher/admin/ts-plugin/recompute'),
    },

    /** Фон основной темы лаунчера; настройка сохраняется сразу */
    uploadBackground: (file: File, onProgress?: TUploadProgress): Promise<{ url: string }> => {
      assertImageSize(file);
      const fd = new FormData();
      fd.append('file', file);
      return xhrUpload(`${BASE}/api/launcher/admin/assets/background`, fd, onProgress);
    },

    /** Фон-сцена: игроки видят только опубликованную, редактор пишет в черновик */
    scene: {
      get: () =>
        request<TLauncherSceneState>('GET', '/api/launcher/admin/scene'),
      saveDraft: (scene: SceneConfig) =>
        request<{ draft: SceneConfig }>('PUT', '/api/launcher/admin/scene/draft', { scene }),
      publish: () =>
        request<{ published: SceneConfig }>('POST', '/api/launcher/admin/scene/publish'),
      revert: () =>
        request<{ draft: SceneConfig }>('POST', '/api/launcher/admin/scene/revert'),
      setMode: (mode: 'news' | 'scene') =>
        request<{ mode: 'news' | 'scene' }>('PUT', '/api/launcher/admin/scene/mode', { mode }),
      assets: {
        list: () =>
          request<{ items: TLauncherSceneAsset[] }>('GET', '/api/launcher/admin/scene/assets'),
        upload: (file: File, kind: 'image' | 'sprite', onProgress?: TUploadProgress): Promise<TLauncherSceneAsset> => {
          assertImageSize(file);
          const fd = new FormData();
          fd.append('file', file);
          return xhrUpload(`${BASE}/api/launcher/admin/scene/assets?kind=${kind}`, fd, onProgress);
        },
        remove: (id: number) =>
          request<{ ok: boolean }>('DELETE', `/api/launcher/admin/scene/assets/${id}`),
      },
    },

    /** Ссылки-иконки на главной странице лаунчера */
    links: {
      list: () =>
        request<TLauncherLink[]>('GET', '/api/launcher/admin/links'),

      create: (data: { title: string; tooltip?: string; url: string; sort?: number }) =>
        request<{ id: number }>('POST', '/api/launcher/admin/links', data),

      update: (id: number, patch: Partial<{ title: string; tooltip: string; url: string; sort: number; is_active: boolean }>) =>
        request<{ ok: boolean }>('PATCH', `/api/launcher/admin/links/${id}`, patch),

      remove: (id: number) =>
        request<{ ok: boolean }>('DELETE', `/api/launcher/admin/links/${id}`),

      uploadIcon: (id: number, file: File): Promise<{ url: string }> => {
        const fd = new FormData();
        fd.append('file', file);
        return xhrUpload(`${BASE}/api/launcher/admin/links/${id}/icon`, fd);
      },
    },

    /** Продающий блок: карточки акций/подарков/товаров/баннеров */
    promos: {
      list: (withArchived = false) =>
        request<TLauncherPromoRow[]>('GET', `/api/launcher/admin/promos${withArchived ? '?archived=1' : ''}`),

      usage: (id: number) =>
        request<TLauncherPromoUsage[]>('GET', `/api/launcher/admin/promos/${id}/usage`),

      archive: (id: number, archived: boolean) =>
        request<{ ok: true }>('POST', `/api/launcher/admin/promos/${id}/archive`, { archived }),

      create: (body: TLauncherPromoBody) =>
        request<{ id: number }>('POST', '/api/launcher/admin/promos', body),

      update: (id: number, body: TLauncherPromoBody) =>
        request<{ ok: true }>('PUT', `/api/launcher/admin/promos/${id}`, body),

      remove: (id: number) =>
        request<{ ok: true }>('DELETE', `/api/launcher/admin/promos/${id}`),

      uploadImage: (id: number, file: File, onProgress?: TUploadProgress): Promise<{ url: string }> => {
        assertImageSize(file);
        const fd = new FormData();
        fd.append('file', file);
        return xhrUpload(`${BASE}/api/launcher/admin/promos/${id}/image`, fd, onProgress);
      },
    },

    sessions: () =>
      request<TLauncherSession[]>('GET', '/api/launcher/admin/sessions'),

    versionStats: (hours = 24) =>
      request<TLauncherVersionStats>('GET', `/api/launcher/admin/stats/versions?hours=${hours}`),

    net: {
      summary: (days: number, userId?: number) =>
        request<TNetSummary>('GET', `/api/launcher/admin/net/summary?days=${days}${userId ? `&user_id=${userId}` : ''}`),
      reports: (p: { days: number; userId?: number; kind?: 'test' | 'download'; limit?: number }) => {
        const q = new URLSearchParams({ days: String(p.days), limit: String(p.limit ?? 200) });
        if (p.userId) q.set('user_id', String(p.userId));
        if (p.kind) q.set('kind', p.kind);
        return request<TNetReport[]>('GET', `/api/launcher/admin/net/reports?${q}`);
      },
    },

    userDevices: (userId: number) =>
      request<TLauncherUserDevice[]>('GET', `/api/launcher/admin/users/${userId}/devices`),

    /** Блок «Лаунчер» в карточке: устройства + последняя сессия одним запросом */
    userProfile: (userId: number) =>
      request<TLauncherUserProfile>('GET', `/api/launcher/admin/users/${userId}/launcher`),

    multiaccounts: () =>
      request<TLauncherMultiaccount[]>('GET', '/api/launcher/admin/multiaccounts'),

    multiaccSuspicions: (status: 'new' | 'dismissed' | 'confirmed' = 'new') =>
      request<TLauncherMultiaccSuspicion[]>('GET', `/api/launcher/admin/multiacc-suspicions?status=${status}`),

    resolveMultiaccSuspicion: (id: number, status: 'dismissed' | 'confirmed') =>
      request<{ ok: true }>('POST', `/api/launcher/admin/multiacc-suspicions/${id}/resolve`, { status }),

    notifications: {
      list: () =>
        request<TLauncherNotification[]>('GET', '/api/launcher/admin/notifications'),
      create: (data: {
        title: string; body_html: string; type: 'push' | 'modal';
        severity?: 'error' | 'warning' | 'success' | 'info';
        target: 'all' | 'branch' | 'users'; target_branch?: string;
        target_user_ids?: number[]; expires_at?: string | null;
      }) =>
        request<{ ok: boolean; id: number }>('POST', '/api/launcher/admin/notifications', data),
      /** Кто получил и закрыл: счётчики не отвечают на вопрос «а дошло ли мне» */
      recipients: (id: number) =>
        request<TLauncherNotificationRecipient[]>('GET', `/api/launcher/admin/notifications/${id}/recipients`),
      deactivate: (id: number) =>
        request<{ ok: boolean }>('POST', `/api/launcher/admin/notifications/${id}/deactivate`),
      remove: (id: number) =>
        request<{ ok: boolean }>('DELETE', `/api/launcher/admin/notifications/${id}`),
    },

    gameParams: {
      get: () =>
        request<{ params: TGameParamsStructure; defaults: TGameParamsStructure; updated_at: string | null }>(
          'GET', '/api/launcher/admin/game-params'),
      save: (params: TGameParamsStructure) =>
        request<{ ok: boolean }>('PUT', '/api/launcher/admin/game-params', { params }),
    },

    baseline: {
      status: () => request<TBaselineStatusInfo>('GET', '/api/launcher/admin/baseline/status'),
      pollSteam: () => request<TBaselineStatusInfo['steam']>('POST', '/api/launcher/admin/baseline/steam/poll'),
      diff: () =>
        request<{ draft_id: number; items: TBaselineDiffEntry[] }>('GET', '/api/launcher/admin/baseline/draft/diff'),
      acceptedTree: (opts: { prefix?: string; q?: string }) =>
        request<{ items: TBaselineTreeNode[]; truncated: boolean }>('GET',
          `/api/launcher/admin/baseline/accepted/tree?prefix=${encodeURIComponent(opts.prefix ?? '')}&q=${encodeURIComponent(opts.q ?? '')}`),
      setStates: (paths: string[], state: TBaselineFileState) =>
        request<{ ok: boolean; affected: number }>('POST', '/api/launcher/admin/baseline/draft/states', { paths, state }),
      /** Папка целиком — через prefix, чтобы не гонять тысячи путей */
      setAcceptedStates: (target: { paths?: string[]; prefix?: string }, state: TBaselineFileState) =>
        request<{ ok: boolean; affected: number }>('POST', '/api/launcher/admin/baseline/accepted/states', { ...target, state }),
      accept: () =>
        request<{ ok: boolean; baseline_rev: number }>('POST', '/api/launcher/admin/baseline/draft/accept'),
      discard: () => request<{ ok: boolean }>('DELETE', '/api/launcher/admin/baseline/draft'),
      players: (forbiddenOnly: boolean) =>
        request<TGameCheckPlayer[]>('GET', `/api/launcher/admin/baseline/players${forbiddenOnly ? '?forbidden=1' : ''}`),
      userVerdict: (userId: number) =>
        request<TGameCheckVerdict | null>('GET', `/api/launcher/admin/users/${userId}/game-check`),

      rules: {
        list:   () => request<{ items: TBaselineRule[] }>('GET', '/api/launcher/admin/baseline/rules'),
        save:   (rule: Partial<TBaselineRule> & { pattern: string }) =>
          request<{ ok: boolean; item: TBaselineRule }>('PUT', '/api/launcher/admin/baseline/rules', rule),
        remove: (ruleId: number) =>
          request<{ ok: boolean }>('DELETE', `/api/launcher/admin/baseline/rules/${ruleId}`),
      },
    },
  },

  // ── Справочник ошибок (errors.manage / errors.reports.view) ─────────
  errors: {
    codes: {
      /** Список карточек: открыт и правящим тексты, и смотрящим отчёты */
      list: (params: { q?: string; source?: TErrorSource | '' } = {}) => {
        const qs = new URLSearchParams();
        if (params.q)      qs.set('q', params.q);
        if (params.source) qs.set('source', params.source);
        return request<TErrorCodeRow[]>('GET', `/api/errors/admin/codes?${qs}`);
      },

      /** Upsert по коду: правка текстов и заведение новой карточки — одна ручка */
      save: (data: TErrorCodePayload) =>
        request<{ ok: boolean }>('PUT', '/api/errors/admin/codes', data),

      remove: (code: string) =>
        request<{ ok: boolean }>('DELETE', `/api/errors/admin/codes/${encodeURIComponent(code)}`),

      /**
       * Полная карточка под правом tickets.view, а не errors.manage:
       * подсказка по коду нужна оператору в переписке тикета.
       */
      get: (code: string) =>
        request<TErrorCode>('GET', `/api/errors/codes/${encodeURIComponent(code)}`),
    },

    reports: {
      list: (params: {
        code?: string; source?: TErrorSource | ''; caseNo?: string;
        ticketId?: number; limit?: number; offset?: number;
      } = {}) => {
        const qs = new URLSearchParams();
        if (params.code)     qs.set('code',      params.code);
        if (params.source)   qs.set('source',    params.source);
        if (params.caseNo)   qs.set('case',      params.caseNo);
        if (params.ticketId) qs.set('ticket_id', String(params.ticketId));
        qs.set('limit',  String(params.limit  ?? 50));
        qs.set('offset', String(params.offset ?? 0));
        return request<{ items: TErrorReport[]; total: number }>(
          'GET', `/api/errors/admin/reports?${qs}`);
      },
    },
  },

  // ── Статистика: воронки и графики (право stats.view) ────────────────
  analytics: {
    summary: (tab: 'site' | 'donate' | 'cabinet' | 'launcher', days: number) =>
      request<TStatsSummary>('GET', `/api/analytics/summary?tab=${tab}&days=${days}`),
  },

  // ── Финансовая аналитика AP (право finance.view) ────────────────────
  finance: {
    /** Live-блок «Сегодня» (агрегатор обновляет каждые ~90 с) */
    today: () =>
      request<TFinanceToday>('GET', '/api/finance/today'),

    /** Полный бандл периода: ?preset=7d|30d|90d|year|all либо from/to */
    period: (params: TFinancePeriodParams) =>
      request<TFinancePeriodData>('GET', `/api/finance/period?${financeQs(params)}`),

    /** Ряд динамики + события-пины (для графика) */
    revenueSeries: (params: TFinancePeriodParams) =>
      request<{
        range: { from: string; to: string };
        granularity: 'day' | 'month';
        series: TFinanceSeriesPoint[];
        prev_series: TFinanceSeriesPoint[];
        events: TFinanceEvent[];
      }>('GET', `/api/finance/revenue-series?${financeQs(params)}`),

    /** Живая лента операций (история; live — через сокет finance:op) */
    feed: (params: { cursor?: string; types?: string; limit?: number }) => {
      const q = new URLSearchParams();
      if (params.cursor) q.set('cursor', params.cursor);
      if (params.types)  q.set('types',  params.types);
      if (params.limit)  q.set('limit',  String(params.limit));
      return request<TFinanceFeedPage>('GET', `/api/finance/feed?${q}`);
    },

    events: {
      list: (params?: { from?: string; to?: string }) => {
        const q = new URLSearchParams();
        if (params?.from) q.set('from', params.from);
        if (params?.to)   q.set('to',   params.to);
        return request<{ data: TFinanceEvent[] }>('GET', `/api/finance/events?${q}`);
      },
      create: (data: { date: string; title: string; description?: string | null }) =>
        request<{ ok: boolean; id: number }>('POST', '/api/finance/events', data),
      update: (id: number, data: { date?: string; title?: string; description?: string | null }) =>
        request<{ ok: boolean }>('PATCH', `/api/finance/events/${id}`, data),
      remove: (id: number) =>
        request<{ ok: boolean }>('DELETE', `/api/finance/events/${id}`),
    },

    /** Поиск игрока для ручной операции (право finance.adjust) */
    playerLookup: (q: string) =>
      request<{ data: TFinancePlayerMatch[] }>(
        'GET', `/api/finance/player-lookup?q=${encodeURIComponent(q)}`),

    /** Ручное списание AP у игрока (право finance.adjust) */
    manualTake: (data: { playerid: string; amount: number; reason: string }) =>
      request<{ ok: boolean; player_name: string | null; new_balance: number }>(
        'POST', '/api/finance/manual-take', data),

    /** Статус собранной истории + прогресс идущей пересборки */
    backfillStatus: () =>
      request<TFinanceBackfillStatus>('GET', '/api/finance/backfill-status'),

    /** Запуск полной пересборки истории (право finance.adjust) */
    rebuild: () =>
      request<{ ok: boolean }>('POST', '/api/finance/rebuild'),

    /** Карта товаров: все позиции rollup + активные переопределения */
    productMap: {
      list: () =>
        request<{ data: TFinanceProductMapItem[] }>('GET', '/api/finance/product-map'),
      /** Upsert переопределений; объединение = одинаковое имя у нескольких ключей */
      save: (items: { source: string; product_key: string; display_name: string; category?: string | null }[]) =>
        request<{ ok: boolean; needs_rebuild: boolean }>('PUT', '/api/finance/product-map', { items }),
      /** Сброс переопределения к кодовой карте */
      remove: (source: string, key: string) =>
        request<{ ok: boolean; needs_rebuild: boolean }>(
          'DELETE', `/api/finance/product-map?source=${encodeURIComponent(source)}&key=${encodeURIComponent(key)}`),
    },

    /** Когорты плательщиков по месяцу первого платежа (кэш на сервере) */
    cohorts: () =>
      request<{ data: TFinanceCohort[] }>('GET', '/api/finance/cohorts'),

    /** Как быстро тратят AP после пополнения (кэш на сервере) */
    spendSpeed: () =>
      request<TFinanceSpendSpeed>('GET', '/api/finance/spend-speed'),

    /** Финансовый профиль игрока (живые запросы к игровой БД) */
    player: (playerid: string) =>
      request<TFinancePlayerProfile>('GET', `/api/finance/player/${encodeURIComponent(playerid)}`),

    /** Балансы игроков по валюте (AP / APb / конфеты) с пометкой исключённых */
    balances: (opts: {
      currency?: TFinanceCurrency; offset?: number; q?: string; limit?: number;
    } = {}) => {
      const p = new URLSearchParams({
        currency: opts.currency ?? 'ap',
        offset: String(opts.offset ?? 0),
        limit: String(opts.limit ?? 50),
      });
      if (opts.q?.trim()) p.set('q', opts.q.trim());
      return request<{ data: TFinanceBalanceRow[]; total: number }>(
        'GET', `/api/finance/balances?${p}`);
    },

    /** Отчёт по месяцам за всю историю */
    monthlyReport: () =>
      request<{ data: TFinanceMonthlyRow[] }>('GET', '/api/finance/monthly-report'),

    /** Кто сколько сжёг за период: APb или конфеты */
    burnPlayers: (opts: {
      kind: 'apb' | 'candy'; from: string; to: string;
      offset?: number; q?: string; limit?: number;
    }) => {
      const p = new URLSearchParams({
        kind: opts.kind, from: opts.from, to: opts.to,
        offset: String(opts.offset ?? 0),
        limit: String(opts.limit ?? 50),
      });
      if (opts.q?.trim()) p.set('q', opts.q.trim());
      return request<{
        data: TFinanceBurnPlayer[]; total: number; unavailable?: boolean;
      }>('GET', `/api/finance/burn-players?${p}`);
    },

    /** Исключить игрока из аналитики / вернуть обратно (finance.adjust) */
    exclude: (playerid: string, reason?: string) =>
      request<{ ok: boolean; needs_rebuild: boolean }>(
        'POST', '/api/finance/exclusions', { playerid, reason }),
    unexclude: (playerid: string) =>
      request<{ ok: boolean; needs_rebuild: boolean }>(
        'DELETE', `/api/finance/exclusions?playerid=${encodeURIComponent(playerid)}`),

    /** История выдач/снятий AP конкретного админа (пагинация по id) */
    manualOpsHistory: (
      adminid: string,
      opts: { cursor?: string | null; dir?: 'all' | 'add' | 'take'; q?: string; limit?: number } = {},
    ) => {
      const p = new URLSearchParams({ adminid, limit: String(opts.limit ?? 50) });
      if (opts.cursor) p.set('cursor', opts.cursor);
      if (opts.dir && opts.dir !== 'all') p.set('dir', opts.dir);
      if (opts.q?.trim()) p.set('q', opts.q.trim());
      return request<{
        data: TFinanceManualOp[];
        nextCursor: string | null;
        totals: { add: number; take: number; ops: number } | null;
      }>('GET', `/api/finance/manual-ops/history?${p}`);
    },
  },

  // ── Статистика онлайна (право online.view) ──────────────────────────
  online: {
    /** Полный бандл периода: ?preset=7d|30d|90d|year|all либо from/to */
    period: (params: TOnlinePeriodParams) =>
      request<TOnlinePeriodData>('GET', `/api/online/period?${financeQs(params)}`),

    /** Блок «Сегодня»: живой онлайн + часовые кривые (live — сокет online:update) */
    now: () =>
      request<TOnlineToday>('GET', '/api/online/now'),

    /** Отчёт по месяцам за всю историю */
    monthlyReport: () =>
      request<{ data: TOnlineMonthlyRow[] }>('GET', '/api/online/monthly-report'),

    /** Статус собранной истории (плашка первичной сборки) */
    backfillStatus: () =>
      request<TOnlineBackfillStatus>('GET', '/api/online/backfill-status'),
  },

  // ── Календарь событий и дежурств (просмотр — dashboard.view) ─────────
  // ── Главная: сводка KPI и счётчики вкладок ────────────────────────
  dashboard: {
    /** Ряд KPI: в ответе только блоки, доступные по правам (без права — ключа нет) */
    summary: () => request<TDashboardSummary>('GET', '/api/dashboard/summary'),
    /** Вкладка «Поддержка»: закрыто сегодня, медианы ответа (общая и ваша) */
    support: () => request<TDashboardSupportStats>('GET', '/api/dashboard/support'),
    /** Вкладка «Из игры»: открыто, просрочено по SLA игровых тикетов, ответ, инциденты */
    game: () => request<TDashboardGameStats>('GET', '/api/dashboard/game'),
  },

  calendar: {
    /** Всё за месяц одним ответом: типы, события, план, факт, дыры в плане */
    month: (ym: string) =>
      request<TCalendarMonth>('GET', `/api/calendar/month?ym=${ym}`),

    /** Метки событий для графиков (online.view / finance.view тоже достаточно) */
    markers: (from: string, to: string) =>
      request<{ data: TCalendarMarker[] }>('GET', `/api/calendar/markers?from=${from}&to=${to}`),

    eventTypes: {
      list: () =>
        request<{ data: TCalendarEventType[] }>('GET', '/api/calendar/event-types'),
      create: (data: { name: string; color: string; sort?: number }) =>
        request<{ ok: boolean; id: number }>('POST', '/api/calendar/event-types', data),
      update: (id: number, data: { name?: string; color?: string; sort?: number; active?: boolean }) =>
        request<{ ok: boolean }>('PATCH', `/api/calendar/event-types/${id}`, data),
      remove: (id: number) =>
        request<{ ok: boolean }>('DELETE', `/api/calendar/event-types/${id}`),
    },

    events: {
      /** start_at / end_at — «настенное» время команды: 'YYYY-MM-DD HH:mm' */
      create: (data: {
        type_id: number | null; start_at: string; end_at?: string | null;
        title: string; comment?: string | null;
      }) => request<{ ok: boolean; id: number }>('POST', '/api/calendar/events', data),
      update: (id: number, data: {
        type_id?: number | null; start_at?: string; end_at?: string | null;
        title?: string; comment?: string | null;
      }) => request<{ ok: boolean }>('PATCH', `/api/calendar/events/${id}`, data),
      remove: (id: number) =>
        request<{ ok: boolean }>('DELETE', `/api/calendar/events/${id}`),
    },

    templates: {
      list: () =>
        request<{ data: TShiftTemplate[] }>('GET', '/api/calendar/templates'),
      create: (data: TShiftTemplatePayload) =>
        request<{ ok: boolean; id: number }>('POST', '/api/calendar/templates', data),
      update: (id: number, data: TShiftTemplatePayload) =>
        request<{ ok: boolean }>('PATCH', `/api/calendar/templates/${id}`, data),
      remove: (id: number) =>
        request<{ ok: boolean }>('DELETE', `/api/calendar/templates/${id}`),
      /** Пересобрать план из шаблонов (ручные правки не затираются) */
      regenerate: () =>
        request<{ ok: boolean; inserted: number }>('POST', '/api/calendar/plan/regenerate'),
    },

    plan: {
      create: (data: {
        user_id: number; date: string; start_time: string; end_time: string; note?: string | null;
      }) => request<{ ok: boolean; id: number }>('POST', '/api/calendar/plan', data),
      update: (id: number, data: { start_time?: string; end_time?: string; note?: string | null }) =>
        request<{ ok: boolean }>('PATCH', `/api/calendar/plan/${id}`, data),
      remove: (id: number) =>
        request<{ ok: boolean }>('DELETE', `/api/calendar/plan/${id}`),
    },
  },

  // ── Антимультиакк: связи аккаунтов (multiacc.view / multiacc.manage) ──
  multiacc: {
    catalog: () =>
      request<{ signals: TMultiaccSignalMeta[]; config: TMultiaccConfig }>('GET', '/api/multiacc/catalog'),

    /** Виджет на главной: сколько связей ждёт решения + 5 свежих */
    summary: () =>
      request<TMultiaccSummary>('GET', '/api/multiacc/summary'),

    /** Карточка игрока, вкладка «Связи» */
    userLinks: (userId: number) =>
      request<TMultiaccUserLinks>('GET', `/api/multiacc/users/${userId}/links`),

    list: (params: { status?: TMultiaccLinkStatus[]; user_id?: number; min_score?: number; would_autoban?: boolean; page?: number; limit?: number }) => {
      const q = new URLSearchParams();
      if (params.status?.length) q.set('status', params.status.join(','));
      if (params.user_id) q.set('user_id', String(params.user_id));
      if (params.min_score != null) q.set('min_score', String(params.min_score));
      if (params.would_autoban) q.set('would_autoban', '1');
      if (params.page) q.set('page', String(params.page));
      if (params.limit) q.set('limit', String(params.limit));
      return request<{ items: TMultiaccLink[]; total: number; page: number; limit: number; config: TMultiaccConfig }>('GET', `/api/multiacc/links?${q}`);
    },

    get: (id: number) =>
      request<TMultiaccLink>('GET', `/api/multiacc/links/${id}`),

    resolve: (id: number, status: 'confirmed' | 'dismissed', note?: string, linkType?: TMultiaccLinkType | null) =>
      request<{ ok: boolean }>('POST', `/api/multiacc/links/${id}/resolve`, { status, note, link_type: linkType ?? undefined }),
    linkLogins: (id: number) =>
      request<TMultiaccLinkLogins>('GET', `/api/multiacc/links/${id}/logins`),
    userLogins: (userId: number) =>
      request<TMultiaccUserLogins>('GET', `/api/multiacc/users/${userId}/logins`),
    forgive: (id: number) =>
      request<{ ok: boolean }>('POST', `/api/multiacc/links/${id}/forgive`),
    reopen: (id: number) =>
      request<{ ok: boolean }>('POST', `/api/multiacc/links/${id}/reopen`),
    /** «Отложить»: остаётся на проверке с заметкой */
    note: (id: number, note: string | null) =>
      request<{ ok: boolean }>('POST', `/api/multiacc/links/${id}/note`, { note }),
    /** Что станет со связью, если отключить или вернуть улики (ничего не сохраняет) */
    previewSignals: (id: number, changes: TMultiaccSignalChange[]) =>
      request<TMultiaccSignalsPreview>('POST', `/api/multiacc/links/${id}/signals/preview`, { changes }),
    applySignals: (id: number, changes: TMultiaccSignalChange[], note?: string) =>
      request<{ ok: boolean; rescored: number }>('POST', `/api/multiacc/links/${id}/signals`, { changes, note }),
    /** Покомпонентное сравнение ПК двух аккаунтов связи */
    hardware: (id: number) =>
      request<TMultiaccHardware>('GET', `/api/multiacc/links/${id}/hardware`),
    /** Когда каждый из пары последний раз пользовался значением улики (по id улики) */
    usage: (id: number) =>
      request<TMultiaccUsage>('GET', `/api/multiacc/links/${id}/usage`),
    ignored: {
      list: () => request<TMultiaccIgnoredValue[]>('GET', '/api/multiacc/ignored'),
      remove: (id: number) => request<{ ok: boolean }>('DELETE', `/api/multiacc/ignored/${id}`),
    },
    forgiveUser: (userId: number) =>
      request<{ ok: boolean }>('POST', `/api/multiacc/users/${userId}/forgive`),
    rescore: () =>
      request<{ ok: boolean; links: number }>('POST', '/api/multiacc/rescore'),

    settings: () =>
      request<TMultiaccSettings>('GET', '/api/multiacc/settings'),
    patchSettings: (values: Record<string, string>) =>
      request<{ ok: boolean }>('PATCH', '/api/multiacc/settings', values),

    exemptions: {
      list: () => request<TMultiaccExemption[]>('GET', '/api/multiacc/exemptions'),
      add: (user_id: number, note: string | null) =>
        request<{ ok: boolean }>('POST', '/api/multiacc/exemptions', { user_id, note }),
      remove: (id: number) =>
        request<{ ok: boolean }>('DELETE', `/api/multiacc/exemptions/${id}`),
    },

    devices: () =>
      request<TLauncherMultiaccount[]>('GET', '/api/multiacc/devices'),
  },

  // ── Блокировки: сайт (service) и игра (ap_game_bans → BattlEye) ──────
  bans: {
    list: (params: { kind: 'site' | 'game'; active?: boolean; q?: string; page?: number; limit?: number }) => {
      const q = new URLSearchParams();
      q.set('kind', params.kind);
      if (params.active != null) q.set('active', params.active ? '1' : '0');
      if (params.q) q.set('q', params.q);
      if (params.page) q.set('page', String(params.page));
      if (params.limit) q.set('limit', String(params.limit));
      return request<{ items: TBanRow[]; total: number; page: number; limit: number }>('GET', `/api/bans?${q}`);
    },
    /** Баны пользователя (сайт + игра) для карточки */
    ofUser: (userId: number) =>
      request<{ user: TMultiaccUserBrief | null; site: TBanRow | null; game: TBanRow[] }>('GET', `/api/bans/users/${userId}`),
    /**
     * Бан: site — ограничение service в auth; game — ap_game_bans + RCON addBan;
     * wait_steam — отложенный до привязки Steam (after: что применить потом)
     */
    create: (data: { kind: TBanKind; after?: TBanAfter; user_id?: number; steam_id?: string; reason: string; reason_public?: string; expires_at?: string | null; link_id?: number }) =>
      request<{ ok: boolean; done: TBanDone[]; errors: string[] }>('POST', '/api/bans', data),
    /** «Бан обоих»: общие причина/срок/текст, у каждого аккаунта свой вид бана */
    createBulk: (data: { items: { user_id: number; kind: TBanKind; after?: TBanAfter }[]; reason: string; reason_public?: string; expires_at?: string | null; link_id?: number }) =>
      request<{ ok: boolean; results: { user_id: number; done: TBanDone[]; errors: string[] }[] }>('POST', '/api/bans/bulk', data),
    /** Соединение с BattlEye и последняя сверка — статус-строка страницы */
    syncStatus: () =>
      request<TBanSyncStatus>('GET', '/api/bans/game/status'),
    removeSite: (userId: number, forgive?: boolean) =>
      request<{ ok: boolean }>('DELETE', `/api/bans/site/${userId}${forgive ? '?forgive=1' : ''}`),
    removeGame: (id: number, forgive?: boolean) =>
      request<{ ok: boolean }>('DELETE', `/api/bans/game/${id}${forgive ? '?forgive=1' : ''}`),
    /** Повторить синхронизацию с BattlEye (addBan/removeBan) */
    resync: () =>
      request<{ ok: boolean; synced: number; errors: number; imported: number; removed: number }>('POST', '/api/bans/game/resync'),
  },

  // ── Вкладка «Сервер» (RCON) ───────────────────────────────────────
  rcon: {
    /** Снимок онлайна и новые строки журнала после after (курсор ленты) */
    live: (after: number) => request<TRconLive>('GET', `/api/rcon/live?after=${after}`),
    log: (params: { from: string; to: string; types?: TRconLogType[]; channel?: TRconChannel | ''; q?: string; page?: number }) => {
      const q = new URLSearchParams({ from: params.from, to: params.to });
      if (params.types) q.set('types', params.types.join(','));
      if (params.channel) q.set('channel', params.channel);
      if (params.q) q.set('q', params.q);
      if (params.page) q.set('page', String(params.page));
      return request<{ items: TRconLogRow[]; total: number; counts: Partial<Record<TRconLogType, number>>; page: number; page_size: number; can_see_chat: boolean }>('GET', `/api/rcon/log?${q}`);
    },
    /** source: rcon — сессии панели, game — игровой журнал входов logs_game_session */
    sessions: (date?: string, source: 'rcon' | 'game' = 'rcon') => {
      const q = new URLSearchParams();
      if (date) q.set('date', date);
      if (source !== 'rcon') q.set('source', source);
      return request<{ date: string; source: 'rcon' | 'game'; hours: { hour: number; players: number }[]; items: TRconSession[] }>('GET', `/api/rcon/sessions${q.toString() ? `?${q}` : ''}`);
    },
    sessionsRange: (from: string, to: string, source: 'rcon' | 'game', q = '') => {
      const p = new URLSearchParams({ from, to });
      if (source !== 'rcon') p.set('source', source);
      if (q) p.set('q', q);
      return request<TRconSessionsRange>('GET', `/api/rcon/sessions/range?${p}`);
    },
    playerHistory: (q: string) => request<TRconPlayerHistory>('GET', `/api/rcon/sessions/player?q=${encodeURIComponent(q)}`),
    guard: () => request<TRconGuard>('GET', '/api/rcon/guard'),
    reconcile: () => request<{ ok: boolean; result: { synced: number; errors: number; imported: number; removed: number } }>('POST', '/api/rcon/guard/reconcile'),
    checkBans: () => request<{ ok: boolean; count: number }>('POST', '/api/rcon/guard/check'),
    kick: (players: { key: string; guid: string | null }[], reason: string) =>
      request<{ ok: boolean; count: number }>('POST', '/api/rcon/kick', { players, reason }),
    kickAll: (reason: string, confirm: string) =>
      request<{ ok: boolean; count: number }>('POST', '/api/rcon/kick-all', { reason, confirm }),
    lock: () => request<{ ok: boolean }>('POST', '/api/rcon/lock'),
    unlock: () => request<{ ok: boolean }>('POST', '/api/rcon/unlock'),
    watchList: () => request<{ items: TRconWatch[] }>('GET', '/api/rcon/watch'),
    watchAdd: (data: { steam_id: string; name?: string | null; reason: string; notify: 'tg' | 'panel'; days: number | null }) =>
      request<{ ok: boolean; id: number }>('POST', '/api/rcon/watch', data),
    watchUpdate: (id: number, data: { reason?: string; notify?: 'tg' | 'panel'; days?: number | null }) =>
      request<{ ok: boolean }>('PATCH', `/api/rcon/watch/${id}`, data),
    watchRemove: (id: number) => request<{ ok: boolean }>('DELETE', `/api/rcon/watch/${id}`),
    actions: (params: { from: string; to: string; actor?: string; action?: string; page?: number }) => {
      const q = new URLSearchParams({ from: params.from, to: params.to });
      if (params.actor) q.set('actor', params.actor);
      if (params.action) q.set('action', params.action);
      if (params.page) q.set('page', String(params.page));
      return request<{ items: TRconAction[]; total: number; page: number; page_size: number }>('GET', `/api/rcon/actions?${q}`);
    },
    settings: () => request<TRconSettings>('GET', '/api/rcon/settings'),
    saveSettings: (data: TRconSettings) => request<TRconSettings>('PUT', '/api/rcon/settings', data),
  },
};

// ── Антимультиакк ────────────────────────────────────────────────────

export type TMultiaccLinkStatus = 'none' | 'review' | 'confirmed' | 'dismissed' | 'banned';

export interface TMultiaccSignalMeta {
  type: string; label: string; hint: string; defaultWeight: number; weight: number;
  source: 'launcher' | 'site' | 'game' | 'forum';
}

export interface TMultiaccConfig {
  review_threshold: number; ban_threshold: number; banned_factor: number; signal_ttl_days: number;
  autoban_enabled: boolean; sources: { launcher: boolean; site: boolean; game: boolean; forum?: boolean };
  weights: Record<string, number>;
}

export interface TMultiaccUserBrief {
  id: number; login?: string | null; display_name?: string | null; avatar_url?: string | null;
  status?: string | null; steam_id?: string | null; steam_name?: string | null;
}

/** Почему улика не в зачёте: отключена в связи / у всех / прощена / устарела */
export type TMultiaccSignalOff = 'ignored_link' | 'ignored_global' | 'forgiven' | 'expired' | 'unknown' | null;

export interface TMultiaccSignal {
  id: number; type: string; label: string; value: string; detail: string | null;
  first_seen: string; last_seen: string; counted: boolean; weight: number;
  off?: TMultiaccSignalOff;
  /** Чей вход принёс улику и чей это ПК или Steam; null — неизвестно (улики до журнала входов) */
  actor_user_id?: number | null;
  owner_user_id?: number | null;
}

export type TMultiaccLinkType = 'account_transfer' | 'twink' | 'shared_pc' | 'shared_disk' | 'shared_network';
export interface TMultiaccLinkTypeHint {
  type: TMultiaccLinkType;
  /** Кто за ПК и чьим аккаунтом пользовались — если удалось понять по журналу */
  pc_owner: number | null; account_of: number | null;
  reason: string;
}

export interface TLauncherLogin {
  id: number; user_id: number; device_id: number | null; kind: 'fingerprint' | 'play';
  steam_id: string | null; active_steam_id: string | null; blocked: number;
  ip_address: string | null; launcher_version: string | null; computer_name: string | null; created_at: string;
}

/** GET /api/multiacc/links/:id/logins — кто, с какого ПК и журнал пары */
export interface TMultiaccLinkLogins {
  homes: Record<string, number | null>;
  pcs: {
    device_id: number; computer_name: string | null;
    owner_user_id: number | null; owner: TMultiaccUserBrief | null;
    rows: { user_id: number; logins: number; first_at: string; last_at: string }[];
  }[];
  logins: TLauncherLogin[];
}

/** GET /api/multiacc/users/:id/logins — ПК аккаунта и журнал входов */
export interface TMultiaccUserLogins {
  home_device_id: number | null;
  devices: {
    device_id: number; computer_name: string | null; logins: number; first_at: string; last_at: string;
    home: boolean; own: boolean; owner: TMultiaccUserBrief | null;
  }[];
  logins: TLauncherLogin[];
}

/** count — учитывать; link — не учитывать в этой связи; global — ни у кого */
export type TMultiaccSignalMode = 'count' | 'link' | 'global';
export interface TMultiaccSignalChange { signal_id: number; mode: TMultiaccSignalMode }

export interface TMultiaccSignalsPreview {
  before: { score: number; raw: number; status: TMultiaccLinkStatus; types: string[] };
  after:  { score: number; raw: number; status: TMultiaccLinkStatus; types: string[] };
  other_links: number;
}

export interface TMultiaccIgnoredValue {
  id: number; type: string; label: string; value: string; note: string | null;
  created_at: string; created_by: string | null;
}

export interface TMultiaccHwRow {
  key: string; label: string; kind: 'disk' | 'hardware' | 'network' | 'info';
  a: string | null; b: string | null; match: 'same' | 'diff' | 'na';
}

export interface TMultiaccHwComparison {
  basis: 'same_device' | 'partial' | 'latest';
  a: { device_id: number; computer_name: string | null; first_seen: string | null; last_seen: string | null };
  b: { device_id: number; computer_name: string | null; first_seen: string | null; last_seen: string | null };
  rows: TMultiaccHwRow[];
  matched: number; total: number; approx: boolean;
  verdict: 'same_pc' | 'same_disk_other_pc' | 'different' | 'mixed';
}

export interface TMultiaccHardware { user_a: number; user_b: number; comparisons: TMultiaccHwComparison[] }

export interface TMultiaccUsage { user_a: number; user_b: number; signals: Record<number, { a: string | null; b: string | null }> }

export interface TMultiaccLink {
  id: number;
  user_a: TMultiaccUserBrief; user_b: TMultiaccUserBrief;
  /** С точки зрения открытой карточки — «другой» аккаунт (только в userLinks) */
  other_user: TMultiaccUserBrief | null;
  score: number; raw_score: number; banned_partner: boolean;
  status: TMultiaccLinkStatus; would_autoban: boolean;
  forgiven_at: string | null; first_seen: string; last_seen: string;
  resolved_by: string | null; resolved_at: string | null; note: string | null;
  /** Типы сигналов в зачёте */
  types: string[];
  signals: TMultiaccSignal[];
  /** Тип, с которым админ подтвердил связь */
  link_type?: TMultiaccLinkType | null;
  /** Подсказка системы по журналу входов */
  link_type_hint?: TMultiaccLinkTypeHint | null;
}

export interface TMultiaccUserLinks {
  config: TMultiaccConfig;
  exempt: boolean;
  links: TMultiaccLink[];
  by_type: {
    type: string; label: string; hint: string; weight: number;
    entries: {
      value: string; detail: string | null; last_seen: string;
      users: (TMultiaccUserBrief & { counted: boolean; link_id: number; link_status: TMultiaccLinkStatus })[];
    }[];
  }[];
}

export interface TMultiaccSummary {
  review: number; would_autoban: number;
  counts: Partial<Record<TMultiaccLinkStatus, number>>;
  latest: TMultiaccLink[];
  autoban_enabled: boolean;
}

export interface TMultiaccSettings {
  values: Record<string, string>;
  defaults: Record<string, string>;
  signals: { type: string; label: string; hint: string; source: 'launcher' | 'site' | 'game'; key: string; default_weight: number }[];
}

export interface TMultiaccExemption {
  id: number; user_id: number; note: string | null; added_by: number; created_at: string;
  user: TMultiaccUserBrief | null;
}

// ── Блокировки ───────────────────────────────────────────────────────

export interface TBanRow {
  kind: 'site' | 'game';
  id: number;
  user: TMultiaccUserBrief | null;
  steam_id: string | null;
  be_guid?: string | null;
  reason: string; reason_public: string | null;
  /** battleye — импортирован из бан-листа сервера */
  source?: 'manual' | 'multiacc' | 'battleye';
  link_id?: number | null;
  expires_at: string | null;
  created_by: string | null; created_at: string;
  removed_by?: string | null; removed_at?: string | null;
  active: boolean;
  /** Игра: состояние синхронизации с BattlEye */
  be_synced_at?: string | null; be_unsynced_at?: string | null; be_error?: string | null;
  /** Отложенный бан: ждёт привязки Steam, пока ничего не блокирует */
  awaiting_steam?: boolean;
  /** После привязки Steam заблокировать и сайт */
  await_site?: boolean;
  /** Срок от активации, мин; null — навсегда */
  await_minutes?: number | null;
  activated_at?: string | null;
}

export type TBanKind = 'site' | 'game' | 'both' | 'wait_steam';
export type TBanAfter = 'both' | 'game';
export type TBanDone = 'site' | 'game' | 'wait_steam';

export interface TBanSyncResult { synced: number; errors: number; imported: number; removed: number }

export interface TBanSyncStatus {
  configured: boolean;
  connected: boolean;
  connected_since: string | null;
  last_sync_at: string | null;
  last_reconcile_at: string | null;
  last_error: string | null;
  last_error_at: string | null;
  last_reconcile: TBanSyncResult | null;
  reconcile_interval_sec: number;
  /** Ждут отправки в BE / из них с ошибкой / ждут привязки Steam */
  pending: number; errors: number; awaiting: number;
}

// ─── Финансовая аналитика AP: типы ─────────────────────────────────────

export type TFinancePreset = '7d' | '30d' | '90d' | 'year' | 'all';

/** Результат поиска игрока для ручной операции. */
export interface TFinancePlayerMatch {
  playerid: string;
  name:     string | null;
  points:   number;
}

/** Прогресс идущей полной пересборки истории. */
export interface TFinanceRebuildState {
  running:    boolean;
  started_at: string;
  step:       string;
  done:       number;
  total:      number;
}

/** Позиция карты товаров: текущее имя из rollup + переопределение (если есть). */
export interface TFinanceProductMapItem {
  source: string;
  product_key: string;
  name: string | null;
  category: string | null;
  override_name: string | null;
  override_category: string | null;
  total: number;
  ops: number;
}

/** Когорта плательщиков: месяц первого платежа и выручка по месяцам жизни. */
export interface TFinanceCohort {
  month: string;
  players: number;
  total: number;
  months: { offset: number; revenue: number; payers: number }[];
}

/** Распределение задержки «пополнил → потратил». */
export interface TFinanceSpendSpeed {
  buckets: { label: string; count: number }[];
  median_minutes: number | null;
  payments: number;
  /** true — нужных таблиц нет в игровой БД, метрику посчитать нельзя */
  unavailable?: boolean;
}

/** Финансовый профиль игрока. */
export interface TFinancePlayerProfile {
  playerid: string;
  name: string | null;
  balance: number | null;
  total_paid: number;
  payments: number;
  avg_check: number;
  first_payment: string | null;
  last_payment: string | null;
  months: { month: string; amount: number }[];
  spend_categories: { source: string; ap: number; apb: number }[];
  top_products: { name: string; ap: number; apb: number; ops: number }[];
  recent: { title: string | null; ap: number; apb: number; at: string }[];
}

/** Валюта балансов: AP, бонусные APb, конфеты. */
export type TFinanceCurrency = 'ap' | 'apb' | 'candy';

/** Строка отчёта по месяцам. */
export interface TFinanceMonthlyRow {
  month: string;
  revenue: number;
  mom_pct: number | null;
  payments: number;
  avg_check: number | null;
  payers: number;
  new_payers: number;
  manual_add: number;
  manual_take: number;
  burn_ap: number;
  burn_apb: number;
  burn_candy: number;
  best_day: string | null;
  best_day_revenue: number;
}

/** Строка списка «кто сколько сжёг». */
export interface TFinanceBurnPlayer {
  playerid: string;
  name: string | null;
  amount: number;
  ops: number;
}

/** Баланс игрока в списке балансов. */
export interface TFinanceBalanceRow {
  playerid: string;
  name: string | null;
  points: number;
  excluded: boolean;
  exclude_reason: string | null;
}

/** Одна ручная операция админа (выдача/снятие AP). */
export interface TFinanceManualOp {
  id: number;
  at: string;
  playerid: string | null;
  player_name: string | null;
  /** Со знаком: положительное — выдача, отрицательное — снятие */
  amount: number;
  text: string | null;
}

/** Статус собранной финансовой истории. */
export interface TFinanceBackfillStatus {
  building:     boolean;
  from:         string | null;
  to:           string | null;
  days:         number;
  completed_at: string | null;
  rebuild:      TFinanceRebuildState | null;
}

export type TFinancePeriodParams =
  | { preset: TFinancePreset; from?: undefined; to?: undefined }
  | { preset?: undefined; from: string; to: string };

function financeQs(params: TFinancePeriodParams): string {
  const q = new URLSearchParams();
  if (params.preset) q.set('preset', params.preset);
  if (params.from)   q.set('from',   params.from);
  if (params.to)     q.set('to',     params.to);
  return q.toString();
}

export interface TFinanceSeriesPoint {
  day: string;
  revenue: number;
  revenue_new: number;
  revenue_old: number;
  payments: number;
  payers: number;
  manual_add: number;
  manual_take: number;
  burn_ap: number;
  burn_apb: number;
  invoices_created: number;
  invoices_paid: number;
}

export interface TFinanceSummary {
  revenue: number;
  revenue_new: number;
  revenue_old: number;
  payments: number;
  payers: number;
  avg_check: number | null;
  new_payers: number;
  returning_payers: number;
  repeat_share: number | null;
  manual_add: number;
  manual_take: number;
  burn_ap: number;
  burn_apb: number;
  burn_candy: number;
  emission: number;
  funnel: { created: number; paid: number; conversion: number | null };
  whales: { top_amount: number; share: number | null };
  prev: {
    revenue: number; payments: number; payers: number;
    revenue_pct: number | null; payments_pct: number | null; payers_pct: number | null;
  };
}

export interface TFinancePeriodData {
  range: { from: string; to: string };
  granularity: 'day' | 'month';
  summary: TFinanceSummary;
  series: TFinanceSeriesPoint[];
  prev_series: TFinanceSeriesPoint[];
  payment_systems: { system: string; revenue: number; payments: number }[];
  spend_categories: { source: 'lk' | 'exchange' | 'vip' | 'game'; ap: number; apb: number; ops: number }[];
  top_products: {
    sources: string[]; product_key: string; product_name: string | null;
    category: string | null; product_keys: string | null;
    ap: number; apb: number; ops: number;
  }[];
  top_payers: { playerid: string; name: string | null; amount: number; payments: number }[];
  check_histogram: { label: string; from: number; to: number | null; count: number }[];
  manual_by_admin: {
    adminid: string; admin_name: string | null;
    ap_add: number; ap_take: number; ops: number;
  }[];
  heatmap: { weekday: number; hour: number; revenue: number; payments: number }[];
  promocodes: {
    source: 'game' | 'site'; code: string; uses: number; ap_spent: number;
    discount: number; kind: 'discount' | 'gift';
  }[];
  /** Метки календаря событий с главной страницы (общие с разделом «Онлайн») */
  calendar_events: TCalendarMarker[];
  built_at: string;
}

export interface TFinanceToday {
  day: string;
  revenue: number;
  payments: number;
  payers: number;
  manual_add: number;
  manual_take: number;
  burn_ap: number;
  burn_apb: number;
  yesterday_same_time: number;
  yesterday_total: number;
  lastweek_same_time: number;
  vs_yesterday_pct: number | null;
  vs_lastweek_pct: number | null;
  spark_today: number[];
  spark_yesterday: number[];
  forecast: {
    month: string;
    month_to_date: number;
    projected: number;
    last_month: number;
    last_month_same_day: number;
    record_month: string | null;
    record_amount: number;
  };
  points_on_balances: number | null;
  apb_on_balances: number | null;
  candy_on_balances: number | null;
  burn_candy: number;
  updated_at: string;
}

export type TFinanceFeedType =
  | 'topup' | 'manual_add' | 'manual_take'
  | 'spend_lk' | 'spend_exchange' | 'spend_game' | 'spend_vip';

export interface TFinanceFeedItem {
  key: string;
  type: TFinanceFeedType;
  source: string;
  id: number;
  at: string;
  playerid: string | null;
  player_name: string | null;
  admin_id: string | null;
  admin_name: string | null;
  amount: number;
  apb: number;
  system: string | null;
  title: string | null;
}

export interface TFinanceFeedPage {
  data: TFinanceFeedItem[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface TFinanceEvent {
  id: number;
  date: string;
  title: string;
  description: string | null;
  created_by: number | null;
  created_by_login: string | null;
  created_at: string;
}

// ─── Статистика онлайна: типы ──────────────────────────────────────────

/** Пресеты и разбор периода у онлайна те же, что у финансов. */
export type TOnlinePreset = TFinancePreset;
export type TOnlinePeriodParams = TFinancePeriodParams;

export interface TOnlineSeriesPoint {
  /** 'YYYY-MM-DD' либо 'YYYY-MM' при granularity='month' */
  day: string;
  /** null — за день нет ни снимков, ни сессий: разрыв линии, а не ноль */
  avg_online: number | null;
  peak_online: number | null;
  dau: number;
  dau_new: number;
  returning_dau: number;
  wau: number;
  mau: number;
  new_players_game: number;
  new_users_site: number;
  sessions_count: number;
  avg_session_min: number;
  launcher_avg_active: number | null;
  launcher_avg_in_game: number | null;
}

export interface TOnlineForecastPoint {
  day: string;
  avg_online: number | null;
  dau: number | null;
}

export interface TOnlineSummary {
  avg_online: number | null;
  peak_online: number | null;
  avg_peak: number | null;
  uniques: number;
  dau_avg: number | null;
  new_players_game: number;
  new_users_site: number;
  sessions_count: number;
  avg_session_min: number | null;
  churn_14d: number;
  launcher_conversion: number | null;
  prev: {
    avg_online: number | null;
    peak_online: number | null;
    uniques: number;
    new_players_game: number;
    avg_online_pct: number | null;
    peak_online_pct: number | null;
    uniques_pct: number | null;
    new_players_pct: number | null;
  };
}

export interface TOnlineHeatmapCell {
  /** 0 = понедельник … 6 = воскресенье */
  weekday: number;
  hour: number;
  avg_online: number;
  peak_online: number;
}

export interface TOnlineCohort {
  /** 'YYYY-MM' — месяц первого входа */
  month: string;
  players: number;
  d1: number;
  d7: number;
  d30: number;
}

export interface TOnlineTopPlayer {
  pid: string;
  name: string | null;
  minutes: number;
  sessions: number;
  days: number;
}

export interface TOnlinePeriodData {
  range: { from: string; to: string };
  granularity: 'day' | 'month';
  summary: TOnlineSummary;
  series: TOnlineSeriesPoint[];
  prev_series: TOnlineSeriesPoint[];
  /** Прогноз на 14 дней (пусто при granularity='month') */
  forecast: TOnlineForecastPoint[];
  heatmap: TOnlineHeatmapCell[];
  cohorts: TOnlineCohort[];
  top_players: TOnlineTopPlayer[];
  /** Метки календаря за период (дочитываются мимо кэша бандла) */
  events: TCalendarMarker[];
  built_at: string;
}

/** Снимок «сейчас»: приходит и в /now, и в сокете online:update. */
export interface TOnlineNowSnapshot {
  /** null — поток от headless-клиента протух, числа нет */
  players_in_game: number | null;
  launchers_active: number;
  launchers_in_game: number;
}

export interface TOnlineToday {
  day: string;
  hour: number;
  now: TOnlineNowSnapshot;
  avg_today: number | null;
  peak_today: number | null;
  dau_today: number;
  new_players_today: number;
  /** Индекс = час 0–23, null = данных за час нет */
  spark_today: (number | null)[];
  spark_yesterday: (number | null)[];
  spark_lastweek: (number | null)[];
  spark_expected: (number | null)[];
  vs_yesterday_pct: number | null;
  vs_lastweek_pct: number | null;
  dau_yesterday: number;
  dau_lastweek: number;
  updated_at: string;
}

export interface TOnlineMonthlyRow {
  month: string;
  avg_online: number | null;
  peak_online: number | null;
  mom_pct: number | null;
  uniques: number;
  dau_avg: number | null;
  new_players_game: number;
  new_users_site: number;
  sessions_count: number;
  avg_session_min: number | null;
  best_day: string | null;
  best_day_peak: number | null;
}

export interface TOnlineBackfillStatus {
  building: boolean;
  from: string | null;
  to: string | null;
  days: number;
}

/** Событие online:update раз в минуту из onlineSampler. */
export interface TOnlineLiveSample extends TOnlineNowSnapshot {
  at: string;
}

// ─── Календарь событий и дежурств: типы ────────────────────────────────

export interface TCalendarEventType {
  id: number;
  name: string;
  color: string;
  sort: number;
  active: boolean;
}

export interface TCalendarEvent {
  id: number;
  type_id: number | null;
  type_name: string | null;
  type_color: string | null;
  /** «Настенное» время команды: 'YYYY-MM-DD HH:mm' */
  start_at: string;
  end_at: string | null;
  title: string;
  comment: string | null;
  created_by_login: string | null;
}

/** Компактная метка события для графиков онлайна и финансов. */
export interface TCalendarMarker {
  id: number;
  day: string;
  time: string;
  title: string;
  type_name: string | null;
  type_color: string | null;
}

export interface TShiftPlanRow {
  id: number;
  user_id: number;
  date: string;
  /** 'HH:mm'; end < start — смена уходит за полночь */
  start_time: string;
  end_time: string;
  note: string | null;
  template_id: number | null;
  is_override: boolean;
  planned_minutes: number;
  /** Сколько минут плана покрыто фактической сменой того же человека */
  fact_minutes: number;
}

export interface TShiftFact {
  id: number;
  user_id: number;
  started_at: string;
  ended_at: string | null;
  note: string | null;
}

export type TShiftRuleType = 'weekly' | 'rotation';

export interface TShiftInterval {
  /** 'HH:mm' */
  start: string;
  end: string;
}

export interface TShiftTemplate {
  id: number;
  user_id: number;
  rule_type: TShiftRuleType;
  /** 1 = понедельник … 7 = воскресенье (для rule_type='weekly') */
  weekdays: number[];
  cycle_on: number | null;
  cycle_off: number | null;
  anchor_date: string | null;
  rotation_offset: number;
  /** Перерыв = два интервала; end < start — переход через полночь */
  intervals: TShiftInterval[];
  active: boolean;
  note: string | null;
}

export interface TShiftTemplatePayload {
  user_id: number;
  rule_type: TShiftRuleType;
  weekdays?: number[];
  cycle_on?: number | null;
  cycle_off?: number | null;
  anchor_date?: string | null;
  rotation_offset?: number;
  intervals: TShiftInterval[];
  active?: boolean;
  note?: string | null;
}

// ─── Главная админки: сводка ──────────────────────────────────────────

export interface TDashboardOnline {
  now: number | null;
  slots: number | null;
  server_online: boolean | null;
  peak_24h: number | null;
  /** 'HH:00' — час пика за сутки */
  peak_hour: string | null;
  spark: (number | null)[];
}
export interface TDashboardSiteTickets { open: number; new_24h: number; closed_24h: number; spark: number[] }
export interface TDashboardGameTickets { open: number; closed_24h: number; response_sec: number | null; spark: number[] }
export interface TDashboardCharacters {
  new_24h: number; prev_24h: number; delta: number; civ: number; cop: number; med: number; spark: number[];
}
export interface TDashboardRegistrations {
  new_24h: number; prev_24h: number; delta: number; email_confirmed: number; steam_linked: number; spark: number[];
}
export interface TDashboardForum {
  /** Аккаунтов ЛК, связанных с форумом */
  linked: number; forum_users: number;
  new_24h: number; prev_24h: number; delta: number; spark: number[];
}
export interface TDashboardFinance {
  revenue_24h: number; prev_24h: number; delta_pct: number | null; payments: number; avg_check: number | null; spark: number[];
}
/** Ключ блока есть только при праве на его данные; null — источник недоступен */
export interface TDashboardSummary {
  online?: TDashboardOnline | null;
  site_tickets?: TDashboardSiteTickets | null;
  game_tickets?: TDashboardGameTickets | null;
  characters?: TDashboardCharacters | null;
  registrations?: TDashboardRegistrations | null;
  forum?: TDashboardForum | null;
  finance?: TDashboardFinance | null;
  tabs: { tasks_mine?: number };
  updated_at: string;
}
export interface TDashboardResponseToday { all_sec: number | null; mine_sec: number | null }
export interface TDashboardSupportStats { closed_today: number; response_today: TDashboardResponseToday }
export interface TDashboardGameStats {
  open: number; overdue: number; sla_minutes: number; closed_24h: number;
  response_today: TDashboardResponseToday;
  incidents?: { hour: number; day: number } | null;
}

export interface TCalendarMonth {
  month: string;
  types: TCalendarEventType[];
  events: TCalendarEvent[];
  plan: TShiftPlanRow[];
  facts: TShiftFact[];
  /** Ближайшие дни без плановых смен — бейдж «дыры в графике» */
  gap_days: string[];
}

// ─── CMS-страницы: типы ────────────────────────────────────────────────

export interface TCmsPage {
  id:              number;
  category_id:     number | null;
  category_slug:   string | null;
  category_title:  string | null;
  slug:            string;
  title:           string;
  description:     string | null;
  access:          'public' | 'auth';
  has_password:    boolean;
  is_unlisted:     boolean;
  is_active:       boolean;
  seo_title:       string | null;
  seo_description: string | null;
  seo_image:       string | null;
  sort_order:      number;
  visits_count:    number;
  created_by:      number;
  updated_by:      number | null;
  created_at:      string;
  updated_at:      string;
  /** Присутствуют в ответах get/create/update */
  body_json?:      string | null;
  body_html?:      string;
}

export interface TCmsPagePayload {
  category_id?:     number | null;
  slug:             string;
  title:            string;
  description?:     string | null;
  body_json?:       string | null;
  body_html?:       string;
  access?:          'public' | 'auth';
  password?:        string;
  is_unlisted?:     boolean;
  is_active?:       boolean;
  seo_title?:       string | null;
  seo_description?: string | null;
  seo_image?:       string | null;
  sort_order?:      number;
  /** id загруженных ассетов для привязки к странице */
  asset_ids?:       number[];
}

export interface TCmsCategory {
  id:             number;
  slug:           string;
  title:          string;
  description:    string | null;
  show_auto_list: boolean;
  sort_order:     number;
  is_active:      boolean;
  pages_count:    number;
  created_at:     string;
  updated_at:     string;
  body_json?:     string | null;
  body_html?:     string | null;
}

export interface TCmsCategoryPayload {
  slug:            string;
  title:           string;
  description?:    string | null;
  body_json?:      string | null;
  body_html?:      string | null;
  show_auto_list?: boolean;
  sort_order?:     number;
  is_active?:      boolean;
  asset_ids?:      number[];
}

export interface TCmsVisitRow {
  id:           number;
  user_id:      number | null;
  user_login:   string | null;
  ip:           string;
  user_agent:   string | null;
  referer:      string | null;
  created_at:   string;
  last_seen_at: string;
}

export interface TCmsVisitsPage {
  data:  TCmsVisitRow[];
  total: number;
  page:  number;
  pages: number;
}

export interface TCmsPageStats {
  total:        number;
  unique_users: number;
  unique_ips:   number;
  last7days:    number;
  recent:       TCmsVisitRow[];
}

export interface TCmsPageVersion {
  id:         number;
  page_id:    number;
  title:      string;
  saved_by:   number | null;
  created_at: string;
}

/** Управляемая загрузка: промис результата + функция отмены (xhr.abort()). */
export interface TUploadHandle<T> {
  promise: Promise<T>;
  abort:   () => void;
}

/**
 * POST FormData через XHR с прогрессом отправки, Bearer-токеном и отменой.
 * При отмене промис реджектится ошибкой с name === 'AbortError'.
 * @param getAuthToken — источник токена; по умолчанию user_access_token.
 */
export function xhrUploadControlled<T>(
  url: string,
  fd: FormData,
  onProgress?: (loaded: number, total: number) => void,
  getAuthToken: () => string | null = getToken,
): TUploadHandle<T> {
  const xhr = new XMLHttpRequest();

  const promise = new Promise<T>((resolve, reject) => {
    xhr.open('POST', url);
    const token = getAuthToken();
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(e.loaded, e.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try { resolve(JSON.parse(xhr.responseText) as T); }
        catch { reject(new Error('Некорректный ответ сервера')); }
      } else {
        let msg = `Ошибка ${xhr.status}`;
        try { msg = (JSON.parse(xhr.responseText) as { error?: string }).error ?? msg; } catch { /* */ }
        reject(new Error(msg));
      }
    };
    xhr.onerror = () => reject(new Error('Сетевая ошибка при загрузке'));
    xhr.onabort = () => {
      const err = new Error('Загрузка отменена');
      err.name = 'AbortError';
      reject(err);
    };
    xhr.send(fd);
  });

  return { promise, abort: () => xhr.abort() };
}

/** POST FormData через XHR с прогрессом отправки и Bearer-токеном. */
function xhrUpload<T>(
  url: string,
  fd: FormData,
  onProgress?: (loaded: number, total: number) => void,
): Promise<T> {
  return xhrUploadControlled<T>(url, fd, onProgress).promise;
}

/** Колбэк прогресса отправки файла (байт передано / всего). */
export type TUploadProgress = (loaded: number, total: number) => void;

/** Лимит изображения; синхронизирован с IMAGE_UPLOAD_MAX_BYTES бэкенда. */
export const IMAGE_UPLOAD_MAX_MB = 30;

/**
 * Предпроверка размера до отправки: без неё админ на медленном аплинке
 * несколько минут заливал файл, чтобы получить отказ сервера.
 * Бросает Error с русским текстом — вызывающий код уже показывает message.
 */
function assertImageSize(file: File): void {
  if (file.size > IMAGE_UPLOAD_MAX_MB * 1024 * 1024) {
    throw new Error(`Файл «${file.name}» больше ${IMAGE_UPLOAD_MAX_MB} МБ — выберите файл поменьше`);
  }
}

// ─── Site Settings ─────────────────────────────────────────────────────

export interface TTicketTopic {
  id:          number;
  value:       string;
  icon:        string;
  label:       string;
  description: string | null;
  /** Имена ролей, кто видит тикеты темы; []/undefined = все */
  view_roles?: string[];
  sort_order:  number;
  is_active?:  number | boolean;
}

export interface TPublicSettings {
  maintenance_enabled:            boolean;
  maintenance_text:               string;
  admin_support_avatar_url:       string | null;
  ticket_auto_close_days:         number;
  ticket_auto_close_message:      string;
  ticket_max_attachment_size_mb:  number;
}

export async function getPublicSettings(): Promise<TPublicSettings> {
  const r = await fetch(`${BASE}/api/settings/public`);
  if (!r.ok) throw new Error(r.statusText);
  return r.json() as Promise<TPublicSettings>;
}

export async function getPublicTicketTopics(): Promise<TTicketTopic[]> {
  const r = await fetch(`${BASE}/api/settings/ticket-topics`);
  if (!r.ok) throw new Error(r.statusText);
  const d = await r.json() as { data: TTicketTopic[] };
  return d.data;
}

// ─── Rules (публичная страница правил) ─────────────────────────────────

export interface TRulesNode {
  id:         number;
  parent_id:  number | null;
  slug:       string;
  title:      string;
  body_html:  string;
  position:   number;
  is_hidden:  boolean;
  updated_at: string;
  children:   TRulesNode[];
}

export async function getPublicRules(): Promise<TRulesNode[]> {
  const r = await fetch(`${BASE}/api/rules`);
  if (!r.ok) throw new Error(r.statusText);
  const d = await r.json() as { sections: TRulesNode[] };
  return d.sections;
}

export interface TFaqItem {
  id:          number;
  question:    string;
  answer_html: string;
  position:    number;
  is_hidden:   boolean;
  updated_at:  string;
}

export type TPublicFaqItem = Pick<TFaqItem, 'id' | 'question' | 'answer_html'>;

export async function getPublicFaq(): Promise<TPublicFaqItem[]> {
  const r = await fetch(`${BASE}/api/faq`);
  if (!r.ok) throw new Error(r.statusText);
  const d = await r.json() as { items: TPublicFaqItem[] };
  return d.items;
}

// ─── Legal documents (amazing_auth) ───────────────────────────────────

export async function fetchLegalDocuments(): Promise<any[]> {
  const token = getToken();
  if (!token) throw new Error('unauthorized');
  const r = await fetch(`${AUTH_BASE}/legal/admin/documents`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!r.ok) throw new Error(r.statusText);
  return r.json();
}

export async function createLegalDocument(data: {
  slug: string; title: string; body?: string; url?: string; version: string;
  is_required?: boolean; require_launcher_install?: boolean; effective_at?: string;
  publish_mode?: 'reaccept' | 'notify'; change_summary?: string;
}): Promise<{ ok: boolean; id: number }> {
  const token = getToken();
  if (!token) throw new Error('unauthorized');
  const r = await fetch(`${AUTH_BASE}/legal/admin/documents`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!r.ok) {
    const d = await r.json().catch(() => ({}));
    throw new Error(d.error ?? r.statusText);
  }
  return r.json();
}

export async function updateLegalDocument(id: number, data: {
  title?: string; body?: string; url?: string; is_required?: boolean; is_active?: boolean;
  require_launcher_install?: boolean;
  publish_mode?: 'reaccept' | 'notify'; change_summary?: string | null;
}): Promise<{ ok: boolean }> {
  const token = getToken();
  if (!token) throw new Error('unauthorized');
  const r = await fetch(`${AUTH_BASE}/legal/admin/documents/${id}`, {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!r.ok) {
    const d = await r.json().catch(() => ({}));
    throw new Error(d.error ?? r.statusText);
  }
  return r.json();
}

export async function deleteLegalDocument(id: number): Promise<{ ok: boolean }> {
  const token = getToken();
  if (!token) throw new Error('unauthorized');
  const r = await fetch(`${AUTH_BASE}/legal/admin/documents/${id}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!r.ok) {
    const d = await r.json().catch(() => ({}));
    throw new Error(d.error ?? r.statusText);
  }
  return r.json();
}

// ─── Logs API ─────────────────────────────────────────────────────────
// (методы вынесены в adminApi.logs — см. выше)
